import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SharedService } from '@services/shared.service';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SharedModule } from '@shared/shared.module';
import { PagesService } from '@services/pages.service';
import { QRCodeComponent } from 'angularx-qrcode';
import { environment } from '@env';
import { Clipboard } from '@angular/cdk/clipboard';
import { v4 as uuidv4 } from 'uuid';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { AuthService } from 'src/app/Auth/AuthService';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FilterConfig, FilterModalComponent } from '@pages/filter-modal/filter-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { isArabicLang, ensureArabicFont, reshapeArabic, containsArabic, arabicAwareCellHook, drawBilingualText, renderArabicTextAsImage } from 'src/app/utils/arabic-pdf.util';
import { resolveQrInstanceLink } from 'src/app/utils/web-qr-link.util';
import { SecureStorageService } from '@services/secure-storage.service';
import { BranchesService, ClientBranch } from '@services/branches.service';
import { buildBranchQrLink } from 'src/app/utils/branch-qr-link.util';

interface ContestPerformance {
  totalViews: number | null;
  completedTasks: number | null;
  completedTasksIsRate?: boolean;
  avgClickRate: number | null;
  avgCompletionRate: number | null;
  participationRate: number | null;
  totalViewsTrend?: number | null;
  completedTasksTrend?: number | null;
  avgClickRateTrend?: number | null;
  participationRateTrend?: number | null;
  totalViewsTrendLabel?: string | null;
  totalViewsTrendClass?: string | null;
  completedTasksTrendLabel?: string | null;
  completedTasksTrendClass?: string | null;
  avgClickRateTrendLabel?: string | null;
  avgClickRateTrendClass?: string | null;
  participationRateTrendLabel?: string | null;
  participationRateTrendClass?: string | null;
}

interface ContestPerson {
  id: number | string;
  name: string;
  email: string;
}

interface OverviewStats {
  totalContests: number;
  activeContests: number;
  pendingContests: number;
  finishedContests: number;
  avgClickRate: number;
  avgCompletionRate: number;
  avgParticipationRate: number;
  avgEngagementRate: number;
}

@Component({
  selector: 'app-constant-detail',
  standalone: true,
  imports: [RouterLink, CommonModule, SharedModule, QRCodeComponent, ReactiveFormsModule, FormsModule, NzModalModule, NzSelectModule, NzTableModule, NzToolTipModule, NzDrawerModule, NzDatePickerModule, FilterModalComponent],
  templateUrl: './constant-detail.component.html',
  styleUrls: ['./constant-detail.scss']
})
export class ConstantDetailComponent implements OnInit, OnDestroy {

  contest_detail: any[] = [];
  baseUrl = environment.apiUrl;
  http = inject(HttpClient);
  sharedService = inject(SharedService);
  router = inject(Router);
  private secureStorage = inject(SecureStorageService);
  private branchesService = inject(BranchesService);
  route = inject(ActivatedRoute);
  pagesService = inject(PagesService);
  clipboard = inject(Clipboard);
  contests_detail: any = {};
  name!: string;
  arabic_name: string = '';
  created_at!: string;
  description!: string;
  arabic_description: string = '';
  isDescriptionExpanded: boolean = false;

  get isDescriptionLong(): boolean {
    const desc = (this.isArabic && this.arabic_description) ? this.arabic_description : this.description;
    if (!desc) return false;
    const raw = desc.replace(/<[^>]*>/g, '').trim();
    return raw.length > 100;
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  // Submission metadata shown on the Contest Details page.
  submitted_by: ContestPerson | null = null;
  submitted_via: string | null = null;
  submitted_on!: string;
  approved_by: ContestPerson | null = null;
  levels_of_contest: any[] = [];
  _contest_prize: any[] = [];
  banners: any[] = [];

  /** First banner's image URL; the API uses `url` on some records and `path` on others. */
  get campaignBannerUrl(): string {
    const banner: any = this.banners?.[0];
    return this.sharedService.cleanImageUrl(banner?.url || banner?.path || '');
  }

  start_date!: string;
  end_date!: string;
  draw_date!: string;
  contestID: any;

  // ── Branch picker for client_qr_completion levels ─────────────────────────
  // A level of this type is completed by scanning a code at one of the
  // client's branches, so its card offers the branch list.

  clientBranches: ClientBranch[] = [];
  branchesLoading = false;
  /** Chosen branch per level — a campaign can have more than one such level. */
  selectedBranchIdByLevel: Record<string, any> = {};

  /** The level type a branch scan completes. */
  isClientQrLevel(level: any): boolean {
    return (level?.level_type || level?.type) === 'client_qr_completion';
  }

  /**
   * Fetches branches only when this campaign actually has a level that needs
   * them — every other level type has nothing to pick.
   */
  private loadBranchesIfNeeded(): void {
    const needed = (this.levels_of_contest || []).some((lvl: any) => this.isClientQrLevel(lvl));
    if (!needed || this.clientBranches.length > 0 || this.branchesLoading) return;

    this.branchesLoading = true;
    this.branchesService.getBranches().subscribe({
      next: (rows) => {
        this.clientBranches = rows || [];
        this.branchesLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clientBranches = [];
        this.branchesLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  onBranchChangeForLevel(levelId: any, branchId: any): void {
    this.selectedBranchIdByLevel[String(levelId)] = branchId ?? null;
  }

  /**
   * The QR link for the branch chosen on this level.
   *
   * Empty until a branch is picked, and also empty when the contest or level
   * is missing a uid — see buildBranchQrLink(), which refuses to build a
   * half-formed link rather than hand out one that scans into a dead page.
   */
  branchQrLink(level: any): string {
    const chosen = this.selectedBranchIdByLevel[String(level?.id)];
    if (!chosen) return '';

    const branch = this.clientBranches.find((b) => b.id === Number(chosen));
    if (!branch) return '';

    // The raw server row carries branchId; `raw` keeps whatever the normalizer
    // did not model.
    return buildBranchQrLink(this.contests_detail, level, branch.raw ?? branch);
  }

  /** Copies a branch QR link. */
  copyBranchQrLink(level: any): void {
    const link = this.branchQrLink(level);
    if (!link) return;
    navigator.clipboard.writeText(link).then(
      () => this.sharedService.createNotification('success', this.translateService.instant('webQr.linkCopied')),
      // A blocked clipboard is the user's own browser setting, not an error
      // worth a red toast — the link is on screen to copy by hand.
      () => undefined,
    );
  }

  /**
   * Which dataset this page reads: 'simple' is the plain campaign report,
   * 'web_qr' the Web QR one. Only the two QR-specific calls on this page
   * change — level metrics and the analytics report. Everything else
   * (overview, leaderboard, participants) is the same either way.
   */
  detailMode: 'simple' | 'web_qr' = 'simple';

  /**
   * Whether this campaign has any QR instance generated against it. The
   * toggle is pointless — and misleading — on a campaign that has none, so it
   * only renders once this is true.
   */
  private hasQrInstancesOnPlan = false;
  qrModeLoading = false;

  /** Web QR is a web_access feature. */
  hasWebAccess = false;

  /**
   * Whether to offer the Web QR side at all: the plan has to include it, and
   * this campaign has to have a QR generated against it. Either one missing
   * and the toggle, the instance filter and the QR Instance table all stay
   * away — there would be nothing behind them.
   */
  get hasQrInstances(): boolean {
    return this.hasWebAccess && this.hasQrInstancesOnPlan;
  }

  /** This campaign's QR instances — feeds both the filter and the table. */
  qrInstances: any[] = [];
  /** Selected instance, or null for "all instances on this campaign". */
  qrInstanceId: any = null;

  /**
   * The qr_report_detail payload for this campaign.
   *
   * It carries both `totals` (the eight Web QR performance metrics) and
   * `instances` (a per-code report that already includes redirected, verified
   * views and reach rate — which qr_instance_list does not). So on Web QR the
   * table reads from here, not from qr_instance_list.
   */
  qrReport: any = null;

  /**
   * The plain participant list, kept only so winners can be matched against
   * it — see getContestParticipants(). On the simple dashboard this is the
   * same data as participantsList.
   */
  winnerSourceList: any[] = [];

  /** QR code preview modal. */
  qrPreviewVisible = false;
  qrPreviewValue = '';
  qrPreviewLabel = '';

  get isWebQrMode(): boolean {
    return this.detailMode === 'web_qr';
  }

  /**
   * Translation key for a performance metric's label.
   *
   * Web QR has no clicks and no plain views — a visitor scans a code and the
   * view is verified — so two of the eight cards are worded differently. The
   * values come from the same fields either way.
   */
  metricLabel(metric: 'views' | 'clicks'): string {
    if (metric === 'views') {
      return this.isWebQrMode ? 'contestDetails.totalVerifiedViews' : 'contestDetails.totalViews';
    }
    return this.isWebQrMode ? 'contestDetails.totalScans' : 'contestDetails.totalClicks';
  }

  /**
   * Looks for QR instances on this campaign to decide whether to offer the
   * toggle at all. A failure leaves the toggle hidden rather than showing a
   * button that would load nothing.
   */
  private checkQrInstances(contestId: number): void {
    if (!this.hasWebAccess) {
      this.qrInstances = [];
      this.hasQrInstancesOnPlan = false;
      return;
    }

    this.pagesService.getAllById('QR_INSTANCE_LIST', { contest_id: contestId }).subscribe({
      next: (res: any) => {
        // The endpoint answers { contest_id, total, instances: [...] } — same
        // shape the Web QR page reads. `instances` is the real key; the rest
        // are only defensive.
        const rows = Array.isArray(res?.instances)
          ? res.instances
          : Array.isArray(res)
            ? res
            : (res?.items ?? res?.data ?? res?.result ?? []);
        this.qrInstances = Array.isArray(rows) ? rows : [];
        this.hasQrInstancesOnPlan = this.qrInstances.length > 0;
      },
      error: () => {
        this.qrInstances = [];
        this.hasQrInstancesOnPlan = false;
      },
    });
  }

  /**
   * Hides the gender filter on Web QR, where no gender is collected, and drops
   * a value already chosen — a filter the user can no longer see must not go
   * on narrowing the list.
   */
  private applyModeToFilters(): void {
    this.filterConfig = { ...this.filterConfig, showGender: !this.isWebQrMode };
    if (this.isWebQrMode && this.currentFilters?.gender) {
      const { gender, ...rest } = this.currentFilters;
      this.currentFilters = rest;
      this.filterModalComponent?.filterForm?.patchValue({ gender: null });
    }
  }

  /**
   * Feeds qr_report_detail's `totals` into the eight performance cards.
   *
   * OverviewStats' field names date from the campaign-overview screen, so the
   * mapping is not obvious — each line below names the card it drives.
   *
   * A null rate means "not measurable yet" (no participants at all); it is
   * shown as 0 because each card carries a progress bar that needs a number.
   */
  /**
   * True only for a real qr_report_detail payload.
   *
   * Distinguishes it from the campaign report the fallback returns, which is
   * also a plain object. The test is the presence of `totals` carrying at
   * least one QR-specific key — not whether the numbers are non-zero: a
   * campaign with no scans yet reports zeros, and those zeros are the truth.
   */
  private hasQrReportTotals(response: any): boolean {
    if (!response || typeof response !== 'object' || Array.isArray(response)) return false;
    const totals = response.totals;
    if (!totals || typeof totals !== 'object') return false;
    return ['scans', 'verified_views', 'participants', 'completed', 'joins']
      .some(key => totals[key] !== undefined && totals[key] !== null);
  }

  private applyQrReportTotals(): void {
    const totals = this.qrReport?.totals;
    if (!totals) return;

    const num = (value: any) => Number(value ?? 0) || 0;

    this.performanceStats = {
      ...this.performanceStats,
      totalContests: num(totals.verified_views),          // Total Verified Views
      activeContests: num(totals.scans),                  // Total Scans
      pendingContests: num(totals.participants),          // Participants
      finishedContests: num(totals.completed),            // Completed
      avgClickRate: num(totals.overall_success_rate_pct),  // Overall Success Rate
      avgCompletionRate: num(totals.completion_rate_pct),  // Completion Rate
      avgParticipationRate: num(totals.participation_rate_pct), // Participation Rate
      // The payload has no engagement_rate_pct; join_rate_pct (scan -> join) is
      // the closest thing it reports.
      avgEngagementRate: num(totals.join_rate_pct),       // Engagement Rate
    };
  }

  /**
   * Rows for the QR Instance table.
   *
   * qr_report_detail's instances are preferred because they carry the scan
   * outcomes; qr_instance_list is the fallback for the moment before the
   * report lands.
   */
  get qrReportInstances(): any[] {
    const fromReport = this.qrReport?.instances;
    if (Array.isArray(fromReport) && fromReport.length > 0) return fromReport;
    return this.qrInstances || [];
  }

  /** Label for an instance row — the API is inconsistent about the field. */
  qrInstanceLabel(instance: any): string {
    return instance?.label || instance?.name || instance?.slug
      || (instance?.id ? `Instance #${instance.id}` : 'N/A');
  }

  /** Reloads the QR-side calls for the instance now selected. */
  async onQrInstanceChange(instanceId: any): Promise<void> {
    this.qrInstanceId = instanceId ?? null;
    if (!this.contestID) return;

    this.qrModeLoading = true;
    this.sharedService.showLoader();
    try {
      await Promise.all([
        this.getLevelCompletionMetrics(Number(this.contestID)),
        this.getContestAnalytics(String(this.contestID)),
        // The participation table swaps source with the mode, and narrows with
        // the instance filter, so it reloads too.
        this.getContestParticipants(String(this.contestID)),
      ]);
    } finally {
      this.qrModeLoading = false;
      this.sharedService.hideLoader();
    }
  }

  /**
   * The instance id to send with the QR calls.
   *
   * 0 means "every instance on this campaign" — the value these endpoints were
   * already being given before the filter existed, so leaving the filter alone
   * keeps the old behaviour exactly.
   */
  private get qrInstanceParam(): number {
    const id = Number(this.qrInstanceId);
    return this.isWebQrMode && id > 0 ? id : 0;
  }

  // ── QR Instance table ─────────────────────────────────────────────────────
  // Fields are read off qr_report_detail's instances, which report per-code
  // scan outcomes. qr_instance_list names its scan count `scan_count` instead
  // and carries no outcomes, hence the second reader on each of these.

  qrInstanceScans(instance: any): number {
    return Number(instance?.scans ?? instance?.scan_count ?? 0) || 0;
  }

  /** Null, not 0, when the field is absent — so the cell can say '—'. */
  qrInstanceRedirected(instance: any): number | null {
    const value =
      instance?.scans_by_outcome?.redirected ??
      instance?.redirected ??
      instance?.redirect_count;
    return value === undefined || value === null ? null : Number(value) || 0;
  }

  qrInstanceVerifiedViews(instance: any): number | null {
    const value = instance?.verified_views ?? instance?.verifiedViews;
    return value === undefined || value === null ? null : Number(value) || 0;
  }

  /**
   * Verified views as a share of scans. Taken from `reach_rate_pct` when the
   * report sends it, otherwise derived — and null when neither is possible,
   * rather than 0%, which would read as a real measurement.
   */
  qrInstanceReachRate(instance: any): number | null {
    const given = instance?.reach_rate_pct ?? instance?.reach_rate ?? instance?.reachRate;
    if (given !== undefined && given !== null) return Number(given) || 0;

    const verified = this.qrInstanceVerifiedViews(instance);
    const scans = this.qrInstanceScans(instance);
    if (verified === null || scans <= 0) return null;
    return Math.round((verified / scans) * 1000) / 10;
  }

  /** Joins / participants / completed, all reported per code. */
  qrInstanceJoins(instance: any): number {
    return Number(instance?.joins ?? 0) || 0;
  }

  qrInstanceParticipants(instance: any): number {
    return Number(instance?.participants ?? 0) || 0;
  }

  qrInstanceCompleted(instance: any): number {
    return Number(instance?.completed ?? 0) || 0;
  }

  qrInstanceExpires(instance: any): string {
    const raw = instance?.expires_at ?? instance?.expiresAt;
    return raw ? this.formatDate(raw) : 'N/A';
  }

  qrInstanceStatus(instance: any): 'active' | 'expired' | 'inactive' {
    if (instance?.is_active === false) return 'inactive';
    const raw = instance?.expires_at ?? instance?.expiresAt;
    if (raw && new Date(raw).getTime() < Date.now()) return 'expired';
    return 'active';
  }

  /**
   * Opens the QR image for an instance.
   *
   * qr_report_detail's instances carry only `slug`, not a ready URL, so the
   * link is composed the same way the Web QR page composes it — including the
   * per-code host the client may have chosen there. Reading `short_link`
   * alone showed nothing, because that field is simply absent here.
   */
  viewQrInstanceCode(instance: any): void {
    this.qrPreviewValue = resolveQrInstanceLink(instance, this.contestID);
    this.qrPreviewLabel = this.qrInstanceLabel(instance);
    this.qrPreviewVisible = true;
  }

  closeQrPreview(): void {
    this.qrPreviewVisible = false;
  }

  /** Copies the link the previewed QR encodes. */
  copyQrPreviewLink(): void {
    if (!this.qrPreviewValue) return;
    navigator.clipboard.writeText(this.qrPreviewValue).then(
      () => this.sharedService.createNotification('success', this.translateService.instant('webQr.linkCopied')),
      // A denied clipboard permission is the user's own setting, not an error
      // worth a red toast — the link is on screen to copy by hand.
      () => undefined,
    );
  }

  /**
   * Editing a QR instance lives on the Web QR management page — that screen
   * owns the form, its validation and the allocation rules, so this hands off
   * rather than keeping a second copy here.
   *
   * The instance id goes with it so that page opens the edit form for this
   * code; without it the user landed on the list and had to find the row again.
   */
  editQrInstance(instance: any): void {
    this.router.navigate(['/home/contests/web-qr'], {
      queryParams: {
        contest_id: this.contestID,
        contest_name: this.name || '',
        qr_instance_id: instance?.id ?? '',
      },
    });
  }

  /** Participants who joined through one specific instance. */
  viewQrInstanceParticipants(instance: any): void {
    this.router.navigate(['/home/participants'], {
      queryParams: {
        contest_id: this.contestID,
        contest_name: this.name || '',
        qr_instance_id: instance?.id ?? '',
      },
    });
  }

  /** Switches dataset and reloads only the two calls that differ. */
  async onDetailModeChange(mode: 'simple' | 'web_qr'): Promise<void> {
    if (this.detailMode === mode || !this.contestID) return;
    this.detailMode = mode;
    // The instance filter belongs to Web QR; leaving it set would silently
    // narrow the plain report too.
    this.qrInstanceId = null;
    // Leaving the QR report in place while switching back to the plain view
    // would keep its totals on the cards, since updatePerformanceOverview()
    // defers to it whenever it is present.
    if (!this.isWebQrMode) {
      this.qrReport = null;
    }
    this.applyModeToFilters();
    this.applyClientFilters();

    this.qrModeLoading = true;
    this.sharedService.showLoader();
    try {
      await Promise.all([
        this.getLevelCompletionMetrics(Number(this.contestID)),
        this.getContestAnalytics(String(this.contestID)),
        // The participation table swaps source with the mode, and narrows with
        // the instance filter, so it reloads too.
        this.getContestParticipants(String(this.contestID)),
        // Back on the plain view the cards have to be rebuilt from the
        // campaign overview; nothing else writes them now the QR report is
        // cleared.
        ...(this.isWebQrMode ? [] : [this.getContestPerformance(this.contestID)]),
      ]);
    } finally {
      this.qrModeLoading = false;
      this.sharedService.hideLoader();
    }
  }
  showAllVariables: { [key: string]: boolean } = {};
  maxVariablesToShow: number = 3;
  editingLevels: { [key: string]: boolean } = {};
  levelForms: { [key: string]: FormGroup } = {};
  levelImagePreviews: { [key: string]: string | ArrayBuffer | null } = {};
  levelImageLoadErrors: { [key: string]: boolean } = {};
  isImageModalVisible: { [key: string]: boolean } = {};
  fb = inject(FormBuilder);
  authService = inject(AuthService);
  translateService = inject(TranslateService);
  cdr = inject(ChangeDetectorRef);

  onLevelImageError(levelId: string): void {
    this.levelImageLoadErrors[levelId] = true;
    this.cdr.detectChanges();
  }

  get isArabic(): boolean {
    return localStorage.getItem('lang') === 'ar';
  }

  // Contest Performance and Participants data
  contestPerformance: ContestPerformance = {
    totalViews: null,
    completedTasks: null,
    avgClickRate: null,
    avgCompletionRate: null,
    participationRate: null,
    totalViewsTrend: null,
    completedTasksTrend: null,
    avgClickRateTrend: null,
    participationRateTrend: null,
    totalViewsTrendLabel: null,
    totalViewsTrendClass: null,
    completedTasksTrendLabel: null,
    completedTasksTrendClass: null,
    avgClickRateTrendLabel: null,
    avgClickRateTrendClass: null,
    participationRateTrendLabel: null,
    participationRateTrendClass: null
  };
  performanceStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performancePreviousStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performanceTrends: Record<keyof OverviewStats | 'overallSuccessRate', number> = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0,
    overallSuccessRate: 0
  };
  performanceLoading: boolean = false;
  performanceError: string | null = null;
  performanceDateRange: Date[] = [];
  performancePickerOpen: boolean = false;
  participantsList: any[] = [];
  filteredParticipants: any[] = [];
  participantsLoading: boolean = false;
  winnersList: any[] = [];
  winnersLoading: boolean = false;
  analyticsActivity: any[] = [];
  analyticsLoading: boolean = false;
  analyticsLoaded: boolean = false;
  quizzesList: any[] = [];
  quizzesLoaded: boolean = false;
  surveyReportDetail: any = null;

  // Level Matrix — per-level completion + demographic breakdown
  levelMatrix: any = null;
  levelMetricsLoading: boolean = false;
  levelMetricsError: string = '';
  levelDemographicsDrawerVisible: boolean = false;
  selectedLevelMetrics: any = null;

  // Search and Filter for participants
  searchTerm: string = '';
  filterModalVisible: boolean = false;
  currentFilters: any = {};
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;

  filterConfig: FilterConfig = {
    showTimeline: false,
    showLevel: false,
    showStatus: true,
    showFormat: false,
    showType: false,
    showViewType: false,
    showLocation: true,
    // Gender and age follow the dataset — see applyModeToFilters(). Web QR
    // collects neither; the plain campaign report always had them.
    showGender: true,
    showContest: false,
    showAge: false,
    showCompletionRate: false,
    showParticipantName: false,
    showParticipantEmail: false,
    statusOptions: [
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' }
    ]
  };

  // Drawer for participant levels
  levelsDrawerVisible: boolean = false;
  selectedParticipant: any = null;
  selectedParticipantLevels: any[] = [];
  selectedParticipantTableTab: 'all' | 'survey' = 'all';

  // Leaderboard
  leaderboard: any[] = [];
  leaderboardLoading: boolean = false;
  leaderboardPage: number = 1;
  leaderboardPerPage: number = 10;
  leaderboardTotal: number = 0;

  // Admin override
  overrideModalVisible: boolean = false;
  overrideParticipantId: any = null;
  overrideReason: string = '';
  overrideSubmitting: boolean = false;
  overrideAuditLog: any[] = [];

  private pdfSectionHeading(doc: jsPDF, text: string, y: number, arabicFontLoaded: boolean = false): number {
    const pageWidth = doc.internal.pageSize.width;
    const isAr = containsArabic(text);
    const isArabicApp = isArabicLang();
    let bottomY = y + 5;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 52, 137);

    if (isAr && isArabicApp) {
      drawBilingualText(doc, text, pageWidth - 14, y + 4, arabicFontLoaded, { align: 'right' });
      bottomY = y + 5;
    } else {
      drawBilingualText(doc, text, 14, y + 4, arabicFontLoaded);
      bottomY = y + 5;
    }

    const lineY = bottomY + 2;
    doc.setDrawColor(60, 52, 137);
    doc.setLineWidth(0.4);
    doc.line(14, lineY, pageWidth - 14, lineY);
    return lineY + 6;
  }

  private pdfEnsureSpace(doc: jsPDF, y: number, needed: number): number {
    const pageHeight = doc.internal.pageSize.height;
    if (y + needed > pageHeight - 15) {
      doc.addPage();
      return 15;
    }
    return y;
  }

  get hasSurveyLevel(): boolean {
    return (this.levels_of_contest || []).some(
      (level: any) => (level.level_type || level.type) === 'survey'
    );
  }

  normalizeSurveyQuestions(questions: any[]): any[] {
    if (!Array.isArray(questions)) return [];
    return questions.map((q: any, idx: number) => {
      const rawOptions =
        (Array.isArray(q.answer_options) && q.answer_options.length > 0)
          ? q.answer_options
          : (Array.isArray(q.options) && q.options.length > 0)
            ? q.options
            : (Array.isArray(q.choices) && q.choices.length > 0)
              ? q.choices
              : (q.answer_options || q.options || []);

      const normalizedOptions = (Array.isArray(rawOptions) ? rawOptions : []).map((opt: any, oIdx: number) => {
        if (typeof opt === 'string') {
          return {
            id: oIdx + 1,
            index: oIdx,
            option_text: opt,
            arabic_option_text: opt,
          };
        }
        return {
          id: opt?.id ?? (oIdx + 1),
          index: oIdx,
          option_text: opt?.option_text ?? opt?.text ?? opt?.title ?? opt?.value ?? `Option ${oIdx + 1}`,
          arabic_option_text: opt?.arabic_option_text ?? opt?.arabic_text ?? opt?.option_text ?? opt?.text ?? `Option ${oIdx + 1}`,
        };
      });

      const hasOptions = normalizedOptions.length > 0;
      const question_type = (q.question_type || q.type)
        ? (q.question_type || q.type)
        : (hasOptions ? 'mcq' : 'open_text');

      return {
        ...q,
        id: q.id ?? (idx + 1),
        question_text: q.question_text || q.text || q.title || `Question ${idx + 1}`,
        arabic_question_text: q.arabic_question_text || q.arabic_text || '',
        options: normalizedOptions,
        answer_options: normalizedOptions,
        question_type,
        is_multi_select: q.is_multi_select ?? (q.answer_selection_type === 'multiple'),
      };
    });
  }

  isMcqQuestion(q: any): boolean {
    if (!q) return false;
    if (q.question_type === 'mcq' || q.type === 'mcq') return true;
    if (q.question_type === 'open_text' && (!q.options || q.options.length === 0) && (!q.answer_options || q.answer_options.length === 0)) return false;
    return (Array.isArray(q.options) && q.options.length > 0) || (Array.isArray(q.answer_options) && q.answer_options.length > 0);
  }

  parseJsonSafely(val: any): any {
    if (!val) return val;
    let parsed = val;
    if (typeof parsed === 'string') {
      let trimmed = parsed.trim();
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        try {
          trimmed = JSON.parse(trimmed);
        } catch { }
      }
      if (typeof trimmed === 'string' && (trimmed.startsWith('[') || trimmed.startsWith('{'))) {
        try {
          parsed = JSON.parse(trimmed);
          if (typeof parsed === 'string' && (parsed.trim().startsWith('[') || parsed.trim().startsWith('{'))) {
            try {
              parsed = JSON.parse(parsed.trim());
            } catch { }
          }
        } catch {
          parsed = trimmed;
        }
      } else {
        parsed = trimmed;
      }
    }
    return parsed;
  }

  // Resolves one question's answer out of a participant's stored survey answer.
  // Matching is identity-first (question id, exact question text, numbered keys/indexes whose base is
  // detected from the data). Positional guessing is only used when the data holds exactly one entry per
  // question — a skipped question may be omitted from the stored answer, and guessing by position would
  // then shift a neighbouring question's answer into it. A shorter plain list is aligned against the
  // level's questions instead (see alignPositionalAnswers). Unmatched questions return null (exported as "-").
  extractQuestionAnswer(parsedAnswerData: any, sq: any, sIdx: number, totalQuestions: number, allQuestions: any[] = []): any {
    if (parsedAnswerData === null || parsedAnswerData === undefined) {
      return null;
    }

    const qId = sq?.id;
    const qText = sq?.question_text ? String(sq.question_text).trim().toLowerCase() : '';
    const qArText = sq?.arabic_question_text ? String(sq.arabic_question_text).trim().toLowerCase() : '';
    const qNum = sIdx + 1;
    const realIds = new Set((allQuestions || []).map((q: any) => q?.id).filter((v: any) => v !== undefined && v !== null).map((v: any) => String(v)));
    const canAlign = Array.isArray(allQuestions) && allQuestions.length === totalQuestions && totalQuestions > 1;

    const pickAnswer = (val: any): any => (val && typeof val === 'object' && !Array.isArray(val))
      ? (val.answer ?? val.user_answer ?? val.userAnswer ?? val.submitted_answer ?? val.value ?? val.val ?? val.selected_option ?? val.selected_options ?? val.selectedOption ?? val.selectedOptions ?? val.options ?? val.option ?? val.response ?? val.text ?? val.text_answer ?? val.answer_text ?? val.choice ?? val.choices ?? val)
      : val;

    // A lone answer record ({ question_id, answer }) behaves like a one-item answer list.
    if (typeof parsedAnswerData === 'object' && !Array.isArray(parsedAnswerData) &&
      (parsedAnswerData.question_id !== undefined || parsedAnswerData.questionId !== undefined || parsedAnswerData.question_text !== undefined)) {
      return this.extractQuestionAnswer([parsedAnswerData], sq, sIdx, totalQuestions, allQuestions);
    }

    // 1. Object keyed per question
    if (typeof parsedAnswerData === 'object' && !Array.isArray(parsedAnswerData)) {
      const nested = parsedAnswerData.answers ?? parsedAnswerData.survey_answers ?? parsedAnswerData.responses ?? parsedAnswerData.data;
      if (nested && nested !== parsedAnswerData && typeof nested === 'object') {
        return this.extractQuestionAnswer(nested, sq, sIdx, totalQuestions, allQuestions);
      }

      const keys = Object.keys(parsedAnswerData);
      // Numbered keys are 0-based only when a "0"-numbered key exists; otherwise they are 1-based.
      const zeroBased = keys.some(k => /^(q|question|answer|ans)?[\s_-]?0$/i.test(k.trim()));
      const num = zeroBased ? sIdx : qNum;
      const numberedKeys = [`${num}`, `Q${num}`, `q${num}`, `Question ${num}`, `question${num}`, `question_${num}`, `q_${num}`, `answer_${num}`, `ans_${num}`];
      const idKeys = (qId !== undefined && qId !== null) ? [`${qId}`, `question_${qId}`, `q_${qId}`, `answer_${qId}`] : [];

      const lookup = (candidates: string[]): { found: boolean; value: any } => {
        for (const k of candidates) {
          if (Object.prototype.hasOwnProperty.call(parsedAnswerData, k)) {
            return { found: true, value: pickAnswer(parsedAnswerData[k]) };
          }
        }
        return { found: false, value: null };
      };

      // An id key wins; a key present with an empty value means "skipped" and must not fall through.
      let hit = lookup(idKeys);
      if (hit.found) return hit.value;

      const exactTextKey = keys.find(k => {
        const normK = k.trim().toLowerCase();
        return (qText && normK === qText) || (qArText && normK === qArText);
      });
      if (exactTextKey !== undefined) return pickAnswer(parsedAnswerData[exactTextKey]);

      // Numbered keys only when they can't be mistaken for another question's real id.
      if (!numberedKeys.some(k => realIds.has(k) && k !== String(qId))) {
        hit = lookup(numberedKeys);
        if (hit.found) return hit.value;
      }
      return null;
    }

    // 2. Array of answers
    if (Array.isArray(parsedAnswerData)) {
      const items = parsedAnswerData;
      const itemId = (it: any) => it?.question_id ?? it?.questionId ?? it?.q_id ?? it?.qid ?? it?.id;
      const itemIdx = (it: any) => it?.index ?? it?.question_index ?? it?.questionIndex;
      const itemText = (it: any) => (it?.question || it?.question_text || it?.questionText || it?.title || it?.question_title || '').toString().trim().toLowerCase();
      const itemArText = (it: any) => (it?.arabic_question || it?.arabic_question_text || it?.arabicQuestion || '').toString().trim().toLowerCase();
      const isObj = (it: any) => it && typeof it === 'object' && !Array.isArray(it);
      const identityBearing = items.some((it: any) => isObj(it) && (itemId(it) !== undefined || itemIdx(it) !== undefined || itemText(it) || itemArText(it)));

      if (identityBearing) {
        // a) real question id
        let matched = (qId !== undefined && qId !== null)
          ? items.find((it: any) => isObj(it) && itemId(it) !== undefined && String(itemId(it)) === String(qId))
          : undefined;
        // b) exact question text
        if (!matched) {
          matched = items.find((it: any) => isObj(it) && ((qText && itemText(it) === qText) || (qArText && itemArText(it) === qArText)));
        }
        // c) explicit index field, base detected from the data
        if (!matched) {
          const idxZeroBased = items.some((it: any) => isObj(it) && itemIdx(it) !== undefined && Number(itemIdx(it)) === 0);
          const want = idxZeroBased ? sIdx : qNum;
          matched = items.find((it: any) => isObj(it) && itemIdx(it) !== undefined && Number(itemIdx(it)) === want);
        }
        // d) id used as a 1-based question number, only when it isn't some question's real id
        if (!matched) {
          matched = items.find((it: any) => isObj(it) && itemId(it) !== undefined && String(itemId(it)) === String(qNum) && !realIds.has(String(itemId(it))));
        }
        return matched ? pickAnswer(matched) : null;
      }

      // Plain positional array — trust position only when every question has a slot;
      // a shorter list (skipped questions left out) is aligned against the questions.
      if (items.length === totalQuestions) {
        return pickAnswer(items[sIdx]);
      }
      if (canAlign && items.length < totalQuestions) {
        return this.alignPositionalAnswers(items.map(pickAnswer), allQuestions)[sIdx];
      }
      return null;
    }

    // 3. Single string
    if (typeof parsedAnswerData === 'string') {
      const trimmedStr = parsedAnswerData.trim();
      if (!trimmedStr) return null;

      // 3a. Explicit "Q2: ..." / "2: ..." prefixes (1-based)
      const prefixed = trimmedStr.match(new RegExp(`(?:^|[\\n|;])\\s*(?:Q|Question|سؤال)?\\s*${qNum}\\s*[:\\-.)]\\s*([^\\n|;]+)`, 'i'));
      if (prefixed && prefixed[1]) {
        return prefixed[1].trim();
      }

      // 3b. Delimited list — positional only when there is one segment per question
      if (trimmedStr.includes('\n') || trimmedStr.includes('\r') || trimmedStr.includes('|')) {
        const segments = trimmedStr.split(/[\r\n]+|(?:\s*\|\s*)/).map((s: string) => s.trim());
        if (segments.length === totalQuestions && totalQuestions > 1) {
          return segments[sIdx] || null;
        }
        if (canAlign && segments.length > 1 && segments.length < totalQuestions) {
          return this.alignPositionalAnswers(segments.filter(Boolean), allQuestions)[sIdx];
        }
      }

      // 3c. Only one question in the survey
      if (totalQuestions === 1) {
        return trimmedStr;
      }

      // 3d. The string is one of this question's own options
      const opts: any[] = (Array.isArray(sq?.options) && sq.options.length > 0)
        ? sq.options
        : (Array.isArray(sq?.answer_options) ? sq.answer_options : []);
      if (opts.length > 0) {
        const lowerTrimmed = trimmedStr.toLowerCase();
        const matchesThisQ = opts.some((o: any) => {
          const en = (o.option_text || '').toString().trim().toLowerCase();
          const ar = (o.arabic_option_text || '').toString().trim().toLowerCase();
          return (en && en === lowerTrimmed) || (ar && ar === lowerTrimmed);
        });
        if (matchesThisQ) {
          return trimmedStr;
        }
      }
      return null;
    }

    // 4. Single number — only meaningful for a one-question survey
    if (typeof parsedAnswerData === 'number' && totalQuestions === 1) {
      const opts: any[] = (Array.isArray(sq?.options) && sq.options.length > 0)
        ? sq.options
        : (Array.isArray(sq?.answer_options) ? sq.answer_options : []);
      return opts.find((o: any) => Number(o.id) === parsedAnswerData || o.index === parsedAnswerData) ?? parsedAnswerData;
    }

    return null;
  }

  // Maps a plain, in-order answer list that has fewer entries than there are questions (skipped
  // questions left out) back onto the questions. Only optional questions may be skipped, and a choice
  // question only takes a value made of its own options. Every valid alignment is considered; a question
  // gets a value only when all alignments agree on it, otherwise it stays null rather than guessing.
  private alignPositionalAnswers(values: any[], questions: any[]): any[] {
    const n = questions.length;
    const m = values.length;
    const isEmpty = (v: any) => v === null || v === undefined || v === '' || v === '-' || (Array.isArray(v) && v.length === 0);
    const norm = (v: any) => String(v ?? '').trim().toLowerCase();

    const optionsOf = (q: any): any[] => (Array.isArray(q?.options) && q.options.length > 0)
      ? q.options
      : (Array.isArray(q?.answer_options) ? q.answer_options : []);
    const isChoice = (q: any) => q?.question_type !== 'open_text' && optionsOf(q).length > 0;

    const matchesOption = (q: any, item: any): boolean => optionsOf(q).some((o: any, oIdx: number) => {
      if (item && typeof item === 'object') {
        return (item.id !== undefined && String(item.id) === String(o.id)) ||
          (!!(item.option_text || item.text) && norm(item.option_text || item.text) === norm(o.option_text));
      }
      const s = norm(item);
      return (!!s && (s === norm(o.option_text) || s === norm(o.arabic_option_text) || s === norm(o.id))) ||
        (typeof item === 'number' && item === oIdx);
    });

    const fits = (q: any, v: any): boolean => {
      if (isEmpty(v)) return q?.is_required !== true;
      if (!isChoice(q)) return !Array.isArray(v) && typeof v !== 'object';
      if (Array.isArray(v)) return v.every((it: any) => matchesOption(q, it));
      if (matchesOption(q, v)) return true;
      const parts = String(v).split(/[,،]/).map((s: string) => s.trim()).filter(Boolean);
      return parts.length > 1 && parts.every((p: string) => matchesOption(q, p));
    };
    const skippable = (q: any) => q?.is_required !== true;

    // feasible[i][j]: questions i.. can absorb values j.. exactly.
    const feasible: boolean[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(false));
    feasible[n][m] = true;
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m; j >= 0; j--) {
        feasible[i][j] = (skippable(questions[i]) && feasible[i + 1][j]) ||
          (j < m && fits(questions[i], values[j]) && feasible[i + 1][j + 1]);
      }
    }
    if (!feasible[0][0]) return new Array(n).fill(null);

    const MAX_ALIGNMENTS = 64;
    const alignments: number[][] = [];
    const current: number[] = new Array(n).fill(-1);
    const walk = (i: number, j: number): void => {
      if (alignments.length >= MAX_ALIGNMENTS) return;
      if (i === n) { alignments.push([...current]); return; }
      if (j < m && fits(questions[i], values[j]) && feasible[i + 1][j + 1]) {
        current[i] = j;
        walk(i + 1, j + 1);
      }
      if (skippable(questions[i]) && feasible[i + 1][j]) {
        current[i] = -1;
        walk(i + 1, j);
      }
    };
    walk(0, 0);
    // Too many possibilities to prove agreement — leave everything unresolved.
    if (alignments.length >= MAX_ALIGNMENTS) return new Array(n).fill(null);

    return questions.map((_: any, i: number) => {
      const first = alignments[0][i];
      const agreed = alignments.every(a => a[i] === first);
      if (!agreed || first === -1) return null;
      return isEmpty(values[first]) ? null : values[first];
    });
  }

  private resolveSurveyQuestionAnswer(
    rawVal: any,
    question: any
  ): {
    displayAnswer: string;
    arabicDisplayAnswer: string;
    rawAnswerText: string;
    hasAnswer: boolean;
  } {
    if (rawVal === null || rawVal === undefined || rawVal === '') {
      return {
        displayAnswer: '-',
        arabicDisplayAnswer: '-',
        rawAnswerText: '-',
        hasAnswer: false,
      };
    }

    let parsedVal = this.parseJsonSafely(rawVal);
    const options: any[] = (Array.isArray(question?.answer_options) && question.answer_options.length > 0)
      ? question.answer_options
      : (Array.isArray(question?.options) ? question.options : []);

    const rawAnswerText = typeof rawVal === 'object' ? JSON.stringify(rawVal) : String(rawVal);

    if (options.length === 0 || question?.question_type === 'open_text') {
      const textVal = typeof parsedVal === 'object'
        ? (parsedVal?.answer ?? parsedVal?.value ?? parsedVal?.text ?? parsedVal?.user_answer ?? parsedVal?.response ?? JSON.stringify(parsedVal))
        : String(parsedVal);
      const cleanText = (textVal !== null && textVal !== undefined && textVal !== '') ? String(textVal) : '-';
      return {
        displayAnswer: cleanText,
        arabicDisplayAnswer: cleanText,
        rawAnswerText,
        hasAnswer: cleanText !== '-',
      };
    }

    let valuesArray: any[] = [];
    if (Array.isArray(parsedVal)) {
      valuesArray = parsedVal;
    } else if (typeof parsedVal === 'string') {
      const trimmed = parsedVal.trim();
      const directMatch = options.some((o: any) =>
        (o.option_text && String(o.option_text).trim().toLowerCase() === trimmed.toLowerCase()) ||
        (o.arabic_option_text && String(o.arabic_option_text).trim().toLowerCase() === trimmed.toLowerCase()) ||
        (o.id !== undefined && String(o.id) === trimmed)
      );
      if (!directMatch && (trimmed.includes(',') || trimmed.includes('|') || trimmed.includes('،'))) {
        valuesArray = trimmed.split(/[,|،]/).map((s: string) => s.trim()).filter(Boolean);
      } else {
        valuesArray = [trimmed];
      }
    } else {
      valuesArray = [parsedVal];
    }

    const resolvedEn: string[] = [];
    const resolvedAr: string[] = [];

    valuesArray.forEach((item: any) => {
      if (item === null || item === undefined || item === '') return;

      let matchedOpt: any = null;

      // 1. If item is an object:
      if (typeof item === 'object') {
        const itemText = item.option_text || item.text || item.value || item.title || item.answer || item.user_answer;
        const itemArText = item.arabic_option_text || item.arabic_text || itemText;
        if (item.id !== undefined) {
          matchedOpt = options.find((o: any) => String(o.id) === String(item.id));
        }
        if (!matchedOpt && itemText) {
          const strVal = String(itemText).trim().toLowerCase();
          matchedOpt = options.find((o: any) =>
            (o.option_text && String(o.option_text).trim().toLowerCase() === strVal) ||
            (o.arabic_option_text && String(o.arabic_option_text).trim().toLowerCase() === strVal)
          );
        }
        if (!matchedOpt && item.index !== undefined && item.index >= 0 && item.index < options.length) {
          matchedOpt = options[item.index];
        }
        if (matchedOpt) {
          resolvedEn.push(matchedOpt.option_text || `Option ${options.indexOf(matchedOpt) + 1}`);
          resolvedAr.push(matchedOpt.arabic_option_text || matchedOpt.option_text || `Option ${options.indexOf(matchedOpt) + 1}`);
          return;
        }
        if (itemText || itemArText) {
          resolvedEn.push(String(itemText || itemArText));
          resolvedAr.push(String(itemArText || itemText));
          return;
        }
      }

      // 2. If item is a number or numeric string, FIRST check if it matches an option id!
      const itemStr = String(item).trim();
      const num = Number(itemStr);
      if (!isNaN(num)) {
        matchedOpt = options.find((o: any) => o.id !== undefined && (Number(o.id) === num || String(o.id) === itemStr));
        // If not matched by ID, check if it's a 0-based index
        if (!matchedOpt && Number.isInteger(num) && num >= 0 && num < options.length) {
          matchedOpt = options[num];
        }
      }

      // 3. Match by text (English or Arabic)
      if (!matchedOpt) {
        const strVal = itemStr.toLowerCase();
        matchedOpt = options.find((o: any) =>
          (o.option_text && String(o.option_text).trim().toLowerCase() === strVal) ||
          (o.arabic_option_text && String(o.arabic_option_text).trim().toLowerCase() === strVal) ||
          (o.id !== undefined && String(o.id).trim().toLowerCase() === strVal)
        );
      }

      // 4. Record result
      if (matchedOpt) {
        resolvedEn.push(matchedOpt.option_text || `Option ${options.indexOf(matchedOpt) + 1}`);
        resolvedAr.push(matchedOpt.arabic_option_text || matchedOpt.option_text || `Option ${options.indexOf(matchedOpt) + 1}`);
      } else {
        const fallback = typeof item === 'object' ? (item.answer || item.text || item.value || JSON.stringify(item)) : String(item);
        resolvedEn.push(fallback);
        resolvedAr.push(fallback);
      }
    });

    const displayAnswer = resolvedEn.length > 0 ? resolvedEn.join(', ') : '-';
    const arabicDisplayAnswer = resolvedAr.length > 0 ? resolvedAr.join(', ') : '-';

    return {
      displayAnswer,
      arabicDisplayAnswer,
      rawAnswerText,
      hasAnswer: resolvedEn.length > 0,
    };
  }

  /**
   * Fetches participant levels from UNIQUE_PARTICIPANTS_CONTEST_LEVELS by testing
   * all candidate IDs (participant.id, participant_id, _end_user.id, user_id)
   * until a response containing level answers is returned.
   */
  async fetchParticipantContestLevels(participant: any): Promise<any[]> {
    const cId = Number(this.contestID || participant?.contest_id || participant?.contest?.id);
    if (!cId) return [];

    // Check embedded levels first
    const embeddedLevels = (Array.isArray(participant?.levelsDetail) && participant.levelsDetail.length > 0)
      ? participant.levelsDetail
      : (Array.isArray(participant?.levels_detail) && participant.levels_detail.length > 0)
        ? participant.levels_detail
        : (Array.isArray(participant?.contest_participant?.[0]?.levels_detail) && participant.contest_participant[0].levels_detail.length > 0)
          ? participant.contest_participant[0].levels_detail
          : (Array.isArray(participant?._contest_participant?.[0]?.levels_detail) && participant._contest_participant[0].levels_detail.length > 0)
            ? participant._contest_participant[0].levels_detail
            : [];

    let bestLevels: any[] = [];
    if (embeddedLevels.length > 0) {
      const mapped = embeddedLevels.map((level: any) => ({
        ...level,
        name: level.name || level.level_name || '',
        level_type: level.level_type || level.type,
        status: level?.status,
        points: level.points || 0,
        user_answer: level?.user_answer ?? level?.submitted_answer ?? level?.answer ?? level?.text_answer ?? level?.response ?? level?.survey_answers ?? level?.answers ?? level?.submission ?? level?.result ?? level?.data ?? null,
        accept_any_answer: level?.accept_any_answer ?? level?.acceptAnyAnswer ?? false
      })).sort((a: any, b: any) => (a.id || 0) - (b.id || 0));

      const hasAnswers = mapped.some((l: any) => {
        const a = l.user_answer;
        return a !== undefined && a !== null && a !== '' && a !== '-';
      });

      if (hasAnswers) {
        return mapped;
      }
      bestLevels = mapped;
    }

    const candidateIds = Array.from(new Set([
      participant?.id,
      participant?.participant_id,
      participant?.participantId,
      participant?._end_user?.id,
      participant?.end_user?.id,
      participant?.endUser?.id,
      participant?.user?.id,
      participant?.participant?.id,
      participant?.user_id,
      participant?.userId,
      participant?.end_user_id,
      participant?.endUserId,
      participant?.contest_participant?.[0]?.id,
      participant?.contest_participant?.[0]?.participant_id,
      participant?.contest_participant?.[0]?.user_id,
      participant?._contest_participant?.[0]?.id,
      participant?._contest_participant?.[0]?.participant_id,
      participant?._contest_participant?.[0]?.user_id,
      participant?.contestParticipant?.id,
      participant?.contestParticipant?.participant_id,
      participant?.contestParticipant?.user_id,
      participant?.contest_participants?.[0]?.id,
      participant?.contest_participants?.[0]?.participant_id,
      participant?.contest_participants?.[0]?.user_id,
    ].filter((v: any) => v !== undefined && v !== null && v !== '')));

    for (const testId of candidateIds) {
      try {
        const response: any = await firstValueFrom(
          this.pagesService.getAll('UNIQUE_PARTICIPANTS_CONTEST_LEVELS', {
            participant_id: testId,
            contest_id: cId,
            contests_id: cId,
            user_id: testId
          })
        );
        const rawLevels = response?.levels ?? response?.data ?? (Array.isArray(response) ? response : []);
        if (Array.isArray(rawLevels) && rawLevels.length > 0) {
          const mapped = rawLevels.map((level: any) => ({
            ...level,
            name: level.name || level.level_name || '',
            level_type: level.level_type || level.type,
            status: level?.status,
            points: level.points || 0,
            user_answer: level?.user_answer ?? level?.submitted_answer ?? level?.answer ?? level?.text_answer ?? level?.response ?? level?.survey_answers ?? level?.answers ?? level?.submission ?? level?.result ?? level?.data ?? null,
            accept_any_answer: level?.accept_any_answer ?? level?.acceptAnyAnswer ?? false
          })).sort((a: any, b: any) => (a.id || 0) - (b.id || 0));

          const hasAnswers = mapped.some((l: any) => {
            const a = l.user_answer;
            return a !== undefined && a !== null && a !== '' && a !== '-';
          });

          if (hasAnswers) {
            return mapped;
          }

          if (bestLevels.length === 0) {
            bestLevels = mapped;
          }
        }
      } catch {
        // Continue to next candidate ID
      }
    }

    return bestLevels;
  }

  // Exports every participant's submitted survey answers to Excel with complete information:
  // - Detailed Sheet: Every question, answer option text, question type, participant metadata, timestamps
  // - Participant Summary Sheet: One row per participant with columns for every survey question
  async exportSurveyResponses(): Promise<void> {
    const surveyLevels = (this.levels_of_contest || []).filter(
      (level: any) => (level.level_type || level.type) === 'survey'
    );

    if (surveyLevels.length === 0) {
      this.sharedService.createNotification('warning', this.isArabic ? 'هذه المشاركة لا تحتوي على مستوى استبيان.' : 'This engagement has no survey level.');
      return;
    }

    this.sharedService.showLoader();

    try {
      if (this.contestID) {
        try {
          if (!this.surveyReportDetail) {
            const res: any = await firstValueFrom(
              this.pagesService.getAll('QR_REPORT_DETAIL', { contest_id: Number(this.contestID), qr_instance_id: 0 })
            );
            if (res) {
              this.surveyReportDetail = res;
            }
          }
        } catch {
          // fallback to existing levels_of_contest
        }
      }

      // ── Step 1: Ensure all participants have their level progression and answers loaded ──
      const participants = this.participantsList || [];

      // TURNED OFF -- two fetches that brought nothing the export uses.
      //
      // This used to call ALL_PARTICIPANTS once and then
      // UNIQUE_PARTICIPANTS_CONTEST_LEVELS once per participant, to "load the
      // answers". Checked against the live responses: neither carries an
      // answer. Their levels hold only { id, name, level_type, points,
      // status }, so every participant failed the has-an-answer test and the
      // per-participant call fired for all of them -- 100 participants meant
      // 101 requests for data already on the page.
      //
      // The answers come from get_total_participates_client, fetched when the
      // page opens: participant.contest.levels[].users_progress[].user_answer.
      // That array is already in `participantsList`, and the resolver below
      // reads it through allCandidateLevels -> progressPools. Status, points
      // and the submission date come from the same rows.
      //
      // Restore only behind an endpoint that actually returns answers in bulk.

      const isAr = this.isArabic;
      const detailedRows: any[] = [];
      const participantSummaryRows: any[] = [];
      const allSurveyQuestionsWithLevel: { level: any; question: any; levelName: string; qNumber: string }[] = [];

      surveyLevels.forEach((surveyLevel: any, lIdx: number) => {
        const surveyLevelName = (isAr && surveyLevel.arabic_name)
          ? surveyLevel.arabic_name
          : (surveyLevel.name || surveyLevel.textContent || surveyLevel.level_name || `Level ${surveyLevel.id || lIdx + 1}`);

        let surveyQuestions: any[] = (Array.isArray(surveyLevel.survey_questions) && surveyLevel.survey_questions.length > 0)
          ? surveyLevel.survey_questions
          : [];

        if (this.surveyReportDetail?.survey_questions && Array.isArray(this.surveyReportDetail.survey_questions)) {
          const apiQs = this.surveyReportDetail.survey_questions;
          const matched = apiQs.filter((q: any) => q.levels_id === surveyLevel.id || String(q.levels_id) === String(surveyLevel.id));
          const bestApiQs = matched.length > 0 ? matched : apiQs;
          if (surveyQuestions.length === 0) {
            surveyQuestions = bestApiQs;
          } else {
            surveyQuestions = surveyQuestions.map((sq: any) => {
              const apiMatch = bestApiQs.find((aq: any) => aq.id === sq.id || String(aq.id) === String(sq.id));
              if (apiMatch) {
                const sqOpts = (Array.isArray(sq.options) && sq.options.length > 0) ? sq.options : (sq.answer_options || []);
                const apiOpts = (Array.isArray(apiMatch.options) && apiMatch.options.length > 0) ? apiMatch.options : (apiMatch.answer_options || []);
                const bestOpts = sqOpts.length >= apiOpts.length ? sqOpts : apiOpts;
                return {
                  ...apiMatch,
                  ...sq,
                  answer_options: bestOpts,
                  options: bestOpts,
                  question_text: (sq.question_text && sq.question_text !== '-') ? sq.question_text : apiMatch.question_text,
                  arabic_question_text: (sq.arabic_question_text && sq.arabic_question_text !== '-') ? sq.arabic_question_text : apiMatch.arabic_question_text,
                };
              }
              return sq;
            });
          }
        }
        surveyLevel.survey_questions = this.normalizeSurveyQuestions(surveyQuestions);

        surveyLevel.survey_questions.forEach((sq: any, sIdx: number) => {
          allSurveyQuestionsWithLevel.push({
            level: surveyLevel,
            question: sq,
            levelName: surveyLevelName,
            qNumber: `Q${sIdx + 1}`,
          });
        });
      });

      participants.forEach((participant: any) => {
        const candidateUserIds = Array.from(new Set([
          participant?._end_user?.id,
          participant?.end_user?.id,
          participant?.endUser?.id,
          participant?.user?.id,
          participant?.participant?.id,
          participant?.participant_id,
          participant?.participantId,
          participant?.user_id,
          participant?.userId,
          participant?.end_user_id,
          participant?.endUserId,
          participant?.id,
          participant?.contest_participant?.[0]?.id,
          participant?.contest_participant?.[0]?.participant_id,
          participant?.contest_participant?.[0]?.user_id,
          participant?._contest_participant?.[0]?.id,
          participant?._contest_participant?.[0]?.participant_id,
          participant?._contest_participant?.[0]?.user_id,
          participant?.contestParticipant?.id,
          participant?.contestParticipant?.participant_id,
          participant?.contestParticipant?.user_id,
          participant?.contest_participants?.[0]?.id,
          participant?.contest_participants?.[0]?.participant_id,
          participant?.contest_participants?.[0]?.user_id,
        ].filter((v: any) => v !== undefined && v !== null && v !== '')));

        const participantName = this.getParticipantName(participant);
        const participantEmail = participant?._end_user?.email || participant?.end_user?.email || participant?.email || '-';
        const participantPhone = participant?._end_user?.phone_number || participant?.end_user?.phone_number || participant?.phone_number || participant?.phone || participant?.mobile || '-';
        const participantGender = this.getParticipantGender(participant);
        const participantAge = String(this.getParticipantAge(participant));
        const participantCountry = this.getParticipantLocation(participant);

        const allCandidateLevels: any[] = [
          ...(Array.isArray(participant?.levelsDetail) ? participant.levelsDetail : []),
          ...(Array.isArray(participant?.levels_detail) ? participant.levels_detail : []),
          ...(Array.isArray(participant?.levels) ? participant.levels : []),
          ...(Array.isArray(participant?.contest_participant?.[0]?.levels_detail) ? participant.contest_participant[0].levels_detail : []),
          ...(Array.isArray(participant?.contest_participant?.[0]?.levelsDetail) ? participant.contest_participant[0].levelsDetail : []),
          ...(Array.isArray(participant?.contest_participant?.[0]?.levels) ? participant.contest_participant[0].levels : []),
          ...(Array.isArray(participant?._contest_participant?.[0]?.levels_detail) ? participant._contest_participant[0].levels_detail : []),
          ...(Array.isArray(participant?._contest_participant?.[0]?.levelsDetail) ? participant._contest_participant[0].levelsDetail : []),
          ...(Array.isArray(participant?.contestParticipant?.levels_detail) ? participant.contestParticipant.levels_detail : []),
          ...(Array.isArray(participant?.contestParticipant?.levelsDetail) ? participant.contestParticipant.levelsDetail : []),
          ...(Array.isArray(participant?.contest?.levels) ? participant.contest.levels : []),
          ...(Array.isArray(participant?.contest?._levels_of_contest) ? participant.contest._levels_of_contest : []),
          ...(Array.isArray(participant?.contest_detail?.levels) ? participant.contest_detail.levels : []),
          ...(Array.isArray(participant?.contest_detail?._levels_of_contest) ? participant.contest_detail._levels_of_contest : []),
        ];

        surveyLevels.forEach((surveyLevel: any, lIdx: number) => {
          const surveyLevelName = (isAr && surveyLevel.arabic_name)
            ? surveyLevel.arabic_name
            : (surveyLevel.name || surveyLevel.textContent || surveyLevel.level_name || `Level ${surveyLevel.id || lIdx + 1}`);
          const surveyQuestions: any[] = (Array.isArray(surveyLevel.survey_questions) && surveyLevel.survey_questions.length > 0)
            ? surveyLevel.survey_questions
            : [];

          const matchedParticipantLevels = allCandidateLevels.filter((l: any) =>
            (l.level_id !== undefined && String(l.level_id) === String(surveyLevel.id)) ||
            (l.levels_id !== undefined && String(l.levels_id) === String(surveyLevel.id)) ||
            (l.id !== undefined && String(l.id) === String(surveyLevel.id)) ||
            (l.name && surveyLevel.name && String(l.name).trim().toLowerCase() === String(surveyLevel.name).trim().toLowerCase()) ||
            ((l.level_type || l.type) === 'survey')
          );

          // Find candidate level that carries an answer
          let matchedParticipantLevel = matchedParticipantLevels.find((ml: any) => {
            const a = ml?.user_answer ?? ml?.submitted_answer ?? ml?.answer ?? ml?.text_answer ?? ml?.response ?? ml?.survey_answers ?? ml?.answers ?? ml?.submission ?? ml?.result ?? ml?.data;
            return a !== undefined && a !== null && a !== '' && a !== '-';
          }) || matchedParticipantLevels[0] || null;

          const matchesUser = (up: any) => {
            const upUid = up?.user_id ?? up?.userId ?? up?.participant_id ?? up?.participantId ?? up?.end_user_id ?? up?.endUserId ?? up?.id ?? up?.created_by;
            return candidateUserIds.some((cid: any) => String(cid) === String(upUid));
          };
          const matchesLevel = (up: any) => {
            const lvl = up?.levels_id ?? up?.level_id;
            return lvl === undefined || lvl === null || String(lvl) === String(surveyLevel.id);
          };

          const progressPools: any[][] = [
            ...matchedParticipantLevels.map((l: any) => Array.isArray(l.users_progress) ? l.users_progress : []),
            Array.isArray(surveyLevel.users_progress) ? surveyLevel.users_progress : [],
            Array.isArray(this.surveyReportDetail?.users_progress) ? this.surveyReportDetail.users_progress : [],
            Array.isArray(participant?.users_progress) ? participant.users_progress : [],
          ];

          let progress: any = null;
          let rawUserAnswer: any = null;

          // 1. Check matched participant level directly
          if (matchedParticipantLevel) {
            const ans = matchedParticipantLevel?.user_answer ?? matchedParticipantLevel?.submitted_answer ?? matchedParticipantLevel?.answer ?? matchedParticipantLevel?.text_answer ?? matchedParticipantLevel?.response ?? matchedParticipantLevel?.survey_answers ?? matchedParticipantLevel?.answers ?? matchedParticipantLevel?.submission ?? matchedParticipantLevel?.result ?? matchedParticipantLevel?.data;
            if (ans !== undefined && ans !== null && ans !== '' && ans !== '-') {
              rawUserAnswer = ans;
            }
          }

          // 2. Check allCandidateLevels for any survey level with answer
          if (rawUserAnswer === null || rawUserAnswer === undefined || rawUserAnswer === '' || rawUserAnswer === '-') {
            const sc = allCandidateLevels.find((l: any) => {
              const isSurveyType = (l.level_type || l.type) === 'survey' || (l.name && surveyLevel.name && String(l.name).trim().toLowerCase() === String(surveyLevel.name).trim().toLowerCase());
              if (!isSurveyType) return false;
              const a = l.user_answer ?? l.submitted_answer ?? l.answer ?? l.text_answer ?? l.response ?? l.survey_answers ?? l.answers ?? l.submission ?? l.result ?? l.data;
              return a !== undefined && a !== null && a !== '' && a !== '-';
            });
            if (sc) {
              rawUserAnswer = sc.user_answer ?? sc.submitted_answer ?? sc.answer ?? sc.text_answer ?? sc.response ?? sc.survey_answers ?? sc.answers ?? sc.submission ?? sc.result ?? sc.data;
              if (!matchedParticipantLevel) matchedParticipantLevel = sc;
            }
          }

          // 3. Check progress pools
          if (rawUserAnswer === null || rawUserAnswer === undefined || rawUserAnswer === '' || rawUserAnswer === '-') {
            for (const pool of progressPools) {
              const up = pool.find((item: any) => matchesUser(item) && matchesLevel(item));
              if (up) {
                if (!progress) progress = up;
                const ans = up?.user_answer ?? up?.submitted_answer ?? up?.answer ?? up?.text_answer ?? up?.response ?? up?.survey_answers ?? up?.answers ?? up?.submission ?? up?.result ?? up?.data;
                if (ans !== undefined && ans !== null && ans !== '' && ans !== '-') {
                  rawUserAnswer = ans;
                  break;
                }
              }
            }
          }

          // 4. Check participant direct answer properties
          if (rawUserAnswer === null || rawUserAnswer === undefined || rawUserAnswer === '' || rawUserAnswer === '-') {
            const pAns = participant?.survey_answers ?? participant?.answers ?? participant?.user_answer ?? participant?.submitted_answer ?? participant?.response ?? participant?.submission ??
              participant?.contest_participant?.[0]?.user_answer ?? participant?.contest_participant?.[0]?.submitted_answer ?? participant?.contest_participant?.[0]?.answer ?? participant?.contest_participant?.[0]?.survey_answers ?? participant?.contest_participant?.[0]?.answers ??
              participant?._contest_participant?.[0]?.user_answer ?? participant?._contest_participant?.[0]?.submitted_answer ?? participant?._contest_participant?.[0]?.answer ?? participant?._contest_participant?.[0]?.survey_answers ??
              participant?.contestParticipant?.user_answer ?? participant?.contestParticipant?.submitted_answer ?? participant?.contestParticipant?.answer ?? participant?.contestParticipant?.survey_answers;
            if (pAns !== undefined && pAns !== null && pAns !== '' && pAns !== '-') {
              rawUserAnswer = pAns;
            }
          }

          const levelStatus = progress?.status ?? matchedParticipantLevel?.status ?? participant?.contestStatus ?? participant?.status ?? (rawUserAnswer ? 'completed' : 'pending');
          const rawDate = progress?.completed_at || progress?.created_at || matchedParticipantLevel?.completed_at || participant?.participation_date || participant?.join_date || participant?.created_at;
          const formattedDate = rawDate ? this.formatDate(rawDate) : '-';
          const points = progress?.points ?? matchedParticipantLevel?.points ?? 0;

          const parsedAnswerData = (rawUserAnswer && rawUserAnswer !== '-') ? this.parseJsonSafely(rawUserAnswer) : null;

          const summaryRow: any = {
            '#': participantSummaryRows.length + 1,
            [isAr ? 'اسم المشارك' : 'Participant Name']: participantName,
            [isAr ? 'البريد الإلكتروني' : 'Email']: participantEmail,
            [isAr ? 'رقم الهاتف' : 'Phone Number']: participantPhone,
            [isAr ? 'الجنس' : 'Gender']: participantGender,
            [isAr ? 'العمر' : 'Age']: participantAge,
            [isAr ? 'البلد' : 'Country']: participantCountry,
            [isAr ? 'مستوى الاستبيان' : 'Survey Level']: surveyLevelName,
          };

          if (surveyQuestions.length > 0) {
            surveyQuestions.forEach((sq: any, sIdx: number) => {
              const qNumber = `Q${sIdx + 1}`;
              const qText = sq.question_text || `Question ${sIdx + 1}`;
              const qArText = sq.arabic_question_text || qText;
              const displayQText = isAr ? qArText : qText;

              const questionVal = this.extractQuestionAnswer(parsedAnswerData, sq, sIdx, surveyQuestions.length, surveyQuestions);
              const resolved = this.resolveSurveyQuestionAnswer(questionVal, sq);

              const opts = (Array.isArray(sq.options) && sq.options.length > 0) ? sq.options : (sq.answer_options || []);
              const availableOptionsEn = opts.length > 0
                ? opts.map((o: any, i: number) => o.option_text || `Option ${i + 1}`).join(' | ')
                : (isAr ? 'نص حر' : 'Open Text');
              const availableOptionsAr = opts.length > 0
                ? opts.map((o: any, i: number) => o.arabic_option_text || o.option_text || `Option ${i + 1}`).join(' | ')
                : (isAr ? 'نص حر' : 'Open Text');

              const qTypeLabel = sq.question_type === 'mcq'
                ? (sq.is_multi_select ? (isAr ? 'اختيار متعدد (متعدد)' : 'Multiple Choice (Multi-Select)') : (isAr ? 'اختيار متعدد (فردي)' : 'Multiple Choice (Single)'))
                : (isAr ? 'إجابة نصية حرة' : 'Open Text');

              detailedRows.push({
                '#': detailedRows.length + 1,
                [isAr ? 'اسم المشارك' : 'Participant Name']: participantName,
                [isAr ? 'البريد الإلكتروني' : 'Email']: participantEmail,
                [isAr ? 'رقم الهاتف' : 'Phone Number']: participantPhone,
                [isAr ? 'الجنس' : 'Gender']: participantGender,
                [isAr ? 'العمر' : 'Age']: participantAge,
                [isAr ? 'البلد' : 'Country']: participantCountry,
                [isAr ? 'مستوى الاستبيان' : 'Survey Level']: surveyLevelName,
                [isAr ? 'رقم السؤال' : 'Question #']: qNumber,
                [isAr ? 'السؤال' : 'Question']: qText,
                [isAr ? 'السؤال (بالعربية)' : 'Question (Arabic)']: qArText,
                [isAr ? 'نوع السؤال' : 'Question Type']: qTypeLabel,
                [isAr ? 'الخيارات المتاحة' : 'Available Options']: availableOptionsEn,
                [isAr ? 'الخيارات المتاحة (بالعربية)' : 'Available Options (Arabic)']: availableOptionsAr,
                [isAr ? 'الإجابة المختارة' : 'Submitted Answer']: resolved.displayAnswer,
                [isAr ? 'الإجابة المختارة (بالعربية)' : 'Submitted Answer (Arabic)']: resolved.arabicDisplayAnswer,
                [isAr ? 'القيمة الأصلية' : 'Raw Value']: resolved.rawAnswerText,
                [isAr ? 'النقاط' : 'Points']: points,
                [isAr ? 'الحالة' : 'Status']: levelStatus,
                [isAr ? 'تاريخ المشاركة' : 'Submission Date']: formattedDate,
              });

              const summaryColName = `${qNumber}: ${displayQText}`;
              summaryRow[summaryColName] = isAr ? resolved.arabicDisplayAnswer : resolved.displayAnswer;
            });
          } else {
            const rawText = rawUserAnswer ? (typeof rawUserAnswer === 'object' ? JSON.stringify(rawUserAnswer) : String(rawUserAnswer)) : '-';
            detailedRows.push({
              '#': detailedRows.length + 1,
              [isAr ? 'اسم المشارك' : 'Participant Name']: participantName,
              [isAr ? 'البريد الإلكتروني' : 'Email']: participantEmail,
              [isAr ? 'رقم الهاتف' : 'Phone Number']: participantPhone,
              [isAr ? 'الجنس' : 'Gender']: participantGender,
              [isAr ? 'العمر' : 'Age']: participantAge,
              [isAr ? 'البلد' : 'Country']: participantCountry,
              [isAr ? 'مستوى الاستبيان' : 'Survey Level']: surveyLevelName,
              [isAr ? 'رقم السؤال' : 'Question #']: 'Q1',
              [isAr ? 'السؤال' : 'Question']: 'Survey Response',
              [isAr ? 'السؤال (بالعربية)' : 'Question (Arabic)']: 'إجابة الاستبيان',
              [isAr ? 'نوع السؤال' : 'Question Type']: 'General',
              [isAr ? 'الخيارات المتاحة' : 'Available Options']: '-',
              [isAr ? 'الخيارات المتاحة (بالعربية)' : 'Available Options (Arabic)']: '-',
              [isAr ? 'الإجابة المختارة' : 'Submitted Answer']: rawText,
              [isAr ? 'الإجابة المختارة (بالعربية)' : 'Submitted Answer (Arabic)']: rawText,
              [isAr ? 'القيمة الأصلية' : 'Raw Value']: rawText,
              [isAr ? 'النقاط' : 'Points']: points,
              [isAr ? 'الحالة' : 'Status']: levelStatus,
              [isAr ? 'تاريخ المشاركة' : 'Submission Date']: formattedDate,
            });
            summaryRow['Survey Response'] = rawText;
          }

          summaryRow[isAr ? 'النقاط' : 'Points'] = points;
          summaryRow[isAr ? 'الحالة' : 'Status'] = levelStatus;
          summaryRow[isAr ? 'تاريخ المشاركة' : 'Submission Date'] = formattedDate;
          participantSummaryRows.push(summaryRow);
        });
      });

      if (detailedRows.length === 0) {
        this.sharedService.createNotification('warning', isAr ? 'لم يتم تقديم أي إجابات استبيان بعد.' : 'No survey responses have been submitted yet.');
        return;
      }

      const workbook = XLSX.utils.book_new();

      const detailedWs = XLSX.utils.json_to_sheet(detailedRows);
      detailedWs['!cols'] = [
        { wch: 6 },
        { wch: 22 },
        { wch: 25 },
        { wch: 18 },
        { wch: 10 },
        { wch: 8 },
        { wch: 16 },
        { wch: 18 },
        { wch: 12 },
        { wch: 32 },
        { wch: 32 },
        { wch: 26 },
        { wch: 28 },
        { wch: 28 },
        { wch: 26 },
        { wch: 26 },
        { wch: 16 },
        { wch: 10 },
        { wch: 14 },
        { wch: 20 },
      ];

      const summaryWs = XLSX.utils.json_to_sheet(participantSummaryRows);
      summaryWs['!cols'] = [
        { wch: 6 },
        { wch: 22 },
        { wch: 25 },
        { wch: 18 },
        { wch: 10 },
        { wch: 8 },
        { wch: 16 },
        { wch: 18 },
        ...allSurveyQuestionsWithLevel.map(() => ({ wch: 28 })),
        { wch: 10 },
        { wch: 14 },
        { wch: 20 },
      ];

      if (isAr) {
        detailedWs['!views'] = [{ RTL: true }];
        summaryWs['!views'] = [{ RTL: true }];
      }

      const detailedSheetName = isAr ? 'الاستجابات التفصيلية' : 'Detailed Responses';
      const summarySheetName = isAr ? 'ملخص المشاركين' : 'Participant Summary';

      XLSX.utils.book_append_sheet(workbook, detailedWs, detailedSheetName);
      XLSX.utils.book_append_sheet(workbook, summaryWs, summarySheetName);

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = isAr
        ? `تقرير_استجابات_الاستبيان_${this.name || this.contestID}_${timestamp}.xlsx`
        : `Survey_Responses_${this.name || this.contestID}_${timestamp}.xlsx`;

      saveAs(blob, filename);
      this.sharedService.createNotification('success', isAr ? 'تم تصدير إجابات الاستبيان بنجاح!' : 'Survey responses exported successfully!');
    } catch (error) {
      console.error('Error exporting survey responses:', error);
      this.sharedService.createNotification('error', this.isArabic ? 'حدث خطأ أثناء تصدير إجابات الاستبيان.' : 'Failed to export survey responses.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  parseSurveyAnswer(userAnswer: any, surveyQuestions: any[] = []): { question: string; answer: string }[] {
    if (!userAnswer) return [];

    const parsed = this.parseJsonSafely(userAnswer);
    const normalizedQuestions = this.normalizeSurveyQuestions(surveyQuestions);
    const results: { question: string; answer: string }[] = [];

    if (normalizedQuestions.length > 0) {
      normalizedQuestions.forEach((sq: any, sIdx: number) => {
        const qTitle = this.isArabic
          ? (sq.arabic_question_text || sq.question_text)
          : (sq.question_text || `Question ${sIdx + 1}`);

        const qVal = this.extractQuestionAnswer(parsed, sq, sIdx, normalizedQuestions.length, normalizedQuestions);
        const resolved = this.resolveSurveyQuestionAnswer(qVal, sq);
        const ans = this.isArabic ? (resolved.arabicDisplayAnswer || resolved.displayAnswer) : resolved.displayAnswer;
        results.push({ question: qTitle, answer: ans });
      });
    } else {
      if (Array.isArray(parsed)) {
        parsed.forEach((item: any, idx: number) => {
          const qText = item?.question || item?.question_text || item?.title || `Question ${idx + 1}`;
          const val = item?.answer ?? item?.value ?? (typeof item === 'string' ? item : JSON.stringify(item));
          results.push({ question: qText, answer: String(val ?? '-') });
        });
      } else if (parsed && typeof parsed === 'object') {
        Object.keys(parsed).forEach((key: string, idx: number) => {
          const val = parsed[key];
          const ans = typeof val === 'string' ? val : (val?.answer ?? val?.value ?? JSON.stringify(val));
          results.push({ question: isNaN(Number(key)) ? key : `Question ${idx + 1}`, answer: String(ans ?? '-') });
        });
      } else {
        results.push({ question: 'Survey Response', answer: String(parsed ?? '-') });
      }
    }

    return results;
  }

  private flattenSurveyAnswerObject(parsed: any, surveyQuestions: any[] = []): { question: string; answer: string }[] {
    return this.parseSurveyAnswer(parsed, surveyQuestions);
  }


  async exportToPDF(): Promise<void> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    let y = 0;

    const isAr = isArabicLang();
    const arabicFontLoaded = await ensureArabicFont(doc);

    const L = isAr ? {
      headerTitle: 'تقرير تفاصيل المشاركة',
      untitled: 'مشاركة بدون عنوان',
      uidLabel: 'المعرف',
      accessCodeLabel: 'رمز الدخول',
      startLabel: 'البداية',
      endLabel: 'النهاية',
      drawLabel: 'السحب',
      na: 'غير متوفر',
      prizesHeading: 'الجوائز',
      prizeNameCol: 'اسم الجائزة',
      valueCol: 'القيمة',
      winnersCol: 'الفائزون',
      drawTypeCol: 'نوع السحب',
      levelsHeading: 'مستويات المشاركة',
      numCol: '#',
      levelNameCol: 'اسم المستوى',
      descriptionCol: 'الوصف',
      pointsCol: 'النقاط',
      performanceHeading: 'أداء المشاركة',
      metricCol: 'المقياس',
      totalViews: 'إجمالي المشاهدات',
      totalClicks: 'إجمالي النقرات',
      participantsMetric: 'المشاركون',
      completed: 'مكتمل',
      overallSuccessRate: 'معدل النجاح الإجمالي',
      completionRate: 'معدل الإكمال',
      participationRate: 'معدل المشاركة',
      engagementRate: 'معدل التفاعل',
      participantsHeading: (count: number) => `المشاركون (${count})`,
      participantCol: 'المشارك',
      participationDateCol: 'تاريخ المشاركة',
      ageCol: 'العمر',
      genderCol: 'الجنس',
      statusCol: 'الحالة',
      locationCol: 'الموقع',
      statusCompleted: 'مكتمل',
      statusNotStarted: 'لم يبدأ',
      statusInProgress: 'قيد التقدم',
      noParticipants: 'لا توجد بيانات مشاركين متاحة.',
      winnersHeading: 'الفائزون',
      rankCol: 'الترتيب',
      completionTimestampCol: 'وقت الإكمال',
      prizeStatusCol: 'حالة الجائزة',
      noWinners: 'لا توجد بيانات فائزين متاحة.',
      previewHeading: 'معاينة الحملة (تطبيق الجوال)',
      pageOf: (p: number, total: number) => `صفحة ${p} من ${total}`,
      generated: (date: string) => `تم الإنشاء: ${date}`,
    } : {
      headerTitle: 'Campaign Details Report',
      untitled: 'Untitled Campaign',
      uidLabel: 'UID',
      accessCodeLabel: 'Access Code',
      startLabel: 'Start',
      endLabel: 'End',
      drawLabel: 'Draw',
      na: 'N/A',
      prizesHeading: 'Prizes',
      prizeNameCol: 'Prize Name',
      valueCol: 'Value',
      winnersCol: 'Winners',
      drawTypeCol: 'Draw Type',
      levelsHeading: 'Engagement Levels',
      numCol: '#',
      levelNameCol: 'Level Name',
      descriptionCol: 'Description',
      pointsCol: 'Points',
      performanceHeading: 'Engagement Performance',
      metricCol: 'Metric',
      totalViews: 'Total Views',
      totalClicks: 'Total Clicks',
      participantsMetric: 'Participants',
      completed: 'Completed',
      overallSuccessRate: 'Overall Success Rate',
      completionRate: 'Completion Rate',
      participationRate: 'Participation Rate',
      engagementRate: 'Engagement Rate',
      participantsHeading: (count: number) => `Participants (${count})`,
      participantCol: 'Participant',
      participationDateCol: 'Participation Date',
      ageCol: 'Age',
      genderCol: 'Gender',
      statusCol: 'Status',
      locationCol: 'Location',
      statusCompleted: 'Completed',
      statusNotStarted: 'Not Started',
      statusInProgress: 'In Progress',
      noParticipants: 'No participant data available.',
      winnersHeading: 'Winners',
      rankCol: 'Rank',
      completionTimestampCol: 'Completion Timestamp',
      prizeStatusCol: 'Prize Status',
      noWinners: 'No winners data available.',
      previewHeading: 'Contest Preview (Mobile App)',
      pageOf: (p: number, total: number) => `Page ${p} of ${total}`,
      generated: (date: string) => `Generated: ${date}`,
    };

    // Header bar
    doc.setFillColor(60, 52, 137);
    doc.rect(0, 0, pageWidth, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    drawBilingualText(doc, L.headerTitle, pageWidth / 2, 14, arabicFontLoaded, { align: 'center' });
    y = 32;

    // Contest preview (banner image)
    const bannerUrl = this.banners?.[0]?.url || (this.banners?.[0] as any)?.path || '';
    const previewData = bannerUrl ? await this.sharedService.getImageDataUrl(bannerUrl) : null;
    if (previewData) {
      const previewWidth = pageWidth - 28;
      const previewHeight = 55;
      try {
        doc.addImage(previewData, 'JPEG', 14, y, previewWidth, previewHeight, undefined, 'FAST');
      } catch {
        // ignore image draw failures, continue with text-only report
      }
      y += previewHeight + 8;
    }

    // Contest name
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    drawBilingualText(doc, this.name || L.untitled, 14, y, arabicFontLoaded);
    y += 7;

    // UID / Access Code
    const idParts: string[] = [];
    if (this.hasValue(this.contests_detail?.uid)) idParts.push(`${L.uidLabel}: ${this.contests_detail.uid}`);
    if (this.hasValue(this.contests_detail?.access_code)) idParts.push(`${L.accessCodeLabel}: ${this.contests_detail.access_code}`);
    if (idParts.length) {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(110, 110, 110);
      drawBilingualText(doc, idParts.join('   |   '), 14, y, arabicFontLoaded);
      y += 6;
    }

    // Description
    if (this.description) {
      const plainDesc = this.description.replace(/<[^>]*>/g, '').trim();
      if (plainDesc) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const lines = doc.splitTextToSize(plainDesc, pageWidth - 28);
        doc.text(lines, 14, y);
        y += lines.length * 5 + 3;
      }
    }

    // Dates row
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const fmt = (d: any) => d ? new Date(d).toLocaleString() : L.na;
    drawBilingualText(doc, `${L.startLabel}: ${fmt(this.start_date)}`, 14, y, arabicFontLoaded);
    drawBilingualText(doc, `${L.endLabel}: ${fmt(this.end_date)}`, 14, y + 5, arabicFontLoaded);
    drawBilingualText(doc, `${L.drawLabel}: ${fmt(this.draw_date)}`, 14, y + 10, arabicFontLoaded);
    y += 18;

    // Prizes
    if (this._contest_prize && this._contest_prize.length > 0) {
      y = this.pdfSectionHeading(doc, L.prizesHeading, y, arabicFontLoaded);
      const prizeRows = this._contest_prize.map((prize: any) => [
        prize?.name || L.na,
        prize?.value != null ? `${prize?.currency || 'USD'} ${prize.value}` : L.na,
        this.contests_detail?.number_of_winners != null ? String(this.contests_detail.number_of_winners) : L.na,
        this.getDrawTypeLabel ? this.getDrawTypeLabel() : (this.contests_detail?.draw_type || L.na),
      ]);
      autoTable(doc, {
        startY: y,
        head: [[L.prizeNameCol, L.valueCol, L.winnersCol, L.drawTypeCol]],
        body: prizeRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Contest Levels
    if (this.levels_of_contest && this.levels_of_contest.length > 0) {
      y = this.pdfEnsureSpace(doc, y, 30);
      y = this.pdfSectionHeading(doc, L.levelsHeading, y, arabicFontLoaded);

      const levelRows = this.levels_of_contest.map((lvl: any, i: number) => [
        String(i + 1),
        lvl.name || lvl.level_name || `Level ${i + 1}`,
        lvl.description || lvl.task || '',
        lvl.points != null ? String(lvl.points) : L.na,
      ]);

      autoTable(doc, {
        startY: y,
        head: [[L.numCol, L.levelNameCol, L.descriptionCol, L.pointsCol]],
        body: levelRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 45 }, 2: { cellWidth: 'auto' }, 3: { cellWidth: 20 } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Mobile app preview mockup
    y = this.pdfEnsureSpace(doc, y, 140);
    y = this.pdfSectionHeading(doc, L.previewHeading, y, arabicFontLoaded);
    await this.drawMobilePreview(doc, y, isAr, arabicFontLoaded);
    y += 140;

    // Performance Overview (matches on-screen 8 metric cards)
    y = this.pdfEnsureSpace(doc, y, 60);
    y = this.pdfSectionHeading(doc, L.performanceHeading, y, arabicFontLoaded);

    autoTable(doc, {
      startY: y,
      head: [[L.metricCol, L.valueCol]],
      body: [
        [L.totalViews, String(this.performanceStats.totalContests ?? 0)],
        [L.totalClicks, String(this.performanceStats.activeContests ?? 0)],
        [L.participantsMetric, String(this.performanceStats.pendingContests ?? 0)],
        [L.completed, String(this.performanceStats.finishedContests ?? 0)],
        [L.overallSuccessRate, `${(this.performanceStats.avgClickRate ?? 0).toFixed(2)}%`],
        [L.completionRate, `${(this.performanceStats.avgCompletionRate ?? 0).toFixed(2)}%`],
        [L.participationRate, `${(this.performanceStats.avgParticipationRate ?? 0).toFixed(2)}%`],
        [L.engagementRate, `${(this.performanceStats.avgEngagementRate ?? 0).toFixed(2)}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 'auto' } },
      margin: { left: 14, right: 14 },
      didParseCell: arabicAwareCellHook(arabicFontLoaded),
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Participants
    y = this.pdfEnsureSpace(doc, y, 30);
    y = this.pdfSectionHeading(doc, L.participantsHeading(this.filteredParticipants?.length || 0), y, arabicFontLoaded);

    if (this.filteredParticipants && this.filteredParticipants.length > 0) {
      // Age and gender are not collected on Web QR, so the export drops those
      // two columns there — it has to match what the screen shows.
      const participantRows = this.filteredParticipants.map((p: any) => [
        this.getParticipantName(p),
        this.getParticipationDate(p) ? new Date(this.getParticipationDate(p) as string).toLocaleString() : L.na,
        ...(this.isWebQrMode ? [] : [String(this.getParticipantAge(p)), this.getParticipantGender(p)]),
        p.status === 'completed' ? L.statusCompleted : (p.status === 'not_started' ? L.statusNotStarted : L.statusInProgress),
        this.getParticipantLocation(p),
      ]);

      autoTable(doc, {
        startY: y,
        head: [[
          L.participantCol,
          L.participationDateCol,
          ...(this.isWebQrMode ? [] : [L.ageCol, L.genderCol]),
          L.statusCol,
          L.locationCol,
        ]],
        body: participantRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 140, 140);
      drawBilingualText(doc, L.noParticipants, 14, y, arabicFontLoaded);
      y += 10;
    }

    // Winners
    y = this.pdfEnsureSpace(doc, y, 30);
    y = this.pdfSectionHeading(doc, L.winnersHeading, y, arabicFontLoaded);

    if (this.winnersList && this.winnersList.length > 0) {
      const winnerRows = this.winnersList.map((w: any, i: number) => [
        String(i + 1),
        w._end_user?.name || w.contest?.user_id || L.na,
        ...(this.isWebQrMode ? [] : [this.getParticipantGender(w), String(this.getParticipantAge(w))]),
        this.getWinnerCompletionTimestamp(w) ? new Date(this.getWinnerCompletionTimestamp(w) as string).toLocaleString() : L.na,
        this.getWinnerPrizeStatus(w),
        w._end_user?.country || w.contest?.location || L.na,
      ]);

      autoTable(doc, {
        startY: y,
        head: [[
          L.rankCol,
          L.participantCol,
          ...(this.isWebQrMode ? [] : [L.genderCol, L.ageCol]),
          L.completionTimestampCol,
          L.prizeStatusCol,
          L.locationCol,
        ]],
        body: winnerRows,
        theme: 'striped',
        headStyles: { fillColor: [8, 80, 65], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 140, 140);
      drawBilingualText(doc, L.noWinners, 14, y, arabicFontLoaded);
    }

    // Footer
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      drawBilingualText(doc, L.pageOf(p, totalPages), pageWidth / 2, doc.internal.pageSize.height - 6, arabicFontLoaded, { align: 'center' });
      drawBilingualText(doc, L.generated(new Date().toLocaleString()), 14, doc.internal.pageSize.height - 6, arabicFontLoaded);
    }

    const sanitizedName = (this.name || 'engagement').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
    const fileName = `${sanitizedName}_details.pdf`;
    doc.save(fileName);
  }

  private pdfImageFormat(dataUrl: string): string {
    if (dataUrl.startsWith('data:image/png')) return 'PNG';
    if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
    return 'JPEG';
  }

  // Draws a modern, realistic phone-frame mockup of the contest mobile app preview.
  private async drawMobilePreview(doc: jsPDF, y: number, isAr: boolean = false, arabicFontLoaded: boolean = false): Promise<void> {
    const pageWidth = doc.internal.pageSize.width;
    const frameWidth = 74;
    const frameHeight = 144;
    const x = (pageWidth - frameWidth) / 2;

    // --- Phone Chassis (Outer dark titanium frame) ---
    doc.setFillColor(22, 24, 30);
    doc.roundedRect(x, y, frameWidth, frameHeight, 8, 8, 'F');
    doc.setDrawColor(55, 65, 81);
    doc.setLineWidth(0.7);
    doc.roundedRect(x, y, frameWidth, frameHeight, 8, 8, 'S');

    // Hardware buttons on edges
    doc.setFillColor(31, 35, 43);
    doc.roundedRect(x - 0.7, y + 24, 0.7, 8, 0.3, 0.3, 'F'); // Volume Up
    doc.roundedRect(x - 0.7, y + 35, 0.7, 8, 0.3, 0.3, 'F'); // Volume Down
    doc.roundedRect(x + frameWidth, y + 28, 0.7, 12, 0.3, 0.3, 'F'); // Power

    // --- Screen Display (Inner bezel) ---
    const screenX = x + 2.5;
    const screenY = y + 2.5;
    const screenWidth = frameWidth - 5;
    const screenHeight = frameHeight - 5;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(screenX, screenY, screenWidth, screenHeight, 6, 6, 'F');

    // --- Dynamic Island / Top Notch ---
    const notchW = 22;
    const notchH = 3.6;
    const notchX = x + (frameWidth - notchW) / 2;
    const notchY = screenY + 2;

    doc.setFillColor(15, 17, 23);
    doc.roundedRect(notchX, notchY, notchW, notchH, 1.8, 1.8, 'F');
    // Camera lens dot & speaker
    doc.setFillColor(28, 32, 42);
    doc.circle(notchX + 17, notchY + 1.8, 0.7, 'F');
    doc.setFillColor(38, 42, 54);
    doc.roundedRect(notchX + 5, notchY + 1.4, 7, 0.6, 0.3, 0.3, 'F');

    // --- Status Bar (Time, Battery, Wifi) ---
    doc.setFontSize(5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 35);
    doc.text('9:41', screenX + 4, screenY + 4.5);

    // Battery & signal icons
    const statRightX = screenX + screenWidth - 11;
    doc.setFillColor(30, 30, 35);
    doc.roundedRect(statRightX + 4, screenY + 2.8, 4.5, 2.2, 0.4, 0.4, 'S');
    doc.roundedRect(statRightX + 4.5, screenY + 3.2, 2.5, 1.4, 0.2, 0.2, 'F');
    doc.roundedRect(statRightX + 8.5, screenY + 3.4, 0.5, 1, 0.2, 0.2, 'F');
    // Wifi bars
    doc.circle(statRightX + 1.5, screenY + 4.2, 0.6, 'F');

    // --- In-App Top Navigation Bar ---
    const navY = screenY + 7.5;
    doc.setFillColor(243, 244, 246);
    doc.circle(screenX + 5, navY + 2, 2.5, 'F');
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(55, 65, 81);
    doc.text('‹', screenX + 4.3, navY + 2.8);

    const typeBadge = String(this.contests_detail?.type || 'Contest').toUpperCase();
    doc.setFillColor(238, 235, 255);
    doc.roundedRect(screenX + screenWidth - 22, navY, 17, 4.2, 2, 2, 'F');
    doc.setTextColor(83, 4, 251);
    doc.setFontSize(4.5);
    doc.text(typeBadge, screenX + screenWidth - 13.5, navY + 2.9, { align: 'center' });

    // --- Contest Banner Hero Area ---
    const bannerX = screenX + 3;
    const bannerY = navY + 6;
    const bannerW = screenWidth - 6;
    const bannerH = 44;

    const bannerUrl = this.banners && this.banners.length > 0 ? (this.banners[0].url || (this.banners[0] as any).path) : null;
    let bannerDrawn = false;
    if (bannerUrl) {
      try {
        const dataUrl = await this.sharedService.getImageDataUrl(bannerUrl);
        if (dataUrl) {
          doc.addImage(dataUrl, this.pdfImageFormat(dataUrl), bannerX, bannerY, bannerW, bannerH);
          bannerDrawn = true;
        }
      } catch {
        bannerDrawn = false;
      }
    }

    if (!bannerDrawn) {
      doc.setFillColor(60, 52, 137);
      doc.roundedRect(bannerX, bannerY, bannerW, bannerH, 4, 4, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('LuQta Campaign', bannerX + bannerW / 2, bannerY + bannerH / 2, { align: 'center' });
    } else {
      // Crisp subtle border around banner
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.3);
      doc.roundedRect(bannerX, bannerY, bannerW, bannerH, 3, 3, 'S');
    }

    // Floating Active Status Badge on Banner
    doc.setFillColor(16, 185, 129);
    doc.roundedRect(bannerX + 3, bannerY + 3, 14, 4, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(4.5);
    doc.setFont('helvetica', 'bold');
    doc.text('ACTIVE', bannerX + 10, bannerY + 5.8, { align: 'center' });

    // --- Content Details (Title, Description, Stats) ---
    let contentCursorY = bannerY + bannerH + 4;
    const contentX = screenX + 4;
    const contentW = screenWidth - 8;

    const rawTitle = this.name || (isAr ? 'الحملة' : 'Campaign');
    const isArabicTitle = containsArabic(rawTitle);

    if (isArabicTitle) {
      const titleImg = renderArabicTextAsImage(rawTitle, contentW, 8.5, '#111827', isAr ? 'right' : 'left', 'bold', 2);
      if (titleImg) {
        const imgX = isAr ? (contentX + contentW - titleImg.widthMm) : contentX;
        doc.addImage(titleImg.dataUrl, 'PNG', imgX, contentCursorY, titleImg.widthMm, titleImg.heightMm);
        contentCursorY += titleImg.heightMm + 2.5;
      } else {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(17, 24, 39);
        drawBilingualText(doc, rawTitle, isAr ? contentX + contentW : contentX, contentCursorY + 3, arabicFontLoaded, { align: isAr ? 'right' : 'left' });
        contentCursorY += 6;
      }
    } else {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(17, 24, 39);
      const titleLines = doc.splitTextToSize(rawTitle, contentW).slice(0, 2);
      doc.text(titleLines, contentX, contentCursorY + 3);
      contentCursorY += titleLines.length * 3.8 + 2;
    }

    // Description
    const rawDesc = (this.description || '').replace(/<[^>]*>/g, '').trim() || (isAr ? 'شارك في هذه المسابقة واربح جوائز قيمة.' : 'Participate in this contest to win exciting prizes.');
    const isArabicDesc = containsArabic(rawDesc);

    if (isArabicDesc) {
      const descImg = renderArabicTextAsImage(rawDesc, contentW, 6, '#4B5563', isAr ? 'right' : 'left', 'normal', 3);
      if (descImg) {
        const imgX = isAr ? (contentX + contentW - descImg.widthMm) : contentX;
        doc.addImage(descImg.dataUrl, 'PNG', imgX, contentCursorY, descImg.widthMm, descImg.heightMm);
        contentCursorY += descImg.heightMm + 3;
      } else {
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(75, 85, 99);
        drawBilingualText(doc, rawDesc.slice(0, 70), isAr ? contentX + contentW : contentX, contentCursorY + 2.5, arabicFontLoaded, { align: isAr ? 'right' : 'left' });
        contentCursorY += 6;
      }
    } else {
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(75, 85, 99);
      const descLines = doc.splitTextToSize(rawDesc, contentW).slice(0, 3);
      doc.text(descLines, contentX, contentCursorY + 2.5);
      contentCursorY += descLines.length * 3 + 2;
    }

    // Mini feature badges (Levels & Prizes)
    const levelsCount = this.levels_of_contest?.length || 1;
    const badgeW = (contentW - 3) / 2;
    const badgeRowY = Math.min(contentCursorY + 1, screenY + screenHeight - 24);

    // Levels Pill
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(contentX, badgeRowY, badgeW, 6, 2, 2, 'F');
    doc.setTextColor(55, 65, 81);
    doc.setFontSize(5);
    doc.setFont('helvetica', 'bold');
    doc.text(`${levelsCount} ${isAr ? 'مستويات' : 'Levels'}`, contentX + badgeW / 2, badgeRowY + 4, { align: 'center' });

    // Prize Pill
    const prizeText = this._contest_prize?.[0]?.name ? `${this._contest_prize[0].name}` : (isAr ? 'جوائز كبرى' : 'Grand Prizes');
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(contentX + badgeW + 3, badgeRowY, badgeW, 6, 2, 2, 'F');
    if (containsArabic(prizeText)) {
      const pImg = renderArabicTextAsImage(prizeText, badgeW - 2, 5, '#374151', 'center', 'bold', 1);
      if (pImg) {
        doc.addImage(pImg.dataUrl, 'PNG', contentX + badgeW + 3 + (badgeW - pImg.widthMm) / 2, badgeRowY + (6 - pImg.heightMm) / 2, pImg.widthMm, pImg.heightMm);
      }
    } else {
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(5);
      doc.setFont('helvetica', 'bold');
      doc.text(prizeText.slice(0, 15), contentX + badgeW + 3 + badgeW / 2, badgeRowY + 4, { align: 'center' });
    }

    // --- Call to Action Button (Participate Now) ---
    const ctaW = screenWidth - 8;
    const ctaH = 8.5;
    const ctaX = screenX + 4;
    const ctaY = screenY + screenHeight - 15;

    doc.setFillColor(83, 4, 251);
    doc.roundedRect(ctaX, ctaY, ctaW, ctaH, 4, 4, 'F');

    const ctaText = isAr ? 'شارك الآن' : 'Participate Now';
    if (isAr || containsArabic(ctaText)) {
      const ctaImg = renderArabicTextAsImage(ctaText, ctaW - 4, 7.5, '#FFFFFF', 'center', 'bold', 1);
      if (ctaImg) {
        doc.addImage(ctaImg.dataUrl, 'PNG', ctaX + (ctaW - ctaImg.widthMm) / 2, ctaY + (ctaH - ctaImg.heightMm) / 2, ctaImg.widthMm, ctaImg.heightMm);
      } else {
        doc.setFontSize(7.5);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        drawBilingualText(doc, ctaText, ctaX + ctaW / 2, ctaY + 5.5, arabicFontLoaded, { align: 'center' });
      }
    } else {
      doc.setFontSize(7.5);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(ctaText, ctaX + ctaW / 2, ctaY + 5.5, { align: 'center' });
    }

    // --- Home Indicator Bar ---
    doc.setFillColor(209, 213, 219);
    doc.roundedRect(x + (frameWidth - 26) / 2, screenY + screenHeight - 3.5, 26, 1.2, 0.6, 0.6, 'F');
  }

  /**
   * Campaign card preview used by the configuration PDF.
   *
   * Unlike drawMobilePreview (a full phone-UI mockup), this renders the campaign
   * exactly as it appears in the mobile app's campaign list: one card whose banner
   * fills the whole surface, with the brand logo, status/prize/level pills and a
   * bottom gradient carrying the title, description and a View button.
   */
  private async drawCampaignCardPreview(doc: jsPDF, y: number, isAr: boolean = false, arabicFontLoaded: boolean = false): Promise<void> {
    const pageWidth = doc.internal.pageSize.width;
    const frameWidth = 84;
    const frameHeight = 80;
    const x = (pageWidth - frameWidth) / 2;

    // --- Phone chassis (dark bezel around the card) ---
    doc.setFillColor(10, 12, 18);
    doc.roundedRect(x, y, frameWidth, frameHeight, 7, 7, 'F');
    doc.setDrawColor(52, 58, 72);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, frameWidth, frameHeight, 7, 7, 'S');

    // --- Card surface ---
    const pad = 4;
    const cardX = x + pad;
    const cardY = y + pad;
    const cardW = frameWidth - pad * 2;
    const cardH = frameHeight - pad * 2;
    const radius = 5;

    // Banner fills the entire card; everything else sits on top of it.
    const bannerUrl = this.banners && this.banners.length > 0
      ? (this.banners[0].url || (this.banners[0] as any).path)
      : null;
    let bannerDrawn = false;
    if (bannerUrl) {
      try {
        const dataUrl = await this.sharedService.getImageDataUrl(bannerUrl);
        if (dataUrl) {
          doc.addImage(dataUrl, this.pdfImageFormat(dataUrl), cardX, cardY, cardW, cardH);
          bannerDrawn = true;
        }
      } catch {
        bannerDrawn = false;
      }
    }
    if (!bannerDrawn) {
      doc.setFillColor(60, 52, 137);
      doc.roundedRect(cardX, cardY, cardW, cardH, radius, radius, 'F');
    }

    // Opacity needs jsPDF's GState; fall back to opaque drawing if unavailable
    // so a missing extension can never abort the whole export.
    const setAlpha = (opacity: number): boolean => {
      try {
        doc.setGState(new (doc as any).GState({ opacity }));
        return true;
      } catch {
        return false;
      }
    };

    // --- Bottom scrim so the title/description stay legible over the photo ---
    // jsPDF has no gradients, so approximate one with stacked translucent bands.
    const scrimH = 26;
    const scrimTop = cardY + cardH - scrimH;
    const bands = 14;
    if (setAlpha(1)) {
      for (let i = 0; i < bands; i++) {
        const t = i / (bands - 1);
        doc.setFillColor(8, 10, 14);
        setAlpha(0.18 + t * 0.80);
        doc.rect(cardX, scrimTop + (scrimH / bands) * i, cardW, scrimH / bands + 0.35, 'F');
      }
      setAlpha(1);
    } else {
      // No alpha support: a solid dark band still keeps the text readable.
      doc.setFillColor(12, 14, 20);
      doc.rect(cardX, scrimTop + scrimH * 0.35, cardW, scrimH * 0.65, 'F');
    }

    // --- Brand logo badge (top-left, white circle) ---
    const logoR = 6;
    const logoCx = cardX + 4 + logoR;
    const logoCy = cardY + 4 + logoR;
    doc.setFillColor(255, 255, 255);
    doc.circle(logoCx, logoCy, logoR, 'F');
    try {
      const clientLogo = await this.sharedService.getClientLogoDataUrl();
      if (clientLogo) {
        const inner = logoR * 1.28;
        doc.addImage(clientLogo, this.pdfImageFormat(clientLogo), logoCx - inner / 2, logoCy - inner / 2, inner, inner);
      }
    } catch { }

    // --- Status / prize / level pills, floating above the scrim ---
    const timeLeft = (this.getTimeRemaining(this.end_date) || '').toString();
    const isExpired = /expired|ended|0 days 0 hours/i.test(timeLeft) || !timeLeft.trim();
    const statusText = isExpired
      ? (isAr ? 'منتهية' : 'Expired')
      : (isAr ? 'نشطة' : 'Active');

    const prizeCount = this._contest_prize?.length || 0;
    const levelCount = this.levels_of_contest?.length || 0;
    const prizeText = isAr
      ? `${prizeCount} ${prizeCount === 1 ? 'جائزة' : 'جوائز'}`
      : `${prizeCount} ${prizeCount === 1 ? 'Prize' : 'Prizes'}`;
    const levelText = isAr
      ? `${levelCount} ${levelCount === 1 ? 'مستوى' : 'مستويات'}`
      : `${levelCount} ${levelCount === 1 ? 'Level' : 'Levels'}`;

    const pills = [statusText, prizeText, levelText];
    const pillH = 7;
    const pillGap = 2.2;
    const pillY = scrimTop - pillH - 2.5;

    doc.setFontSize(5.6);
    doc.setFont('helvetica', 'bold');
    const pillWidths = pills.map(p => Math.max(16, doc.getTextWidth(p) + 9));
    const pillsTotal = pillWidths.reduce((a, b) => a + b, 0) + pillGap * (pills.length - 1);
    let pillX = cardX + Math.max(3, (cardW - pillsTotal) / 2);

    pills.forEach((label, i) => {
      const w = pillWidths[i];
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(pillX, pillY, w, pillH, pillH / 2, pillH / 2, 'F');

      // Small filled dot standing in for the app card's pill icon. An outlined
      // ring reads as an empty circle at this size, so fill it instead.
      doc.setFillColor(70, 78, 95);
      doc.circle(pillX + 4.2, pillY + pillH / 2, 1.5, 'F');

      if (containsArabic(label)) {
        const img = renderArabicTextAsImage(label, w - 9, 5.2, '#1F2937', 'center', 'bold', 1);
        if (img) {
          doc.addImage(img.dataUrl, 'PNG', pillX + 7 + ((w - 9) - img.widthMm) / 2, pillY + (pillH - img.heightMm) / 2, img.widthMm, img.heightMm);
        }
      } else {
        doc.setTextColor(31, 41, 55);
        doc.setFontSize(5.6);
        doc.setFont('helvetica', 'bold');
        doc.text(label, pillX + 7 + (w - 9) / 2, pillY + pillH / 2 + 1.1, { align: 'center' });
      }
      pillX += w + pillGap;
    });

    // --- Title, description and View button inside the scrim ---
    const viewW = 24;
    const viewH = 10;
    const textX = cardX + 5;
    const textW = cardW - viewW - 11;
    let textY = scrimTop + 7;

    const rawTitle = this.name || (isAr ? 'الحملة' : 'Campaign');
    if (containsArabic(rawTitle)) {
      const img = renderArabicTextAsImage(rawTitle, textW, 7, '#FFFFFF', isAr ? 'right' : 'left', 'bold', 1);
      if (img) {
        const imgX = isAr ? (textX + textW - img.widthMm) : textX;
        doc.addImage(img.dataUrl, 'PNG', imgX, textY - 4, img.widthMm, img.heightMm);
        textY += img.heightMm - 0.5;
      }
    } else {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      const line = doc.splitTextToSize(rawTitle, textW)[0] || rawTitle;
      doc.text(line, textX, textY);
      textY += 4.4;
    }

    const rawDesc = (this.description || '').replace(/<[^>]*>/g, '').trim()
      || (isAr ? 'شارك في هذه الحملة واربح جوائز قيمة.' : 'Participate in this campaign to win exciting prizes.');
    if (containsArabic(rawDesc)) {
      const img = renderArabicTextAsImage(rawDesc, textW, 5, '#E5E7EB', isAr ? 'right' : 'left', 'normal', 2);
      if (img) {
        const imgX = isAr ? (textX + textW - img.widthMm) : textX;
        doc.addImage(img.dataUrl, 'PNG', imgX, textY - 2.5, img.widthMm, img.heightMm);
      }
    } else {
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(226, 232, 240);
      const descLines = doc.splitTextToSize(rawDesc, textW).slice(0, 2);
      doc.text(descLines, textX, textY);
    }

    // View button (bottom-right, translucent white)
    const viewX = cardX + cardW - viewW - 4;
    const viewY = cardY + cardH - viewH - 4;
    setAlpha(0.22);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(viewX, viewY, viewW, viewH, viewH / 2, viewH / 2, 'F');
    setAlpha(1);
    // Outline keeps the button visible even when alpha is unsupported.
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    doc.roundedRect(viewX, viewY, viewW, viewH, viewH / 2, viewH / 2, 'S');

    const viewText = isAr ? 'عرض' : 'View';
    if (containsArabic(viewText)) {
      const img = renderArabicTextAsImage(viewText, viewW - 4, 6, '#FFFFFF', 'center', 'bold', 1);
      if (img) {
        doc.addImage(img.dataUrl, 'PNG', viewX + (viewW - img.widthMm) / 2, viewY + (viewH - img.heightMm) / 2, img.widthMm, img.heightMm);
      }
    } else {
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(viewText, viewX + viewW / 2, viewY + viewH / 2 + 1.3, { align: 'center' });
    }
  }

  // Contest configuration PDF: all creation-form fields, Luqta/client logos, a mobile-app
  // preview mockup, and submission metadata (submitted by/via/on, status, approved by).
  async exportConfigurationPDF(): Promise<void> {
    this.sharedService.showLoader();
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      let y = 0;

      const isAr = isArabicLang();
      const arabicFontLoaded = await ensureArabicFont(doc);

      const L = isAr ? {
        headerTitle: 'تفاصيل إعدادات المسابقة',
        untitled: 'مسابقة بدون عنوان',
        submissionDetailsHeading: 'تفاصيل التقديم',
        fieldCol: 'الحقل',
        valueCol: 'القيمة',
        submittedBy: 'مقدم بواسطة',
        submittedVia: 'تم التقديم عبر',
        submittedOn: 'تاريخ التقديم',
        status: 'الحالة',
        approvedBy: 'تمت الموافقة بواسطة',
        notYetApproved: 'لم تتم الموافقة بعد',
        na: 'غير متوفر',
        descriptionHeading: 'الوصف',
        configurationHeading: 'الإعدادات',
        type: 'النوع',
        format: 'الصيغة',
        sharingSetting: 'إعداد المشاركة',
        gender: 'الجنس',
        ageGroup: 'الفئة العمرية',
        location: 'الموقع',
        businessObjective: 'الهدف التجاري',
        numberOfWinners: 'عدد الفائزين',
        drawType: 'نوع السحب',
        pointsPoolPerContest: 'مجموع النقاط لكل مسابقة',
        noOfUsersSdk: 'عدد المستخدمين (SDK)',
        startDate: 'تاريخ البداية',
        endDate: 'تاريخ النهاية',
        drawDate: 'تاريخ السحب',
        prizesHeading: 'الجوائز',
        prizeNameCol: 'اسم الجائزة',
        levelsHeading: 'مستويات المشاركة',
        numCol: '#',
        levelNameCol: 'اسم المستوى',
        typeCol: 'النوع',
        pointsCol: 'النقاط',
        previewHeading: 'معاينة الحملة (تطبيق الجوال)',
        pageOf: (p: number, total: number) => `صفحة ${p} من ${total}`,
        generated: (date: string) => `تم الإنشاء: ${date}`,
      } : {
        headerTitle: 'Campaign Configuration Details',
        untitled: 'Untitled Campaign',
        submissionDetailsHeading: 'Submission Details',
        fieldCol: 'Field',
        valueCol: 'Value',
        submittedBy: 'Submitted By',
        submittedVia: 'Submitted Via',
        submittedOn: 'Submitted On',
        status: 'Status',
        approvedBy: 'Approved By',
        notYetApproved: 'Not yet approved',
        na: 'N/A',
        descriptionHeading: 'Description',
        configurationHeading: 'Configuration',
        type: 'Type',
        format: 'Format',
        sharingSetting: 'Sharing Setting',
        gender: 'Gender',
        ageGroup: 'Age Group',
        location: 'Location',
        businessObjective: 'Business Objective',
        numberOfWinners: 'Number of Winners',
        drawType: 'Draw Type',
        pointsPoolPerContest: 'Points Pool Per Campaign',
        noOfUsersSdk: 'No. of Users (SDK)',
        startDate: 'Start Date',
        endDate: 'End Date',
        drawDate: 'Draw Date',
        prizesHeading: 'Prizes',
        prizeNameCol: 'Prize Name',
        levelsHeading: 'Campaign Levels',
        numCol: '#',
        levelNameCol: 'Level Name',
        typeCol: 'Type',
        pointsCol: 'Points',
        previewHeading: 'Campaign Preview (Mobile App)',
        pageOf: (p: number, total: number) => `Page ${p} of ${total}`,
        generated: (date: string) => `Generated: ${date}`,
      };

      const luqtaLogo = await this.sharedService.getLuqtaLogoDataUrl();

      // Header bar with Luqta logo (left)
      doc.setFillColor(60, 52, 137);
      doc.rect(0, 0, pageWidth, 26, 'F');
      if (luqtaLogo) {
        try { doc.addImage(luqtaLogo, this.pdfImageFormat(luqtaLogo), 10, 4, 18, 18); } catch { }
      }
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, L.headerTitle, pageWidth / 2, 15, arabicFontLoaded, { align: 'center' });
      y = 36;

      const cd = this.contests_detail || {};

      doc.setTextColor(30, 30, 30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, this.name || L.untitled, 14, y, arabicFontLoaded);
      y += 8;

      // Submission details
      y = this.pdfSectionHeading(doc, L.submissionDetailsHeading, y, arabicFontLoaded);
      autoTable(doc, {
        startY: y,
        head: [[L.fieldCol, L.valueCol]],
        body: [
          [L.submittedBy, this.submitted_by?.name ? `${this.submitted_by.name}${this.submitted_by.email ? ' (' + this.submitted_by.email + ')' : ''}` : L.na],
          [L.submittedVia, this.submitted_via || L.na],
          [L.submittedOn, this.submitted_on || L.na],
          [L.status, this.getStatusLabel(cd.status)],
          [L.approvedBy, this.approved_by?.name ? `${this.approved_by.name}${this.approved_by.email ? ' (' + this.approved_by.email + ')' : ''}` : L.notYetApproved],
        ],
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      // Description
      y = this.pdfEnsureSpace(doc, y, 30);
      y = this.pdfSectionHeading(doc, L.descriptionHeading, y, arabicFontLoaded);
      const plainDesc = (this.description || '').replace(/<[^>]*>/g, '').trim() || L.na;
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      if (containsArabic(plainDesc) && arabicFontLoaded) {
        doc.setFont('Amiri', 'normal');
        const descLines = doc.splitTextToSize(reshapeArabic(plainDesc), pageWidth - 28);
        doc.text(descLines, pageWidth - 14, y, { align: 'right' });
        y += descLines.length * 5 + 8;
      } else {
        doc.setFont('helvetica', 'normal');
        const descLines = doc.splitTextToSize(plainDesc, pageWidth - 28);
        doc.text(descLines, 14, y);
        y += descLines.length * 5 + 8;
      }

      // Configuration fields (from the contest creation form)
      y = this.pdfEnsureSpace(doc, y, 80);
      y = this.pdfSectionHeading(doc, L.configurationHeading, y, arabicFontLoaded);
      const businessObjective = Array.isArray(cd.business_objective)
        ? cd.business_objective.join(', ')
        : (cd.business_objective || L.na);
      const configRows: string[][] = [
        [L.type, cd.type || L.na],
        [L.format, cd.format_of_contest || L.na],
        [L.sharingSetting, cd.sharingSetting || cd.sharing_setting || L.na],
        [L.gender, cd.gender || L.na],
        [L.ageGroup, cd.age || L.na],
        [L.location, cd.location || L.na],
        [L.businessObjective, businessObjective],
        [L.numberOfWinners, cd.number_of_winners != null ? String(cd.number_of_winners) : L.na],
        [L.drawType, this.getDrawTypeLabel()],
        [L.pointsPoolPerContest, cd.points_pool_per_contest != null ? String(cd.points_pool_per_contest) : L.na],
        [L.noOfUsersSdk, cd.no_of_users != null ? String(cd.no_of_users) : L.na],
        [L.startDate, this.start_date || L.na],
        [L.endDate, this.end_date || L.na],
        [L.drawDate, this.draw_date || L.na],
      ];
      autoTable(doc, {
        startY: y,
        head: [[L.fieldCol, L.valueCol]],
        body: configRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      // Prizes
      if (this._contest_prize && this._contest_prize.length > 0) {
        y = this.pdfEnsureSpace(doc, y, 30);
        y = this.pdfSectionHeading(doc, L.prizesHeading, y, arabicFontLoaded);
        const prizeRows = this._contest_prize.map((prize: any) => [
          prize?.name || L.na,
          prize?.value != null ? `${prize?.currency || 'USD'} ${prize.value}` : L.na,
        ]);
        autoTable(doc, {
          startY: y,
          head: [[L.prizeNameCol, L.valueCol]],
          body: prizeRows,
          theme: 'striped',
          headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          margin: { left: 14, right: 14 },
          didParseCell: arabicAwareCellHook(arabicFontLoaded),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // Levels
      if (this.levels_of_contest && this.levels_of_contest.length > 0) {
        y = this.pdfEnsureSpace(doc, y, 30);
        y = this.pdfSectionHeading(doc, L.levelsHeading, y, arabicFontLoaded);
        const levelRows = this.levels_of_contest.map((lvl: any, i: number) => [
          String(i + 1),
          lvl.name || lvl.level_name || `Level ${i + 1}`,
          lvl.level_type || lvl.type || L.na,
          lvl.points != null ? String(lvl.points) : L.na,
        ]);
        autoTable(doc, {
          startY: y,
          head: [[L.numCol, L.levelNameCol, L.typeCol, L.pointsCol]],
          body: levelRows,
          theme: 'striped',
          headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          margin: { left: 14, right: 14 },
          didParseCell: arabicAwareCellHook(arabicFontLoaded),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // Mobile app preview: the campaign card as it appears in the app's list.
      y = this.pdfEnsureSpace(doc, y, 90);
      y = this.pdfSectionHeading(doc, L.previewHeading, y, arabicFontLoaded);
      await this.drawCampaignCardPreview(doc, y, isAr, arabicFontLoaded);

      // Footer
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        drawBilingualText(doc, L.pageOf(p, totalPages), pageWidth / 2, doc.internal.pageSize.height - 6, arabicFontLoaded, { align: 'center' });
        drawBilingualText(doc, L.generated(new Date().toLocaleString()), 14, doc.internal.pageSize.height - 6, arabicFontLoaded);
      }

      const sanitizedName = (this.name || 'campaign').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
      doc.save(`${sanitizedName}_configuration.pdf`);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to export configuration PDF.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  ngOnInit(): void {
    // Setup debounced search for participants
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applyClientFilters();
    });

    const encodedUid = this.route.snapshot.queryParamMap.get('id');

    if (encodedUid) {
      const storedId = localStorage.getItem(encodedUid);

      if (storedId) {
        this.hasWebAccess = this.secureStorage.getItem<boolean>('hasWebAccess') || false;
        this.contestID = Number(storedId);
        this.getContestPerformance(this.contestID);
        this.getContestDetails(this.contestID);
        this.getLevelCompletionMetrics(this.contestID);
        this.checkQrInstances(this.contestID);
      } else {
        this.sharedService.createNotification('error', this.translateService.instant('contestDetails.invalidOrExpiredLink'));
        this.router.navigate(['/home/contests']);
      }
    } else {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.noContestIdInUrl'));
      this.router.navigate(['/home/contests']);
    }
  }

  async getLevelCompletionMetrics(contestId: number): Promise<void> {
    this.levelMetricsLoading = true;
    this.levelMetricsError = '';
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll(
          this.isWebQrMode ? 'CONTEST_LEVEL_COMPLETION_METRICS' : 'CONTEST_LEVEL_METRICS_SIMPLE',
          { contest_id: contestId, qr_instance_id: this.qrInstanceParam },
        )
      );
      const payload = response?.data ?? response;
      if (payload && Array.isArray(payload.levels)) {
        payload.levels = payload.levels.map((lvl: any) => {
          const completed = Number(lvl.completed_count ?? lvl.completed ?? 0);
          const inProgress = Number(lvl.in_progress_count ?? lvl.in_progress ?? 0);
          const notStarted = Number(lvl.not_started_count ?? lvl.not_started ?? 0);
          const total = completed + inProgress + notStarted;
          let completionRate = Number(lvl.completion_rate ?? lvl.completion_rate_pct ?? lvl.completionRate ?? 0);
          if (completionRate === 0 && total > 0 && completed > 0) {
            completionRate = Math.round((completed / total) * 100 * 10) / 10;
          }
          return {
            ...lvl,
            completed_count: completed,
            in_progress_count: inProgress,
            not_started_count: notStarted,
            completion_rate: completionRate
          };
        });
        this.levelMatrix = payload;
      } else {
        this.levelMatrix = null;
      }
    } catch (error: any) {
      this.levelMetricsError = 'Level metrics unavailable.';
      this.levelMatrix = null;
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch level completion metrics:', error);
      }
    } finally {
      this.levelMetricsLoading = false;
    }
  }

  // Opens the demographic breakdown drawer for a single level row. The level
  // object already carries by_gender/by_country/by_age_group/by_device_type
  // from the contest_level_completion_metrics response, so no extra fetch.
  openLevelDemographicsDrawer(level: any): void {
    this.selectedLevelMetrics = level;
    this.levelDemographicsDrawerVisible = true;
  }

  closeLevelDemographicsDrawer(): void {
    this.levelDemographicsDrawerVisible = false;
    this.selectedLevelMetrics = null;
  }

  // "unknown" (missing profile field) is rendered as its own labeled bucket
  // rather than dropped, per each dimension's breakdown array.
  getBreakdownLabel(value: any): string {
    if (value === null || value === undefined) return 'Unknown';
    const normalized = String(value).trim();
    if (!normalized || normalized.toLowerCase() === 'unknown') return 'Unknown';
    return normalized;
  }

  getDemographicSections(level: any): Array<{ key: string; title: string; dimensionLabel: string; icon: string; rows: any[]; totalCount: number }> {
    if (!level) return [];

    // Rows are sorted highest-total-first so a later top-N slice (e.g. country) keeps the most relevant groups.
    const buildRows = (entries: any[], dimensionKey: string) =>
      (Array.isArray(entries) ? entries : []).map((entry: any) => {
        const notStarted = entry?.not_started ?? 0;
        const inProgress = entry?.in_progress ?? 0;
        const completed = entry?.completed ?? 0;
        return {
          label: this.getBreakdownLabel(entry?.[dimensionKey]),
          not_started: notStarted,
          in_progress: inProgress,
          completed: completed,
          total: notStarted + inProgress + completed
        };
      }).sort((a, b) => b.total - a.total);

    const countryRows = buildRows(level.by_country, 'country');
    const deviceTypeRows = buildRows(level.by_device_type, 'device_type');

    const sections = [
      // Country lists can grow large, so only the top 5 by participant count are shown.
      { key: 'country', title: this.translateService.instant('contestDetails.byCountryTop5'), dimensionLabel: this.translateService.instant('contestDetails.country'), icon: 'fa-earth-americas', rows: countryRows.slice(0, 5), totalCount: countryRows.length },
      { key: 'device_type', title: this.translateService.instant('contestDetails.byDeviceType'), dimensionLabel: this.translateService.instant('contestDetails.deviceType'), icon: 'fa-mobile-screen-button', rows: deviceTypeRows, totalCount: deviceTypeRows.length },
    ];

    // Web QR collects neither gender nor age group, so those two breakdowns are
    // dropped there. The plain campaign report keeps them.
    if (this.isWebQrMode) {
      return sections;
    }

    const genderRows = buildRows(level.by_gender, 'gender');
    const ageGroupRows = buildRows(level.by_age_group, 'age_group');

    return [
      { key: 'gender', title: this.translateService.instant('contestDetails.byGender'), dimensionLabel: this.translateService.instant('contestDetails.gender'), icon: 'fa-venus-mars', rows: genderRows, totalCount: genderRows.length },
      { key: 'age_group', title: this.translateService.instant('contestDetails.byAgeGroup'), dimensionLabel: this.translateService.instant('contestDetails.ageGroup'), icon: 'fa-users', rows: ageGroupRows, totalCount: ageGroupRows.length },
      ...sections,
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editContest() {
    if (!this.isContestEditable()) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.cannotEditApprovedCompleted'));
      return;
    }

    const id = this.contestID;
    const uid = uuidv4(); // Generate a unique UID for this contest

    // Store the mapping temporarily (UID → actual numeric ID)
    localStorage.setItem(uid, id.toString());

    // Navigate using the UID instead of the real numeric ID
    this.router.navigate(['/home/contests/edit'], { queryParams: { id: uid } });
  }

  duplicateContest() {
    const id = this.contestID || this.contests_detail?.id;
    if (!id) return;
    const uid = uuidv4();
    localStorage.setItem(uid, id.toString());
    this.router.navigate(['/home/contests/add'], { queryParams: { duplicateId: uid } });
  }

  isContestEditable(): boolean {
    if (!this.contests_detail || !this.contests_detail.status) {
      return true; // Allow edit if status is not available
    }

    const status = this.contests_detail.status.toLowerCase();
    return status !== 'approved' && status !== 'complete' && status !== 'completed';
  }

  viewContestAnalytics() {
    this.router.navigate(['/home/analytic/monitoring'], {
      queryParams: { contest_id: this.contestID },
    });
  }
  async getContestDetails(id: string) {
    this.sharedService.showLoader();
    try {
      const response: any = await firstValueFrom(this.pagesService.getById('GET_SINGLE_CONTEST', id));
      if (response) {
        const contestData = response;
        this.contests_detail = contestData;
        this.name = contestData.name;
        this.arabic_name = contestData.arabic_name || '';
        this.created_at = contestData.created_at;
        this.description = contestData.details;
        this.arabic_description = contestData.arabic_details || '';

        this.submitted_by = contestData.submitted_by ?? null;
        this.submitted_via = contestData.submitted_via ?? null;
        // submitted_on has no dedicated backend field yet — falls back to created_at.
        this.submitted_on = this.formatDate(contestData.submitted_on ?? contestData.created_at);
        this.approved_by = contestData.approved_by ?? null;

        // Sort levels by ID (order by id ascending)
        const normalizedLevels = contestData._levels_of_contest
          .map((lvl: any) => this.normalizeLevelForDisplay(lvl));
        this.levels_of_contest = [...normalizedLevels].sort((a: any, b: any) => {
          const idA = a?.id ?? a?.level_id;
          const idB = b?.id ?? b?.level_id;
          if (idA !== undefined && idA !== null && idB !== undefined && idB !== null) {
            return Number(idA) - Number(idB);
          }
          const ordA = a?.order ?? a?.level_order;
          const ordB = b?.order ?? b?.level_order;
          if (ordA !== undefined && ordA !== null && ordB !== undefined && ordB !== null) {
            return Number(ordA) - Number(ordB);
          }
          return 0;
        });

        this.loadBranchesIfNeeded();

        this.banners = contestData.banners;
        this.start_date = this.formatDate(contestData.start_date);
        this.end_date = this.formatDate(contestData.end_date);
        this.draw_date = this.resolveDrawDate(contestData);
        this._contest_prize = contestData._contest_prize;

        this.levels_of_contest.forEach(level => {
          this.showAllVariables[level.id] = false;
        });

        // Pre-load quizzes if any level has quiz type
        const hasQuizLevel = this.levels_of_contest.some((level: any) =>
          (level.level_type || level.type) === 'quiz'
        );
        if (hasQuizLevel && !this.quizzesLoaded) {
          this.getQuizzesRecords();
        }

        // Fetch participants data and analytics for this contest
        await this.getContestParticipants(id);
        await this.getContestAnalytics(id);
        await this.buildLeaderboard();

        this.applyPerformanceFallbacks();
      }
    } catch (error) {
      this.sharedService.showError(error, 'Failed to fetch campaign details.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  /**
   * The campaign's participants.
   *
   * On Web QR the table reads qr_participants, which is the only source that
   * says which QR instance someone joined through and which level they are on.
   *
   * The plain list is still fetched alongside it, because winners are matched
   * against it: the draw records a user id, while qr_participants keys on the
   * participation and returns `end_user: null`, so matching there would find
   * nobody and empty the Winners table.
   */
  async getContestParticipants(contestId: string) {
    this.participantsLoading = true;
    this.winnersLoading = true;
    try {
      const plainRequest = firstValueFrom(
        this.pagesService.getByCustomName('GET_Total_PARTICIPANT', 'contest_id', contestId)
      );
      const qrRequest = this.isWebQrMode
        ? firstValueFrom(
            this.pagesService.getAll('QR_PARTICIPANTS', {
              contest_id: Number(contestId),
              qr_instance_id: this.qrInstanceParam,
            })
          ).catch(() => null)
        : Promise.resolve(null);

      const [response, qrResponse] = await Promise.all([plainRequest, qrRequest]);

      const readItems = (payload: any): any[] =>
        Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];

      const plainItems = readItems(response);
      // A failed or empty QR response falls back to the plain list rather than
      // blanking the table.
      const qrItems = readItems(qrResponse);
      const tableItems = this.isWebQrMode && qrItems.length > 0 ? qrItems : plainItems;

      this.winnerSourceList = plainItems.map((participant: any) =>
        this.normalizeContestParticipant(participant)
      );

      const items = tableItems;

      if (items.length > 0) {
        const normalizedParticipants = items.map((participant: any) =>
          this.normalizeContestParticipant(participant)
        );

        const sortedParticipants = [...normalizedParticipants].sort(
          (a: any, b: any) =>
            this.getParticipationDateTimestamp(b) - this.getParticipationDateTimestamp(a)
        );

        this.participantsList = sortedParticipants;
        this.filteredParticipants = sortedParticipants;

        // Extract winners from participants
        this.extractWinners();
        this.applyPerformanceFallbacks();
      } else {
        // If response is not an array, set empty arrays
        this.participantsList = [];
        this.filteredParticipants = [];
        this.winnersList = [];
        this.applyPerformanceFallbacks();
      }
    } catch (error: any) {
      // Handle error silently - just set empty arrays
      this.participantsList = [];
      this.filteredParticipants = [];
      this.winnersList = [];
      this.applyPerformanceFallbacks();

      // Don't show error notification for ERROR_CODE_NOT_FOUND
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch participants:', error);
      }
    } finally {
      this.participantsLoading = false;
      this.winnersLoading = false;
    }
  }

  async getContestAnalytics(contestId: string) {
    this.analyticsLoading = true;
    try {
      // Web QR reads the QR report; the plain page reads the campaign report it
      // always used. Web QR keeps the old fallback, since qr_report_detail is
      // the newer endpoint and can still be missing for a campaign.
      let response: any;
      if (this.isWebQrMode) {
        try {
          response = await firstValueFrom(
            this.pagesService.getAll('QR_REPORT_DETAIL', { contest_id: Number(contestId), qr_instance_id: this.qrInstanceParam })
          );
        } catch {
          response = await firstValueFrom(
            this.pagesService.getByIdByQuerry('ANALYSIS_URL_Reports_DETAIL', contestId)
          );
        }
      } else {
        response = await firstValueFrom(
          this.pagesService.getByIdByQuerry('ANALYSIS_URL_Reports_DETAIL', contestId)
        );
      }

      if (response && typeof response === 'object') {
        this.surveyReportDetail = response;
        const apiQuestions = response?.survey_questions || response?.data?.survey_questions;
        if (Array.isArray(apiQuestions) && apiQuestions.length > 0) {
          const normalizedApiQs = this.normalizeSurveyQuestions(apiQuestions);
          this.levels_of_contest = (this.levels_of_contest || []).map((lvl: any) => {
            if ((lvl.level_type || lvl.type) === 'survey') {
              const matchedQs = normalizedApiQs.filter((q: any) => q.levels_id === lvl.id);
              const qsToUse = matchedQs.length > 0 ? matchedQs : normalizedApiQs;
              const existingQs: any[] = Array.isArray(lvl.survey_questions) ? lvl.survey_questions : [];

              let finalQs: any[];
              if (existingQs.length === 0) {
                finalQs = qsToUse;
              } else {
                finalQs = existingQs.map((eq: any) => {
                  const apiQ = qsToUse.find((aq: any) => aq.id === eq.id || String(aq.id) === String(eq.id));
                  if (!apiQ) return eq;
                  const eqOpts = (Array.isArray(eq.options) && eq.options.length > 0) ? eq.options : (eq.answer_options || []);
                  const apiOpts = (Array.isArray(apiQ.options) && apiQ.options.length > 0) ? apiQ.options : (apiQ.answer_options || []);
                  const bestOpts = eqOpts.length >= apiOpts.length ? eqOpts : apiOpts;
                  return {
                    ...apiQ,
                    ...eq,
                    options: bestOpts,
                    answer_options: bestOpts,
                    question_text: (eq.question_text && eq.question_text !== '-') ? eq.question_text : apiQ.question_text,
                    arabic_question_text: (eq.arabic_question_text && eq.arabic_question_text !== '-') ? eq.arabic_question_text : apiQ.arabic_question_text,
                  };
                });
              }

              return {
                ...lvl,
                survey_questions: finalQs,
                min_answers_required: response.minimum_answers ?? lvl.min_answers_required,
                question_order: response.question_order ?? lvl.question_order,
              };
            }
            return lvl;
          });
        }
      }

      const dataArray = Array.isArray(response) ? response : (response?.data || response?.items || response?.result || []);
      this.analyticsActivity = Array.isArray(dataArray) ? dataArray : [];

      // qr_report_detail answers an object with totals/instances rather than a
      // list, so it is kept whole and read from directly.
      //
      // `totals` has to be there: the catch above falls back to the campaign
      // report, which is also a non-array object but carries no QR totals.
      // Storing that as qrReport left the cards on zero — applyQrReportTotals()
      // found nothing to apply, and updatePerformanceOverview() then stood
      // aside for a report that was never a QR report.
      if (this.isWebQrMode && this.hasQrReportTotals(response)) {
        this.qrReport = response;
        // Runs last regardless of which of the two overview calls resolves
        // first: getContestPerformance() starts alongside this one on load, and
        // its campaign-wide totals must not be what the cards end up showing.
        this.applyQrReportTotals();
      } else {
        // No usable QR totals — let the campaign overview drive the cards
        // rather than leaving them empty.
        this.qrReport = null;
      }
    } catch (error: any) {
      this.analyticsActivity = [];
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch contest analytics:', error);
      }
    } finally {
      this.analyticsLoading = false;
      this.analyticsLoaded = true;
      this.applyPerformanceFallbacks();
    }
  }

  private normalizeContestParticipant(participant: any): any {
    if (!participant || typeof participant !== 'object') {
      return participant;
    }

    const normalized = { ...participant };

    const contestCandidate =
      participant?.contest ||
      participant?._contest ||
      participant?.contest_detail ||
      participant?.contestDetail ||
      participant?.contest_details ||
      participant?.contestDetails ||
      this.contests_detail ||
      null;

    if (contestCandidate && !participant?.contest) {
      normalized.contest = contestCandidate;
    }

    const endUserCandidate =
      participant?._end_user ||
      participant?.end_user ||
      participant?.endUser ||
      participant?.user ||
      participant?.participant ||
      null;

    if (endUserCandidate && !participant?._end_user) {
      normalized._end_user = endUserCandidate;
    }

    const statusCandidate =
      participant?.status ||
      participant?.participant_status ||
      participant?.contestParticipant?.status ||
      participant?.contest_participant?.[0]?.status ||
      participant?._contest_participant?.[0]?.status ||
      participant?.contest_participants?.[0]?.status ||
      null;

    if (statusCandidate && !participant?.status) {
      normalized.status = statusCandidate;
    }

    if (statusCandidate && !participant?.participant_status) {
      normalized.participant_status = statusCandidate;
    }

    if (!participant?.levels_detail && participant?.levelsDetail) {
      normalized.levels_detail = participant.levelsDetail;
    }

    if (!participant?.levelsDetail && participant?.levels_detail) {
      normalized.levelsDetail = participant.levels_detail;
    }

    return normalized;
  }

  private getParticipationDateTimestamp(participant: any): number {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    if (!participationDate) {
      return 0;
    }

    const timestamp = new Date(participationDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  getParticipationDate(participant: any): string | null {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    return participationDate || null;
  }

  getParticipantName(participant: any): string {
    return (
      participant?._end_user?.name ||
      participant?.end_user?.name ||
      participant?.participant_name ||
      participant?.userName ||
      participant?.user_name ||
      participant?.name ||
      participant?.end_user?.full_name ||
      participant?._end_user?.full_name ||
      participant?.end_user?.phone_number ||
      participant?.phone ||
      participant?.email ||
      (participant?.id ? `Participant #${participant.id}` : 'User')
    );
  }

  getParticipantGender(participant: any): string {
    return (
      participant?._end_user?.gender ||
      participant?.end_user?.gender ||
      participant?.participant_gender ||
      participant?.participant_gender ||
      participant?.gender ||
      'N/A'
    );
  }

  /**
   * Which QR instance the participant joined through (Web QR only).
   *
   * The API has not settled on one field name for this, so the first one that
   * answers wins — same approach as the other participant getters here.
   */
  getParticipantQrInstance(participant: any): string {
    const instance = participant?.qr_instance || participant?.qrInstance || {};
    return (
      instance.label ||
      instance.name ||
      instance.slug ||
      participant?.qr_instance_name ||
      participant?.qrInstanceName ||
      participant?.instance_name ||
      'N/A'
    );
  }

  /**
   * The level the participant is currently on (Web QR only).
   *
   * Prefers an explicit field from the API; otherwise derives it from the
   * levels breakdown — the first level not yet completed is where they are.
   */
  getParticipantCurrentLevel(participant: any): string {
    // qr_participants sends level: { name, position, total_levels, status }.
    // The position is worth showing — a level name alone does not say how far
    // through the engagement they are.
    const level = participant?.level || participant?.current_level || {};
    const name =
      level?.name ||
      level?.level_name ||
      participant?.current_level_name ||
      participant?.currentLevelName ||
      participant?.level_name;

    if (name) {
      const position = Number(level?.position ?? 0);
      const total = Number(level?.total_levels ?? participant?.total_levels ?? 0);
      return position > 0 && total > 0 ? `${name} (${position}/${total})` : String(name);
    }

    // No level object: fall back to the levels breakdown, where the first
    // level not yet completed is where they are.
    const levels = participant?.levels_detail || participant?.levelsDetail;
    if (!Array.isArray(levels) || levels.length === 0) return 'N/A';

    const pending = levels.find(
      (lvl: any) => String(lvl?.status || '').trim().toLowerCase() !== 'completed'
    );
    // Every level done means they finished rather than sitting on a level.
    if (!pending) return this.translateService.instant('contestDetails.completed');
    return String(pending?.name || pending?.level_name || 'N/A');
  }

  getParticipantLocation(participant: any): string {
    return (
      participant?._end_user?.country ||
      participant?.end_user?.country ||
      participant?.participant_country ||
      participant?.country ||
      'N/A'
    );
  }

  getParticipantAge(participant: any): string | number {
    const directAge =
      participant?._end_user?.age ??
      participant?.end_user?.age ??
      participant?.participant_age ??
      participant?.age;

    if (directAge !== null && directAge !== undefined && directAge !== '') {
      return directAge;
    }

    const dob =
      participant?._end_user?.date_of_birth ||
      participant?._end_user?.dob ||
      participant?.end_user?.date_of_birth ||
      participant?.end_user?.dob ||
      participant?.participant_dob ||
      participant?.date_of_birth ||
      participant?.dob;

    if (!dob) {
      return 'N/A';
    }

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) {
      return 'N/A';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : 'N/A';
  }

  getWinnerCompletionTimestamp(winner: any): string | null {
    const completionTimestamp =
      winner?.completed_at ||
      winner?.completion_date ||
      winner?.completion_timestamp ||
      winner?.updated_at ||
      winner?.modified_at ||
      null;

    return completionTimestamp || null;
  }

  getWinnerPrizeStatus(winner: any): string {
    const participantId = winner?.participant_id ?? winner?._end_user?.id;
    const nestedPrizeHistoryStatus =
      winner?.contest?.prizes?.[0]?.prize_history?.find(
        (history: any) => String(history?.end_user_id) === String(participantId)
      )?.status;

    return (
      winner?.prize_status ||
      winner?.prizeStatus ||
      nestedPrizeHistoryStatus ||
      (winner?.is_winner ? 'Winner' : 'N/A')
    );
  }

  // Display label for the prize status badge — translates only the client-added 'Winner' fallback,
  // leaving raw backend status values (pending, shipped, etc.) untouched so getWinnerPrizeStatusClass matching still works.
  getWinnerPrizeStatusDisplay(winner: any): string {
    const status = this.getWinnerPrizeStatus(winner);
    return status === 'Winner' ? this.translateService.instant('contestDetails.won') : status;
  }

  getWinnerPrizeStatusClass(winner: any): string {
    const rawStatus = (this.getWinnerPrizeStatus(winner) || '').toLowerCase().trim();
    const status = rawStatus.replace(/_/g, ' ');

    if (status === 'pending') return 'prize-status pending';
    if (status === 'processing') return 'prize-status processing';
    if (status === 'ready for dispatch') return 'prize-status ready-dispatch';
    if (status === 'shipped') return 'prize-status shipped';
    if (status === 'in transit') return 'prize-status in-transit';
    if (status === 'out for delivery') return 'prize-status out-delivery';
    if (status === 'delivered' || status === 'completed' || status === 'winner') return 'prize-status delivered';
    if (status === 'returned') return 'prize-status returned';
    if (status === 'rejected' || status === 'cancelled' || status === 'failed') return 'prize-status failed';

    return 'prize-status default';
  }

  extractWinners() {
    this.winnersList = [];

    // Winner source can come from single contest payload OR nested inside participant.contest.
    const contestDraws = Array.isArray(this.contests_detail?.contest_draw)
      ? this.contests_detail.contest_draw
      : Array.isArray(this.contests_detail?._contest_draw)
        ? this.contests_detail._contest_draw
        : Array.isArray(this.participantsList?.[0]?.contest?.contest_draw)
          ? this.participantsList[0].contest.contest_draw
          : [];

    const winnerIds = new Set<string>();
    if (Array.isArray(contestDraws)) {
      contestDraws.forEach((draw: any) => {
        const rawWinners = draw?.winner_of_contest_draw || draw?.winners || draw?.winner_list || [];
        if (Array.isArray(rawWinners)) {
          rawWinners.forEach((winner: any) => {
            const wId = winner?.winner_id ?? winner?.participant_id ?? winner?.id ?? winner?.user_id;
            if (wId !== null && wId !== undefined) {
              winnerIds.add(String(wId));
            }
          });
        }
      });
    }

    // Also check direct winners array on contest payload
    const directContestWinners = this.contests_detail?.winners || this.contests_detail?.winner_list || this.contests_detail?._winners;
    if (Array.isArray(directContestWinners)) {
      directContestWinners.forEach((w: any) => {
        const wId = w?.winner_id ?? w?.participant_id ?? w?.id ?? w?.user_id;
        if (wId !== null && wId !== undefined) {
          winnerIds.add(String(wId));
        }
      });
    }

    // Matched against the plain list: a draw records a user id, which
    // qr_participants does not carry.
    const winnerCandidates = this.winnerSourceList.length > 0
      ? this.winnerSourceList
      : this.participantsList;

    if (winnerIds.size > 0 && winnerCandidates.length > 0) {
      this.winnersList = winnerCandidates.filter((participant: any) => {
        const participantId =
          participant?.participant_id ??
          participant?.user_id ??
          participant?.end_user?.id ??
          participant?._end_user?.id ??
          participant?.id;

        return participantId !== null &&
          participantId !== undefined &&
          winnerIds.has(String(participantId));
      });
    }

    if (this.winnersList.length === 0 && winnerCandidates.length > 0) {
      this.winnersList = winnerCandidates.filter((p: any) =>
        p.is_winner || p.isWinner || p.winner || String(p.status).toLowerCase() === 'winner' || String(p.contestStatus).toLowerCase() === 'winner'
      );
    }

    // If direct winners array contains populated user records not matched in participantsList, map them
    if (this.winnersList.length === 0 && Array.isArray(directContestWinners) && directContestWinners.length > 0) {
      this.winnersList = directContestWinners.map((w: any) => this.normalizeContestParticipant(w));
    }

    this.winnersLoading = false;
  }

  async buildLeaderboard(page: number = 1): Promise<void> {
    if (this.contests_detail?.draw_type !== 'top_points') return;
    this.leaderboardLoading = true;
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll('CONTEST_LEADERBOARD', {
          contest_id: this.contestID,
          page: page - 1,
          per_page: this.leaderboardPerPage
        })
      );
      const items = Array.isArray(response) ? response : (response?.items || response?.data || []);
      this.leaderboardTotal = response?.total ?? response?.itemsReceived ?? items.length;
      this.leaderboardPage = page;
      this.leaderboard = items.map((p: any, i: number) => ({
        ...p,
        _rank: p?.rank ?? ((page - 1) * this.leaderboardPerPage + i + 1),
        _points: Number(p?.points ?? p?.total_points ?? p?.score ?? 0),
        _name: p?.name ?? p?.participant_name ?? this.getParticipantName(p),
        _gender: p?.gender ?? this.getParticipantGender(p)
      }));
    } catch {
      this.leaderboard = [];
    } finally {
      this.leaderboardLoading = false;
    }
  }

  getDrawTypeLabel(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: this.translateService.instant('contestDetails.drawTypeTopPoints'),
      first_to_complete: this.translateService.instant('contestDetails.drawTypeFirstToComplete'),
      random: this.translateService.instant('contestDetails.drawTypeRandom'),
      manual: this.translateService.instant('contestDetails.drawTypeManual'),
    };
    return map[dt] || dt;
  }

  getTieBreakingRule(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: this.translateService.instant('contestDetails.tieBreakTopPoints'),
      first_to_complete: this.translateService.instant('contestDetails.tieBreakFirstToComplete'),
      random: this.translateService.instant('contestDetails.tieBreakRandom'),
      manual: this.translateService.instant('contestDetails.tieBreakManual'),
    };
    return map[dt] || '';
  }

  // Frontend-only label mapping — raw contests_detail.status is unchanged.
  getStatusLabel(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'pending' || normalized === 'luqta_pending') {
      return this.translateService.instant('contestDetails.statusPendingApproval');
    }
    const map: Record<string, string> = {
      approved: this.translateService.instant('contestDetails.statusApproved'),
      rejected: this.translateService.instant('contestDetails.statusRejected'),
      draft: this.translateService.instant('contestDetails.statusDraft'),
      completed: this.translateService.instant('contestDetails.statusCompleted'),
    };
    return map[normalized] || (status || 'N/A');
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'pending' || normalized === 'luqta_pending') return 'submission-status pending';
    if (['approved', 'rejected', 'draft', 'completed'].includes(normalized)) {
      return `submission-status ${normalized}`;
    }
    return 'submission-status default';
  }

  openOverrideModal(): void {
    this.overrideModalVisible = true;
    this.overrideParticipantId = null;
    this.overrideReason = '';
    this.loadAuditLog();
  }

  closeOverrideModal(): void {
    this.overrideModalVisible = false;
  }

  async loadAuditLog(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('WINNER_OVERRIDE_AUDIT_LOG', 'contest_id', this.contestID)
      );
      this.overrideAuditLog = Array.isArray(response) ? response : (response?.data || response?.items || []);
    } catch {
      this.overrideAuditLog = [];
    }
  }

  async submitOverride(): Promise<void> {
    if (!this.overrideParticipantId || !this.overrideReason.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.selectParticipantAndReason'));
      return;
    }
    this.overrideSubmitting = true;
    try {
      await firstValueFrom(
        this.pagesService.update('WINNER_OVERRIDE', {
          contest_id: this.contestID,
          participant_id: this.overrideParticipantId,
          reason: this.overrideReason.trim()
        })
      );
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.winnerOverriddenSuccess'));
      this.overrideModalVisible = false;
      await this.getContestParticipants(this.contestID);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to override winner.');
    } finally {
      this.overrideSubmitting = false;
    }
  }

  private coerceNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') {
      if (value === null) return null;
      if (Object.prototype.hasOwnProperty.call(value, 'value')) {
        return this.coerceNumber(value.value);
      }
      if (Object.prototype.hasOwnProperty.call(value, 'count')) {
        return this.coerceNumber(value.count);
      }
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/[%\,]/g, '').trim();
      if (cleaned === '') return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private extractNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const parsed = this.coerceNumber(payload[key]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }
    const payloadKeys = Object.keys(payload);
    for (const targetKey of keys) {
      const lowerTarget = targetKey.toLowerCase();
      const matchedKey = payloadKeys.find(k => k.toLowerCase() === lowerTarget);
      if (matchedKey) {
        const parsed = this.coerceNumber(payload[matchedKey]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }
    return null;
  }

  private extractString(payload: any, keys: string[]): string | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key];
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized !== '') {
          return normalized;
        }
      }
    }
    const payloadKeys = Object.keys(payload);
    for (const targetKey of keys) {
      const lowerTarget = targetKey.toLowerCase();
      const matchedKey = payloadKeys.find(k => k.toLowerCase() === lowerTarget);
      if (matchedKey) {
        const value = payload[matchedKey];
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized !== '') {
          return normalized;
        }
      }
    }
    return null;
  }

  private extractTrendNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    const payloadKeys = Object.keys(payload);
    for (const key of keys) {
      let raw: any;
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        raw = payload[key];
      } else {
        const lowerKey = key.toLowerCase();
        const matchedKey = payloadKeys.find(k => k.toLowerCase() === lowerKey);
        if (matchedKey) {
          raw = payload[matchedKey];
        } else {
          continue;
        }
      }
      if (raw && typeof raw === 'object') {
        if (Object.prototype.hasOwnProperty.call(raw, 'trend')) {
          const parsedTrend = this.coerceNumber((raw as any).trend);
          if (parsedTrend !== null) {
            return parsedTrend;
          }
        }
        continue;
      }
      const parsed = this.coerceNumber(raw);
      if (parsed !== null) {
        return parsed;
      }
    }
    return null;
  }

  private normalizeRate(value: number | null): number | null {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return null;
    }
    const abs = Math.abs(value);
    if (abs > 100) {
      return value / 100;
    }
    if (abs > 0 && abs <= 1) {
      return value * 100;
    }
    return value;
  }

  private unwrapPerformanceResponse(response: any): any {
    if (!response) return null;
    const candidate = response?.data ?? response?.result ?? response?.body ?? response;
    if (Array.isArray(candidate)) {
      return candidate[0] ?? null;
    }
    if (candidate?.performance) {
      return candidate.performance;
    }
    if (candidate?.contestPerformance) {
      return candidate.contestPerformance;
    }
    if (candidate?.overview) {
      return candidate.overview;
    }
    if (candidate?.stats) {
      return candidate.stats;
    }
    return candidate;
  }

  private resolveTrendClass(label: string | null): string | null {
    if (!label) return null;
    const normalized = label.toLowerCase();
    if (normalized.includes('lower') || normalized.includes('down') || normalized.includes('decrease') || normalized.startsWith('-')) {
      return 'bg-danger';
    }
    if (normalized.includes('higher') || normalized.includes('up') || normalized.includes('increase') || normalized.startsWith('+')) {
      return 'bg-success';
    }
    return 'bg-secondary';
  }

  private buildEmptyPerformance(): ContestPerformance {
    return {
      totalViews: null,
      completedTasks: null,
      completedTasksIsRate: false,
      avgClickRate: null,
      avgCompletionRate: null,
      participationRate: null,
      totalViewsTrend: null,
      completedTasksTrend: null,
      avgClickRateTrend: null,
      participationRateTrend: null,
      totalViewsTrendLabel: null,
      totalViewsTrendClass: null,
      completedTasksTrendLabel: null,
      completedTasksTrendClass: null,
      avgClickRateTrendLabel: null,
      avgClickRateTrendClass: null,
      participationRateTrendLabel: null,
      participationRateTrendClass: null
    };
  }

  private normalizeContestPerformance(response: any): ContestPerformance {
    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      return this.buildEmptyPerformance();
    }

    const totalViews = this.extractNumber(payload, [
      'total_views', 'totalViews', 'total_view', 'views', 'view', 'total_view_count', 'totalViewCount'
    ]);

    const completedTasks = this.extractNumber(payload, [
      'completed_tasks', 'completedTasks', 'completed', 'completed_count', 'tasks_completed', 'total_completed_tasks'
    ]);

    const avgClickRate = this.extractNumber(payload, [
      'avg_click_rate', 'avgClickRate', 'avg_clicks_rate', 'avg_click_through_rate', 'click_rate', 'clickRate',
      'engagement_rate', 'engagementRate'
    ]);

    const avgCompletionRate = this.extractNumber(payload, [
      'avg_completion_rate', 'avgCompletionRate', 'completion_rate', 'completionRate',
      'average_completion_rate', 'averageCompletionRate'
    ]);

    const participationRate = this.extractNumber(payload, [
      'participation_rate', 'participationRate', 'participation_rate_percent', 'participationPercentage'
    ]);

    const totalViewsTrend = this.extractTrendNumber(payload, [
      'totalViews', 'total_views', 'total_view', 'views', 'view',
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]);
    const completedTasksTrend = this.extractTrendNumber(payload, [
      'completedTasks', 'completed_tasks', 'completed', 'completed_count',
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]);
    const avgClickRateTrend = this.extractTrendNumber(payload, [
      'avgClickRate', 'avg_click_rate', 'averageCTR',
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend', 'averageCTRTrend'
    ]);
    const participationRateTrend = this.extractTrendNumber(payload, [
      'participationRate', 'participation_rate', 'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]);

    const buildTrendLabel = (trend: number | null): string | null => {
      if (trend === null || trend === undefined || Number.isNaN(trend)) return null;
      const direction = trend >= 0 ? 'Higher' : 'Lower';
      return `${Math.abs(trend).toFixed(0)}% ${direction}`;
    };

    const totalViewsTrendLabel = this.extractString(payload, [
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]) ?? buildTrendLabel(totalViewsTrend);
    const completedTasksTrendLabel = this.extractString(payload, [
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]) ?? buildTrendLabel(completedTasksTrend);
    const avgClickRateTrendLabel = this.extractString(payload, [
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend'
    ]) ?? buildTrendLabel(avgClickRateTrend);
    const participationRateTrendLabel = this.extractString(payload, [
      'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]) ?? buildTrendLabel(participationRateTrend);

    const totalViewsTrendClass = totalViewsTrendLabel
      ? this.resolveTrendClass(totalViewsTrendLabel)
      : totalViewsTrend !== null
        ? (totalViewsTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const completedTasksTrendClass = completedTasksTrendLabel
      ? this.resolveTrendClass(completedTasksTrendLabel)
      : completedTasksTrend !== null
        ? (completedTasksTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const avgClickRateTrendClass = avgClickRateTrendLabel
      ? this.resolveTrendClass(avgClickRateTrendLabel)
      : avgClickRateTrend !== null
        ? (avgClickRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const participationRateTrendClass = participationRateTrendLabel
      ? this.resolveTrendClass(participationRateTrendLabel)
      : participationRateTrend !== null
        ? (participationRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;

    const normalizedAvgCompletionRate = this.normalizeRate(avgCompletionRate);
    const normalizedAvgClickRate = this.normalizeRate(avgClickRate);
    const normalizedParticipationRate = this.normalizeRate(participationRate);

    const hasCompletedTasks = completedTasks !== null && completedTasks !== undefined;
    const resolvedCompletedTasks = hasCompletedTasks ? completedTasks : normalizedAvgCompletionRate;
    const completedTasksIsRate = !hasCompletedTasks && normalizedAvgCompletionRate !== null;

    return {
      totalViews,
      completedTasks: resolvedCompletedTasks,
      completedTasksIsRate,
      avgClickRate: normalizedAvgClickRate,
      avgCompletionRate: normalizedAvgCompletionRate,
      participationRate: normalizedParticipationRate,
      totalViewsTrend,
      completedTasksTrend,
      avgClickRateTrend,
      participationRateTrend,
      totalViewsTrendLabel,
      totalViewsTrendClass,
      completedTasksTrendLabel,
      completedTasksTrendClass,
      avgClickRateTrendLabel,
      avgClickRateTrendClass,
      participationRateTrendLabel,
      participationRateTrendClass
    };
  }

  private buildEmptyPerformanceOverview(): OverviewStats {
    return {
      totalContests: 0,
      activeContests: 0,
      pendingContests: 0,
      finishedContests: 0,
      avgClickRate: 0,
      avgCompletionRate: 0,
      avgParticipationRate: 0,
      avgEngagementRate: 0
    };
  }

  private updatePerformanceOverview(response: any): void {
    // On Web QR the eight cards belong to qr_report_detail, which is the only
    // source that knows about QR instances. contest_detail_overview is
    // campaign-wide and instance-blind, so letting it write here replaced the
    // instance's figures with the campaign's — the reason Total Verified Views
    // showed a different number from the QR Instance table below it.
    if (this.isWebQrMode && this.qrReport) {
      this.applyQrReportTotals();
      return;
    }

    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      return;
    }

    const readValue = (obj: any, fallback: number = 0): number => {
      if (obj == null) return fallback;
      const coerced = this.coerceNumber(obj);
      if (coerced !== null && !isNaN(coerced)) return coerced;
      return fallback;
    };
    const readTrend = (obj: any): number | null => {
      if (obj == null) return null;
      if (typeof obj === 'number') return obj;
      if (typeof obj?.trend === 'number') return obj.trend;
      return null;
    };
    const prevFromTrend = (current: number, trend: number | null): number => {
      if (trend == null || !isFinite(trend)) return 0;
      const denom = 1 + trend / 100;
      if (denom === 0) return 0;
      return current / denom;
    };

    const clickKeys = [
      'Clicks', 'clicks', 'totalClicks', 'total_clicks', 'TotalClicks',
      'click', 'total_click', 'totalClick', 'clicks_count', 'click_count',
      'total_clicks_count', 'totalClicksCount', 'totalScans', 'total_scans',
      'scans', 'scan', 'totalScan', 'qr_scans', 'qr_clicks', 'total_qr_scans',
      'scans_count', 'scan_count', 'total_unique_clicks', 'unique_clicks', 'interactions'
    ];
    const viewKeys = [
      'view', 'views', 'totalViews', 'total_views', 'totalView', 'total_view',
      'total_view_count', 'totalViewCount', 'impressions', 'totalImpressions'
    ];
    const participantKeys = [
      'participants', 'totalParticipants', 'total_participants', 'total_participant',
      'totalParticipant', 'participant', 'participants_count', 'participant_count',
      'totalUnique', 'total_unique', 'total_users', 'users', 'user_count',
      'total_joined', 'joins', 'total_joins', 'joined'
    ];
    const completedKeys = [
      'completed', 'completedTasks', 'completed_tasks', 'completed_count',
      'completed_participants', 'total_completed', 'tasks_completed', 'finished',
      'total_finished', 'totalFinished'
    ];

    let totalViews = this.extractNumber(payload, viewKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, viewKeys) : null) ??
      readValue(
        payload?.view,
        readValue(payload?.views, readValue(payload?.totalViews, readValue(payload?.total_views, 0)))
      );
    if (totalViews === 0) {
      const fallbackViews = this.getContestTotalViewsFallback();
      if (fallbackViews !== null && fallbackViews > 0) {
        totalViews = fallbackViews;
      }
    }

    let totalClicks = this.extractNumber(payload, clickKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, clickKeys) : null) ??
      readValue(
        payload?.click,
        readValue(payload?.clicks, readValue(payload?.Clicks, readValue(payload?.totalClicks, readValue(payload?.total_clicks, 0))))
      );
    if (totalClicks === 0) {
      const fallbackClicks = this.getContestTotalClicksFallback();
      if (fallbackClicks !== null && fallbackClicks > 0) {
        totalClicks = fallbackClicks;
      }
    }

    let participants = this.extractNumber(payload, participantKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, participantKeys) : null) ??
      readValue(
        payload?.participants,
        readValue(payload?.totalParticipants, readValue(payload?.total_participants, 0))
      );
    if (participants === 0) {
      const fallbackParticipants = this.getUniqueParticipantsCount();
      if (fallbackParticipants !== null && fallbackParticipants > 0) {
        participants = fallbackParticipants;
      }
    }

    const completed = this.extractNumber(payload, completedKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, completedKeys) : null) ??
      readValue(
        payload?.completed,
        readValue(payload?.completedTasks, readValue(payload?.completed_tasks, 0))
      );

    const overallSuccessRateRaw = readValue(
      payload?.overallSuccessRate,
      readValue(payload?.overall_success_rate, readValue(payload?.averageClickThroughRate, readValue(payload?.averageCTR, 0)))
    );
    const avgCompletionRaw = readValue(
      payload?.completionRate,
      readValue(payload?.avgCompletionRate, readValue(payload?.averageCompletionRate, readValue(payload?.avg_completion_rate, 0)))
    );
    const avgParticipationRaw = readValue(
      payload?.participationRate,
      readValue(payload?.avgParticipationRate, readValue(payload?.averageParticipationRate, readValue(payload?.avg_participation_rate, 0)))
    );
    const avgEngagementRaw = readValue(
      payload?.engagementRate,
      readValue(payload?.avgEngagementRate, readValue(payload?.averageEngagementRate, readValue(payload?.avg_engagement_rate, 0)))
    );

    let overallSuccessRate = this.normalizeRate(overallSuccessRateRaw) ?? 0;
    if (overallSuccessRate === 0 && totalViews > 0 && totalClicks > 0) {
      overallSuccessRate = this.normalizeRate((totalClicks / totalViews) * 100) ?? 0;
    }
    const avgCompletion = this.normalizeRate(avgCompletionRaw) ?? 0;
    let avgParticipation = this.normalizeRate(avgParticipationRaw) ?? 0;
    if (avgParticipation === 0 && totalViews > 0 && participants > 0) {
      avgParticipation = this.normalizeRate((participants / totalViews) * 100) ?? 0;
    }
    const avgEngagement = this.normalizeRate(avgEngagementRaw) ?? 0;

    const totalTrend = this.extractTrendNumber(payload, viewKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, viewKeys) : null) ??
      readTrend(payload?.view) ?? readTrend(payload?.views) ?? readTrend(payload?.totalViews);
    const activeTrend = this.extractTrendNumber(payload, clickKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, clickKeys) : null) ??
      readTrend(payload?.click) ?? readTrend(payload?.clicks) ?? readTrend(payload?.Clicks) ?? readTrend(payload?.totalClicks);
    const pendingTrend = this.extractTrendNumber(payload, participantKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, participantKeys) : null) ??
      readTrend(payload?.participants) ?? readTrend(payload?.totalParticipants);
    const finishedTrend = this.extractTrendNumber(payload, completedKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, completedKeys) : null) ??
      readTrend(payload?.completed) ?? readTrend(payload?.completedTasks);
    const overallTrend = readTrend(payload?.overallSuccessRate) ?? readTrend(payload?.averageClickThroughRate);
    const completionTrend = readTrend(payload?.completionRate) ?? readTrend(payload?.avgCompletionRate) ?? readTrend(payload?.averageCompletionRate);
    const participationTrend = readTrend(payload?.participationRate) ?? readTrend(payload?.avgParticipationRate) ?? readTrend(payload?.averageParticipationRate);
    const engagementTrend = readTrend(payload?.engagementRate) ?? readTrend(payload?.avgEngagementRate) ?? readTrend(payload?.averageEngagementRate);

    this.performanceStats = {
      totalContests: totalViews,
      activeContests: totalClicks,
      pendingContests: participants,
      finishedContests: completed,
      avgClickRate: overallSuccessRate,
      avgCompletionRate: avgCompletion,
      avgParticipationRate: avgParticipation,
      avgEngagementRate: avgEngagement
    };

    this.performancePreviousStats = {
      totalContests: prevFromTrend(totalViews, totalTrend),
      activeContests: prevFromTrend(totalClicks, activeTrend),
      pendingContests: prevFromTrend(participants, pendingTrend),
      finishedContests: prevFromTrend(completed, finishedTrend),
      avgClickRate: prevFromTrend(overallSuccessRate, overallTrend),
      avgCompletionRate: prevFromTrend(avgCompletion, completionTrend),
      avgParticipationRate: prevFromTrend(avgParticipation, participationTrend),
      avgEngagementRate: prevFromTrend(avgEngagement, engagementTrend)
    };

    this.performanceTrends = {
      totalContests: totalTrend ?? 0,
      activeContests: activeTrend ?? 0,
      pendingContests: pendingTrend ?? 0,
      finishedContests: finishedTrend ?? 0,
      avgClickRate: overallTrend ?? 0,
      avgCompletionRate: completionTrend ?? 0,
      avgParticipationRate: participationTrend ?? 0,
      avgEngagementRate: engagementTrend ?? 0,
      overallSuccessRate: overallTrend ?? 0
    };
  }

  async getContestPerformance(contestId: number | string): Promise<void> {
    this.performanceLoading = true;
    this.performanceError = null;

    try {
      const parsedContestId = Number(contestId);
      if (!Number.isFinite(parsedContestId)) {
        throw new Error('Invalid contest id for performance request.');
      }
      const startDate = this.performanceDateRange?.[0] ?? null;
      const endDate = this.performanceDateRange?.[1] ?? null;
      const params = {
        start_date: startDate ? Math.floor(startDate.getTime() / 1000) : '',
        end_date: endDate ? Math.floor(endDate.getTime() / 1000) : '',
        contest_id: parsedContestId
      };
      const response = await firstValueFrom(
        this.pagesService.getAllById('CONTEST_DETAIL_OVERVIEW', params)
      );
      // contest_detail_overview still drives the non-card performance panel in
      // both modes; updatePerformanceOverview() is what defers to the QR report
      // for the eight cards on Web QR.
      this.contestPerformance = this.normalizeContestPerformance(response);
      this.updatePerformanceOverview(response);
    } catch (error: any) {
      this.contestPerformance = this.buildEmptyPerformance();
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      this.performanceError = 'Performance data unavailable.';
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch contest performance:', error);
      }
    } finally {
      this.performanceLoading = false;
      this.applyPerformanceFallbacks();
    }
  }

  private getUniqueParticipantsCount(): number | null {
    if (!Array.isArray(this.participantsList) || this.participantsList.length === 0) {
      return null;
    }

    const participantIds = new Set<string>();
    this.participantsList.forEach((p: any) => {
      const id = p?.participant_id ?? p?.user_id ?? p?._end_user?.id ?? p?.id;
      if (id !== null && id !== undefined) {
        participantIds.add(String(id));
      }
    });

    return participantIds.size > 0 ? participantIds.size : this.participantsList.length;
  }

  private getContestTotalViewsFallback(): number | null {
    return this.extractNumber(this.contests_detail, [
      'total_views', 'totalViews', 'views', 'view', 'total_view_count', 'total_view', 'impressions'
    ]);
  }

  private getContestTotalClicksFallback(): number | null {
    const clickKeys = [
      'Clicks', 'clicks', 'total_clicks', 'totalClicks', 'TotalClicks',
      'click', 'total_click', 'totalClick', 'clicks_count', 'click_count',
      'total_clicks_count', 'totalClicksCount', 'totalScans', 'total_scans',
      'scans', 'scan', 'totalScan', 'qr_scans', 'qr_clicks', 'total_qr_scans',
      'scans_count', 'scan_count', 'total_unique_clicks', 'unique_clicks', 'interactions'
    ];

    // 1. From contests_detail (from GET_SINGLE_CONTEST which queries x2_dev_18_contest)
    const fromDetail = this.extractNumber(this.contests_detail, clickKeys);
    if (fromDetail !== null && fromDetail > 0) {
      return fromDetail;
    }

    // 2. From analyticsActivity (from QR_REPORT_DETAIL / ANALYSIS_URL_Reports_DETAIL)
    if (Array.isArray(this.analyticsActivity) && this.analyticsActivity.length > 0) {
      const fromAnalytics = this.analyticsActivity.reduce((acc: number, u: any) => {
        const itemClicks = this.extractNumber(u, clickKeys);
        if (itemClicks !== null && itemClicks > 0) return acc + itemClicks;
        const levelSteps = (Number(u?.levelsCompleted || 0) + Number(u?.levelsInProgress || 0));
        return acc + (levelSteps > 0 ? levelSteps : (u?.contestId || u?.id ? 1 : 0));
      }, 0);
      if (fromAnalytics > 0) {
        return fromAnalytics;
      }
    }

    // 3. From participants: each participant who participated clicked/scanned to participate
    const uniqueParticipants = this.getUniqueParticipantsCount();
    if (uniqueParticipants !== null && uniqueParticipants > 0) {
      return uniqueParticipants;
    }

    // 4. Fallback to total views if clicks were unrecorded but views exist
    const views = this.performanceStats?.totalContests || this.getContestTotalViewsFallback();
    if (views && views > 0 && (!this.performanceStats?.activeContests || this.performanceStats.activeContests === 0)) {
      return views;
    }

    return null;
  }

  private applyPerformanceFallbacks(): void {
    // On Web QR the cards come straight from qr_report_detail's totals, and a
    // real 0 there (no verified views yet, no participants yet) is a fact —
    // these fallbacks treat 0 as "missing" and would replace it with non-QR
    // numbers, so they are skipped entirely.
    if (this.isWebQrMode) return;

    const nextPerformance: ContestPerformance = { ...this.contestPerformance };
    const fallbackViews = this.getContestTotalViewsFallback();
    const fallbackParticipants = this.getUniqueParticipantsCount();
    const fallbackClicks = this.getContestTotalClicksFallback();

    if ((nextPerformance.totalViews === null || nextPerformance.totalViews === 0) && fallbackViews && fallbackViews > 0) {
      nextPerformance.totalViews = fallbackViews;
    }

    if ((nextPerformance.participationRate === null || nextPerformance.participationRate === 0) &&
      fallbackViews && fallbackViews > 0 &&
      fallbackParticipants && fallbackParticipants > 0) {
      nextPerformance.participationRate = this.normalizeRate((fallbackParticipants / fallbackViews) * 100);
    }

    this.contestPerformance = nextPerformance;

    // Update performanceStats which drives the UI cards
    const stats = { ...this.performanceStats };
    const prevStats = { ...this.performancePreviousStats };
    const trends = { ...this.performanceTrends };
    let updated = false;

    if ((!stats.totalContests || stats.totalContests === 0) && fallbackViews && fallbackViews > 0) {
      stats.totalContests = fallbackViews;
      prevStats.totalContests = prevStats.totalContests || fallbackViews;
      updated = true;
    }

    if ((!stats.pendingContests || stats.pendingContests === 0) && fallbackParticipants && fallbackParticipants > 0) {
      stats.pendingContests = fallbackParticipants;
      prevStats.pendingContests = prevStats.pendingContests || fallbackParticipants;
      updated = true;
    }

    if ((!stats.activeContests || stats.activeContests === 0) && fallbackClicks && fallbackClicks > 0) {
      stats.activeContests = fallbackClicks;
      if ((!trends.activeContests || trends.activeContests === 0) && trends.pendingContests) {
        trends.activeContests = trends.pendingContests;
      }
      const activeTrend = trends.activeContests ?? 0;
      const denom = 1 + activeTrend / 100;
      prevStats.activeContests = denom !== 0 ? fallbackClicks / denom : fallbackClicks;
      updated = true;
    }

    const views = stats.totalContests;
    const participants = stats.pendingContests;
    const clicks = stats.activeContests;

    if ((!stats.avgParticipationRate || stats.avgParticipationRate === 0) && views > 0 && participants > 0) {
      stats.avgParticipationRate = this.normalizeRate((participants / views) * 100) ?? 0;
      updated = true;
    }

    if ((!stats.avgClickRate || stats.avgClickRate === 0) && views > 0 && clicks > 0) {
      stats.avgClickRate = this.normalizeRate((clicks / views) * 100) ?? 0;
      updated = true;
    }

    if (updated) {
      this.performanceStats = stats;
      this.performancePreviousStats = prevStats;
      this.performanceTrends = trends;
    }
  }

  formatMetric(value: number | null, isPercent: boolean = false, fractionDigits: number = 1): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--';
    }
    if (isPercent) {
      return `${value.toFixed(fractionDigits)}%`;
    }
    return new Intl.NumberFormat('en-US').format(value);
  }

  getPercentWidth(value: number | null): number {
    if (value === null || value === undefined || Number.isNaN(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  formatDateToString(date: Date | null): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  onPerformanceDateChange(dates: Date[]): void {
    this.performanceDateRange = dates || [];
    if (this.contestID) {
      this.getContestPerformance(this.contestID);
    }
  }

  getTrendBadge(trend: number | null | undefined): { value: number; isPositive: boolean } {
    const safe = typeof trend === 'number' && isFinite(trend) ? trend : 0;
    return { value: Math.abs(safe), isPositive: safe >= 0 };
  }

  getPercentageChange(current: number, previous: number): { value: number; isPositive: boolean } {
    if (previous === 0) {
      return { value: 0, isPositive: true };
    }
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0
    };
  }
  getTimeRemaining(createdAt: string) {
    return this.sharedService.timeRemaining(createdAt);
  }
  // Get variables to display (limited or all)
  getDisplayedVariables(level: any): any[] {
    if (!level.client_variables || level.client_variables.length === 0) {
      return [];
    }
    const showAll = this.showAllVariables[level.id];
    return showAll ? level.client_variables : level.client_variables.slice(0, this.maxVariablesToShow);
  }
  shouldShowMore(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      !this.showAllVariables[level.id];
  }
  shouldShowLess(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      this.showAllVariables[level.id];
  }
  LevelUID(uid: string): void {
    if (uid) {
      this.clipboard.copy(uid);
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelUidCopiedSuccess'));
    } else {
      this.sharedService.createNotification('warning', this.translateService.instant('contestDetails.noUidForLevel'));
    }
  }
  getRemainingVariablesCount(level: any): number {
    return level.client_variables ? level.client_variables.length - this.maxVariablesToShow : 0;
  }
  toggleVariablesDisplay(levelId: string): void {
    this.showAllVariables[levelId] = !this.showAllVariables[levelId];
  }
  private formatDate(timestamp: number | string): string {
    if (!timestamp) return 'N/A';

    const date = new Date(Number(timestamp));
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private resolveDrawDate(contestData: any): string {
    const drawList = Array.isArray(contestData?.contest_draw) ? contestData.contest_draw : [];
    if (drawList.length > 0) {
      const latestDraw = [...drawList].sort(
        (a: any, b: any) => Number(b?.created_at || 0) - Number(a?.created_at || 0)
      )[0];
      if (latestDraw?.created_at) {
        return this.formatDate(latestDraw.created_at);
      }
    }

    return this.formatDate(contestData?.prize_announce_date);
  }

  copyUID(value: string): void {
    if (value) {
      this.clipboard.copy(value);
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.copiedToClipboard'));
    } else {
      this.sharedService.createNotification('warning', this.translateService.instant('contestDetails.nothingToCopy'));
    }
  }

  copyLevelId(levelId: number): void {
    if (levelId) {
      this.clipboard.copy(levelId.toString());
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelIdCopied'));
    }
  }

  // Helper method to check if value exists
  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  // Toggle edit mode for a level
  onQuizDropdownOpen(isOpen: boolean): void {
    if (isOpen && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }
  }

  getQuizzesRecords(): void {
    if (this.quizzesLoaded) return;
    this.authService.getAll('GET_QUIZZES_LOOKUP').subscribe({
      next: (response: any) => {
        this.quizzesList = response || [];
        this.quizzesLoaded = true;
      },
      error: () => {
        this.sharedService.createNotification('error', this.translateService.instant('contestDetails.failedToLoadQuizzes'));
      },
    });
  }

  getQuizTitle(quizId: number): string {
    const quiz = this.quizzesList.find(q => q.id === quizId);
    return quiz?.quiz_title || 'Quiz #' + quizId;
  }

  toggleEditLevel(levelId: string, level: any): void {
    if (this.editingLevels[levelId]) {
      // Cancel editing
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } else {
      // Start editing - create form for this level
      this.editingLevels[levelId] = true;
      this.levelForms[levelId] = this.createLevelForm(level);
    }
  }

  // Create form for editing level
  // Minimum answers can't drop below the number of questions marked required (floor of 1).
  getSurveyRequiredCount(level: any): number {
    const required = (level?.survey_questions || []).filter((q: any) => q?.is_required === true).length;
    return Math.max(1, required);
  }

  createLevelForm(level: any): FormGroup {
    const levelType = level.level_type || level.type;
    const isTextLevel = levelType === 'text';

    const form = this.fb.group({
      name: [level.name || '', Validators.required],
      arabic_name: [level.arabic_name || ''],
      description: [level.description || '', Validators.required],
      arabic_description: [level.arabic_description || '', Validators.required],
      textContent: this.fb.array(
        level.textContent && level.textContent.trim() !== ''
          ? this.splitTextContent(level.textContent).map((text: string) =>
            this.fb.control(text, isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])
          )
          : [this.fb.control('', isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])]
      ),
      qrData: [level.qrData || level.textContent || ''],
      client_webhook_url: [level.client_webhook_url || ''],
      imageFile: [null],
      client_variables: this.fb.array([]),
      quizzes_id: [level.quizzes_id || null],
      min_answers_required: [
        level.min_answers_required || 1,
        levelType === 'survey'
          ? [Validators.required, Validators.min(this.getSurveyRequiredCount(level)), Validators.max(level.survey_questions?.length || 1)]
          : [Validators.min(1)]
      ],
      question_order: [level.question_order || 'fixed'],
      link_task_instruction: [level.link_task_instruction || ''],
      link_task_arabic_instruction: [level.link_task_arabic_instruction || level.arabic_task_instruction || ''],
      link_task_url: [level.link_task_url || ''],
      link_task_timer: [level.link_task_timer || 0],
      link_open_mode: [level.link_open_mode || 'in_app'],
    });

    // Set existing image preview if available
    if (level.image && level.image.url) {
      this.levelImagePreviews[level.id] = level.image.url;
    }

    // Pre-load quizzes if level type is quiz
    if ((level.level_type || level.type) === 'quiz' && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }

    // Add existing client variables if available
    if (level.client_variables && level.client_variables.length > 0) {
      const variablesArray = form.get('client_variables') as FormArray;
      level.client_variables.forEach((variable: any) => {
        variablesArray.push(this.fb.group({
          variable_name: [variable.variable_name || '', Validators.required],
          display_name: [variable.display_name || '', Validators.required],
          datatype: [variable.datatype || 'string', Validators.required],
        }));
      });
    }

    return form;
  }

  // Save updated level
  async saveLevel(levelId: string, level: any): Promise<void> {
    const form = this.levelForms[levelId];

    if (!form || form.invalid) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.fillRequiredFields'));
      form?.markAllAsTouched();
      return;
    }

    this.sharedService.showLoader();

    try {
      const formValue = form.value;
      const levelType = level.level_type || level.type;
      // `level` is normalized for display, but a raw nested record may still be
      // the only place some fields live — resolve through it as formatLevelForApi does.
      const levelRecord = level?.level_details ?? level?.level_detail ?? level?.details ?? level;
      const resolvedStartDate =
        level?.level_start_date ?? level?.start_date ??
        levelRecord?.level_start_date ?? levelRecord?.start_date ?? null;
      const resolvedTimeline =
        level?.level_timeline ?? level?.timeline ??
        levelRecord?.level_timeline ?? levelRecord?.timeline ?? null;

      const updatePayload: any = {
        id: level.id,
        name: formValue.name,
        arabic_name: formValue.arabic_name ?? level.arabic_name ?? '',
        description: formValue.description,
        arabic_description: formValue.arabic_description,
        type: levelType,
        start_date: resolvedStartDate,
        level_start_date: resolvedStartDate,
        level_timeline: resolvedTimeline,
        luqta_webhook_url: level.luqta_webhook_url ?? levelRecord?.luqta_webhook_url ?? '',
        client_webhook_url: level.client_webhook_url ?? levelRecord?.client_webhook_url ?? '',
        client_variables: level.client_variables ?? levelRecord?.client_variables ?? [],
      };

      // Add type-specific fields
      if (levelType === 'text') {
        const textContentArray = formValue.textContent || [];
        updatePayload.textContent = textContentArray.filter((text: string) => text.trim() !== '').join(' || ');
        updatePayload.arabic_textContent = level.arabic_textContent ?? levelRecord?.arabic_textContent ?? '';
      } else if (levelType === 'qr') {
        // The QR input may be absent from the form (it is only rendered for QR
        // levels) — falling back to the stored code stops a blank value from
        // wiping the saved QR data.
        const qr =
          (formValue.qrData !== undefined && formValue.qrData !== null && formValue.qrData !== '')
            ? formValue.qrData
            : (level.qrData ?? levelRecord?.qrData ?? level.textContent ?? levelRecord?.textContent ?? '');
        updatePayload.qrData = qr;
        updatePayload.textContent = qr;
      } else if (levelType === 'image') {
        // Handle image upload - check preview first (has base64), then file
        const previewImage = this.levelImagePreviews[levelId];
        const imageFile = formValue.imageFile;

        if (previewImage && typeof previewImage === 'string' && previewImage.startsWith('data:image')) {
          // Use base64 from preview (newly uploaded image)
          updatePayload.image = { path: previewImage };
        } else if (imageFile && imageFile instanceof File) {
          // Convert file to base64 if preview not available
          const imageBase64 = await this.fileToBase64(imageFile);
          updatePayload.image = { path: imageBase64 };
        } else if (level.image) {
          // Keep existing image from server
          updatePayload.image = level.image;
        }
      } else if (levelType === 'client_webhook') {
        updatePayload.client_webhook_url = formValue.client_webhook_url || '';
        updatePayload.client_variables = formValue.client_variables || [];
      } else if (levelType === 'quiz') {
        updatePayload.quizzes_id = formValue.quizzes_id || null;
        updatePayload.quiz_text_en = level.quiz_text_en ?? levelRecord?.quiz_text_en ?? '';
        updatePayload.quiz_text_ar = level.quiz_text_ar ?? levelRecord?.quiz_text_ar ?? '';
      } else if (levelType === 'survey') {
        updatePayload.min_answers_required = Number(formValue.min_answers_required) || 1;
        updatePayload.question_order = formValue.question_order || 'fixed';
        updatePayload.survey_questions = level.survey_questions || [];
      } else if (levelType === 'link_task') {
        const inst = formValue.link_task_instruction || '';
        const arInst = formValue.link_task_arabic_instruction || '';
        const url = formValue.link_task_url || '';
        const timer = Number(formValue.link_task_timer) || 0;
        const openMode = formValue.link_open_mode || 'in_app';

        updatePayload.link_task_instruction = inst;
        updatePayload.task_instruction = inst;
        updatePayload.link_task_arabic_instruction = arInst;
        updatePayload.arabic_task_instruction = arInst;
        updatePayload.link_task_url = url;
        updatePayload.task_url = url;
        updatePayload.link_task_timer = timer;
        updatePayload.timer_duration = timer;
        updatePayload.link_open_mode = openMode;
      } else {
        // For other types, preserve existing values
        updatePayload.client_webhook_url = '';
        updatePayload.client_variables = [];
      }

      // Update level via API — preserve ALL levels so other levels (e.g. QR) and dates are never lost
      const allLevelsPayload = this.levels_of_contest.map((lvl: any, index: number) => {
        if (lvl.id === level.id) {
          return {
            ...updatePayload,
            order: lvl.order ?? (index + 1),
          };
        }
        return this.formatLevelForApi(lvl, index);
      });

      const levelData = {
        level_details: allLevelsPayload,
        contest_id: this.contestID,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local data
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        const textContentArray = formValue.textContent || [];
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          name: formValue.name,
          arabic_name: formValue.arabic_name ?? level.arabic_name ?? '',
          description: formValue.description,
          arabic_description: formValue.arabic_description,
          textContent: textContentArray.filter((text: string) => text.trim() !== '').join(' || '),
          qrData: updatePayload.qrData ?? this.levels_of_contest[levelIndex]?.qrData,
          level_start_date: resolvedStartDate,
          start_date: resolvedStartDate,
          level_timeline: resolvedTimeline,
          client_webhook_url: formValue.client_webhook_url,
          client_variables: formValue.client_variables || [],
          quizzes_id: formValue.quizzes_id,
          min_answers_required: formValue.min_answers_required,
          question_order: formValue.question_order,
          link_task_instruction: formValue.link_task_instruction,
          link_task_arabic_instruction: formValue.link_task_arabic_instruction,
          link_task_url: formValue.link_task_url,
          link_task_timer: formValue.link_task_timer,
          link_open_mode: formValue.link_open_mode,
        };
      }

      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelUpdatedSuccess'));
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to update level');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Format existing level object for API submission preserving all configuration
  formatLevelForApi(lvl: any, index?: number): any {
    const levelRecord = lvl?.level_details ?? lvl?.level_detail ?? lvl?.details ?? lvl;
    const levelType = lvl?.level_type || lvl?.type || levelRecord?.level_type || levelRecord?.type || '';
    const startDate = lvl?.level_start_date ?? lvl?.start_date ?? levelRecord?.level_start_date ?? levelRecord?.start_date ?? null;
    const timeline = lvl?.level_timeline ?? lvl?.timeline ?? levelRecord?.level_timeline ?? levelRecord?.timeline ?? null;
    const order = lvl?.order ?? levelRecord?.order ?? (index !== undefined ? index + 1 : undefined);

    const payload: any = {
      id: lvl?.id || levelRecord?.id || null,
      name: lvl?.name ?? levelRecord?.name ?? '',
      arabic_name: lvl?.arabic_name ?? levelRecord?.arabic_name ?? '',
      description: lvl?.description ?? levelRecord?.description ?? '',
      arabic_description: lvl?.arabic_description ?? levelRecord?.arabic_description ?? '',
      type: levelType,
      start_date: startDate,
      level_start_date: startDate,
      level_timeline: timeline,
      luqta_webhook_url: lvl?.luqta_webhook_url ?? levelRecord?.luqta_webhook_url ?? '',
      client_webhook_url: lvl?.client_webhook_url ?? levelRecord?.client_webhook_url ?? '',
      client_variables: lvl?.client_variables ?? levelRecord?.client_variables ?? [],
      order: order,
    };

    if (levelType === 'text') {
      payload.textContent = lvl?.textContent ?? levelRecord?.textContent ?? '';
      payload.arabic_textContent = lvl?.arabic_textContent ?? levelRecord?.arabic_textContent ?? '';
    } else if (levelType === 'qr') {
      const qr = lvl?.qrData ?? levelRecord?.qrData ?? lvl?.qr_data ?? levelRecord?.qr_data ?? lvl?.textContent ?? levelRecord?.textContent ?? '';
      payload.qrData = qr;
      payload.textContent = qr;
    } else if (levelType === 'image') {
      payload.image = lvl?.image ?? levelRecord?.image ?? null;
    } else if (levelType === 'quiz') {
      payload.quizzes_id = lvl?.quizzes_id ?? levelRecord?.quizzes_id ?? null;
      payload.quiz_text_en = lvl?.quiz_text_en ?? levelRecord?.quiz_text_en ?? '';
      payload.quiz_text_ar = lvl?.quiz_text_ar ?? levelRecord?.quiz_text_ar ?? '';
    } else if (levelType === 'survey') {
      payload.survey_questions = lvl?.survey_questions ?? levelRecord?.survey_questions ?? [];
      payload.min_answers_required = lvl?.min_answers_required ?? levelRecord?.min_answers_required ?? 1;
      payload.question_order = lvl?.question_order ?? levelRecord?.question_order ?? 'fixed';
    } else if (levelType === 'link_task') {
      const inst = lvl?.link_task_instruction ?? lvl?.task_instruction ?? levelRecord?.link_task_instruction ?? levelRecord?.task_instruction ?? '';
      const arInst = lvl?.link_task_arabic_instruction ?? lvl?.arabic_task_instruction ?? levelRecord?.link_task_arabic_instruction ?? levelRecord?.arabic_task_instruction ?? '';
      const url = lvl?.link_task_url ?? lvl?.task_url ?? levelRecord?.link_task_url ?? levelRecord?.task_url ?? '';
      const timer = lvl?.link_task_timer ?? lvl?.timer_duration ?? levelRecord?.link_task_timer ?? levelRecord?.timer_duration ?? 30;
      const openMode = lvl?.link_open_mode ?? levelRecord?.link_open_mode ?? 'in_app';

      payload.link_task_instruction = inst;
      payload.task_instruction = inst;
      payload.link_task_arabic_instruction = arInst;
      payload.arabic_task_instruction = arInst;
      payload.link_task_url = url;
      payload.task_url = url;
      payload.link_task_timer = timer;
      payload.timer_duration = timer;
      payload.link_open_mode = openMode;
    }

    return payload;
  }

  // Check if level is in edit mode
  isEditingLevel(levelId: string): boolean {
    return this.editingLevels[levelId] || false;
  }

  // Get form for a level
  getLevelForm(levelId: string): FormGroup | null {
    return this.levelForms[levelId] || null;
  }

  // Get textContent FormArray for a specific level
  getTextContents(levelId: string): FormArray {
    const levelForm = this.getLevelForm(levelId);
    return (levelForm?.get('textContent') as FormArray) ?? this.fb.array([]);
  }

  // Add a new text content field
  addTextContent(levelId: string): void {
    const textContents = this.getTextContents(levelId);
    textContents.push(this.fb.control('', [Validators.required, Validators.maxLength(1000)]));
  }

  // Remove a text content field
  removeTextContent(levelId: string, textIndex: number): void {
    const textContents = this.getTextContents(levelId);
    if (textContents && textContents.length > 1) {
      textContents.removeAt(textIndex);
    }
  }

  // API responses have been observed returning level fields under different
  // shapes/names (e.g. nested level_details, text_content instead of textContent,
  // type instead of level_type) — normalize so display bindings don't silently
  // fail to find the answer data.
  private normalizeLevelForDisplay(level: any): any {
    const levelRecord = level?.level_details ?? level?.level_detail ?? level?.details ?? level;
    const level_type =
      level?.level_type ||
      levelRecord?.level_type ||
      level?.type ||
      levelRecord?.type ||
      level?.levelType ||
      levelRecord?.levelType ||
      '';
    const textContent =
      level?.textContent ??
      levelRecord?.textContent ??
      level?.text_content ??
      levelRecord?.text_content ??
      level?.text ??
      levelRecord?.text ??
      '';

    // For QR levels, ensure qrData is always populated — the API may only
    // return it under textContent, so fall back to that.
    const qrData =
      level?.qrData ??
      levelRecord?.qrData ??
      level?.qr_data ??
      levelRecord?.qr_data ??
      textContent;

    const rawSurveyQuestions = level?.survey_questions ?? levelRecord?.survey_questions ?? [];
    const survey_questions = this.normalizeSurveyQuestions(rawSurveyQuestions);

    const arabic_name =
      level?.arabic_name ??
      levelRecord?.arabic_name ??
      '';

    const level_start_date =
      level?.level_start_date ??
      levelRecord?.level_start_date ??
      level?.start_date ??
      levelRecord?.start_date ??
      level?.level_start ??
      levelRecord?.level_start ??
      null;

    const level_timeline =
      level?.level_timeline ??
      levelRecord?.level_timeline ??
      level?.timeline ??
      levelRecord?.timeline ??
      null;

    const order =
      level?.order ??
      levelRecord?.order ??
      level?.level_order ??
      levelRecord?.level_order ??
      undefined;

    return {
      ...level,
      level_type,
      textContent,
      qrData,
      survey_questions,
      arabic_name: arabic_name || level.arabic_name || '',
      level_start_date,
      start_date: level_start_date,
      level_timeline,
      order
    };
  }

  // Split textContent by || for display
  splitTextContent(textContent: string): string[] {
    if (!textContent || textContent.trim() === '') {
      return [];
    }
    return textContent
      .split(/\s*\|\|\s*/)
      .map((text) => text.trim())
      .filter((text) => text !== '');
  }

  // Get edit button text based on level type
  getEditButtonText(level: any): string {
    if (!level) return this.translateService.instant('contestDetails.editLevel');

    const levelType = level.level_type || level.type;

    switch (levelType) {
      case 'text':
        return this.translateService.instant('contestDetails.editTextLevel');
      case 'qr':
        return this.translateService.instant('contestDetails.editQrLevel');
      case 'image':
        return this.translateService.instant('contestDetails.editImageLevel');
      case 'luqta_weebhook':
        return this.translateService.instant('contestDetails.editLuqtaWebhook');
      case 'client_webhook':
        return this.translateService.instant('contestDetails.editClientWebhook');
      case 'client_qr_completion':
        return 'Client QR Completion';
      default:
        return this.translateService.instant('contestDetails.editLevel');
    }
  }

  // Trigger level image input
  triggerLevelImageInput(levelId: string): void {
    const fileInput = document.getElementById(`imageFile_${levelId}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle level image selection
  onLevelImageSelected(event: Event, levelId: string): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.levelImagePreviews[levelId] = reader.result;
        this.levelImageLoadErrors[levelId] = false;
        const form = this.levelForms[levelId];
        if (form) {
          form.patchValue({ imageFile: file });
        }
        // Show modal after image is uploaded
        this.showImageModal(levelId);
      };
      reader.readAsDataURL(file);
    }
  }

  // Show image preview modal
  showImageModal(levelId: string): void {
    this.isImageModalVisible[levelId] = true;
  }

  // Handle image modal OK
  handleImageOk(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Handle image modal cancel
  handleImageCancel(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Download level image
  async downloadLevelImage(levelId: any, level?: any): Promise<void> {
    const rawUrl = this.levelImagePreviews[levelId] ||
      level?.image?.url ||
      level?.image?.path ||
      (typeof level?.image === 'string' ? level.image : null);

    if (!rawUrl || typeof rawUrl !== 'string') {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.uploadImageFirst') || 'No image available to download.');
      return;
    }

    const levelName = level?.name || `level_${levelId}`;
    const cleanFileName = `${(levelName.replace(/[^a-zA-Z0-9_-]/g, '_') || 'image')}_${Date.now()}.png`;

    const success = await this.sharedService.downloadImageFile(rawUrl, cleanFileName);
    if (!success) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.downloadError') || 'Failed to download image.');
    }
  }

  // Save image and keep preview visible
  async saveImageAndShowPreview(levelId: string, level: any): Promise<void> {
    const previewImage = this.levelImagePreviews[levelId];

    if (!previewImage || typeof previewImage !== 'string' || !previewImage.startsWith('data:image')) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.uploadImageFirst'));
      return;
    }

    this.sharedService.showLoader();

    try {
      const levelType = level.level_type || level.type;

      const updatePayload: any = {
        id: level.id,
        name: level.name,
        description: level.description,
        type: levelType,
        level_timeline: level.level_timeline,
        luqta_webhook_url: level.luqta_webhook_url || '',
        image: { path: previewImage }
      };

      const allLevelsPayload = this.levels_of_contest.map((lvl: any, index: number) => {
        if (lvl.id === level.id) {
          return {
            ...updatePayload,
            start_date: lvl.level_start_date ?? lvl.start_date ?? null,
            level_start_date: lvl.level_start_date ?? lvl.start_date ?? null,
            order: lvl.order ?? (index + 1),
          };
        }
        return this.formatLevelForApi(lvl, index);
      });

      const levelData = {
        level_details: allLevelsPayload,
        contest_id: this.contestID,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local level data immediately with preview image
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          image: { url: previewImage, path: previewImage }
        };
      }

      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.imageSavedSuccess'));

      // Refresh contest details from server to get updated data
      await this.getContestDetails(this.contestID);

    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to save image');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Convert file to base64
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Get client variables FormArray for a level
  getClientVariables(levelId: string): FormArray {
    const form = this.levelForms[levelId];
    return form?.get('client_variables') as FormArray;
  }

  // Add new variable to client webhook
  addVariable(levelId: string): void {
    const variables = this.getClientVariables(levelId);
    if (variables) {
      variables.push(this.fb.group({
        variable_name: ['', Validators.required],
        display_name: ['', Validators.required],
        datatype: ['string', Validators.required],
      }));
    }
  }

  // Remove variable from client webhook
  removeVariable(levelId: string, index: number): void {
    const variables = this.getClientVariables(levelId);
    if (variables && index >= 0 && index < variables.length) {
      variables.removeAt(index);
    }
  }

  // Helper method to get countries as array from location string
  getCountriesArray(location: string | null | undefined): string[] {
    if (!location || location.trim() === '') {
      return [];
    }
    // Split by comma, semicolon, or pipe and trim whitespace
    const countries = location.split(/[,;|]/).map(c => c.trim()).filter(c => c !== '');
    return countries;
  }

  // Search handler for participants
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  // Open filter modal for participants
  openFilterModal(): void {
    this.filterModalVisible = true;
  }

  // Handle filters applied from modal
  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    this.applyClientFilters();
  }

  // Handle filter reset
  onFiltersReset(): void {
    this.currentFilters = {};
    this.applyClientFilters();
  }

  private getGenderFilterValues(selectedGender: string | null | undefined): string[] {
    const gender = String(selectedGender || '').trim().toLowerCase();

    if (gender === 'male') {
      return ['male', '\u0630\u0643\u0631'];
    }

    if (gender === 'female') {
      return ['female', '\u0623\u0646\u062b\u0649'];
    }

    if (gender === 'unspecified') {
      return [''];
    }

    if (gender === 'all') {
      return ['male', '\u0630\u0643\u0631', 'female', '\u0623\u0646\u062b\u0649', ''];
    }

    return [];
  }

  // Check if any filters are active
  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some(key => {
      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }

  // Clear all filters
  clearAllFilters(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        dateRange: null, level: null, status: null, format: null,
        type: null, viewType: null, location: [], gender: null,
        contest: null, age: null, completionRate: null,
        participant_name: null, participant_email: null
      });
    }
    this.applyClientFilters();
  }

  // Apply client-side filtering on participants
  applyClientFilters(): void {
    let filtered = [...this.participantsList];
    const search = this.searchTerm?.trim().toLowerCase();
    const filters = this.currentFilters;

    // Search filter
    if (search) {
      filtered = filtered.filter(p => {
        const name = this.getParticipantName(p).toLowerCase();
        const gender = this.getParticipantGender(p).toLowerCase();
        const location = this.getParticipantLocation(p).toLowerCase();
        return name.includes(search) || gender.includes(search) || location.includes(search);
      });
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(
        p => String(p.status || p.participant_status || '').toLowerCase() === String(filters.status).toLowerCase()
      );
    }

    // Gender filter
    if (filters.gender) {
      const allowedGenderValues = this.getGenderFilterValues(filters.gender).map((value) =>
        value.toLowerCase().trim()
      );
      filtered = filtered.filter(p => {
        const participantGender = this.getParticipantGender(p);
        const normalizedGender = participantGender === 'N/A'
          ? ''
          : String(participantGender || '').toLowerCase().trim();
        return allowedGenderValues.includes(normalizedGender);
      });
    }

    // Location filter
    if (filters.location && Array.isArray(filters.location) && filters.location.length > 0) {
      filtered = filtered.filter(p => {
        const loc = this.getParticipantLocation(p).toLowerCase();
        return filters.location.some((l: string) => loc.includes(l.toLowerCase()));
      });
    }

    this.filteredParticipants = filtered;
  }

  // Open drawer to view participant's level progression. Built only from data
  // already loaded (the participant row + the contest's levels/users_progress),
  // so opening it makes no API call.
  openLevelsDrawer(participant: any): void {
    this.selectedParticipant = participant;

    const candidateUserIds = Array.from(new Set([
      participant?._end_user?.id,
      participant?.end_user?.id,
      participant?.endUser?.id,
      participant?.user?.id,
      participant?.participant?.id,
      participant?.participant_id,
      participant?.participantId,
      participant?.user_id,
      participant?.userId,
      participant?.end_user_id,
      participant?.endUserId,
      participant?.id,
      participant?.contest_participant?.[0]?.id,
      participant?.contest_participant?.[0]?.participant_id,
      participant?.contest_participant?.[0]?.user_id,
      participant?._contest_participant?.[0]?.id,
      participant?._contest_participant?.[0]?.participant_id,
      participant?._contest_participant?.[0]?.user_id,
      participant?.contestParticipant?.id,
      participant?.contestParticipant?.participant_id,
      participant?.contestParticipant?.user_id,
      participant?.contest_participants?.[0]?.id,
      participant?.contest_participants?.[0]?.participant_id,
      participant?.contest_participants?.[0]?.user_id,
    ].filter((v: any) => v !== undefined && v !== null && v !== '')));

    // Get levels from the contest's levels_of_contest and map participant's progress
    this.selectedParticipantLevels = this.levels_of_contest.map((level: any, index: number) => {
      // Check if participant has completed this level
      const allLevels = [
        ...(Array.isArray(participant.levels_detail) ? participant.levels_detail : []),
        ...(Array.isArray(participant.levelsDetail) ? participant.levelsDetail : []),
        ...(Array.isArray(participant?.contest_participant?.[0]?.levels_detail) ? participant.contest_participant[0].levels_detail : []),
        ...(Array.isArray(participant?._contest_participant?.[0]?.levels_detail) ? participant._contest_participant[0].levels_detail : []),
      ];

      const participantLevel = allLevels.find((pl: any) => (pl.level_id === level.id || pl.id === level.id || String(pl.level_id) === String(level.id) || String(pl.id) === String(level.id))) ||
        ((this.levels_of_contest.filter((l: any) => (l.level_type || l.type) === (level.level_type || level.type)).length === 1)
          ? allLevels.find((pl: any) => (pl.level_type || pl.type) === (level.level_type || level.type))
          : null);

      const progressFromLevel = (level.users_progress || []).find((up: any) => {
        const upUid = up?.user_id ?? up?.userId ?? up?.participant_id ?? up?.participantId ?? up?.end_user_id ?? up?.id ?? up?.created_by;
        return candidateUserIds.some((cid: any) => String(cid) === String(upUid));
      });

      const userAnswer = participantLevel?.user_answer ?? participantLevel?.submitted_answer ?? participantLevel?.answer ?? participantLevel?.text_answer ?? participantLevel?.response ?? participantLevel?.survey_answers ?? participantLevel?.answers ?? participantLevel?.submission ?? participantLevel?.result ?? participantLevel?.data ??
        progressFromLevel?.user_answer ?? progressFromLevel?.submitted_answer ?? progressFromLevel?.answer ?? progressFromLevel?.text_answer ?? progressFromLevel?.response ?? progressFromLevel?.survey_answers ?? progressFromLevel?.answers ?? progressFromLevel?.submission ??
        participant?.user_answer ?? participant?.submitted_answer ?? participant?.survey_answers ?? participant?.answers ??
        participant?.contest_participant?.[0]?.user_answer ?? participant?.contest_participant?.[0]?.submitted_answer ?? participant?.contest_participant?.[0]?.answer ?? participant?.contest_participant?.[0]?.survey_answers ?? null;

      let displayUserAnswer = userAnswer;
      let parsedQuestions: { question: string; answer: string }[] = [];

      if ((level.level_type || level.type) === 'survey' && userAnswer) {
        const sqs = (Array.isArray(level.survey_questions) && level.survey_questions.length > 0) ? level.survey_questions : [];
        parsedQuestions = this.parseSurveyAnswer(userAnswer, sqs);
        if (parsedQuestions.length > 0) {
          displayUserAnswer = parsedQuestions.map((p: any) => `${p.question}: ${p.answer}`).join(' | ');
        }
      }

      return {
        name: level.name || `Level ${index + 1}`,
        // Only real values: a participant who completed the campaign completed
        // every level, otherwise an unknown status/points stays null ("-").
        status: participantLevel?.status || progressFromLevel?.status ||
          ((participant.status || participant.participant_status) === 'completed' ? 'completed' : null),
        points: participantLevel?.points ?? progressFromLevel?.points ?? null,
        completed_at: participantLevel?.completed_at || progressFromLevel?.completed_at || null,
        level_type: level.level_type || level.type,
        accept_any_answer: level.accept_any_answer ?? level.acceptAnyAnswer ?? false,
        user_answer: displayUserAnswer,
        parsedQuestions,
        image: level.image,
        textContent: level.textContent,
        qrData: level.qrData,
        description: level.description
      };
    });
    this.levelsDrawerVisible = true;
    this.cdr.detectChanges();
  }

  // Close drawer
  closeLevelsDrawer(): void {
    this.levelsDrawerVisible = false;
    this.selectedParticipant = null;
    this.selectedParticipantLevels = [];
  }

  // Tab switcher in participants card: 'all' or 'survey'
  setParticipantTableTab(tab: 'all' | 'survey'): void {
    this.selectedParticipantTableTab = tab;
    if (tab === 'survey') {
      this.ensureParticipantsAnswersLoaded();
    }
  }

  get surveyLevelQuestions(): any[] {
    const sLevel = (this.levels_of_contest || []).find(
      (l: any) => (l.level_type || l.type) === 'survey'
    );
    if (!sLevel) return [];
    const qs = (Array.isArray(sLevel.survey_questions) && sLevel.survey_questions.length > 0)
      ? sLevel.survey_questions
      : [];
    return this.normalizeSurveyQuestions(qs);
  }

  get hasSurveyQuestions(): boolean {
    return this.surveyLevelQuestions.length > 0;
  }

  getParticipantSurveyRawAnswer(participant: any): any {
    if (!participant) return null;

    const sLevel = (this.levels_of_contest || []).find(
      (l: any) => (l.level_type || l.type) === 'survey'
    );
    const sId = sLevel?.id;

    const allLevels = [
      ...(Array.isArray(participant.levelsDetail) ? participant.levelsDetail : []),
      ...(Array.isArray(participant.levels_detail) ? participant.levels_detail : []),
      ...(Array.isArray(participant.levels) ? participant.levels : []),
      ...(Array.isArray(participant?.contest_participant?.[0]?.levels_detail) ? participant.contest_participant[0].levels_detail : []),
      ...(Array.isArray(participant?._contest_participant?.[0]?.levels_detail) ? participant._contest_participant[0].levels_detail : []),
      ...(Array.isArray(participant?.contestParticipant?.levels_detail) ? participant.contestParticipant.levels_detail : []),
    ];

    const matched = allLevels.find((l: any) =>
      (sId !== undefined && (l.level_id === sId || l.id === sId || String(l.level_id) === String(sId) || String(l.id) === String(sId))) ||
      ((l.level_type || l.type) === 'survey')
    );

    let ans = matched?.user_answer ?? matched?.submitted_answer ?? matched?.answer ?? matched?.text_answer ?? matched?.response ?? matched?.survey_answers ?? matched?.answers ?? matched?.submission ?? matched?.result ?? matched?.data;
    if (ans !== undefined && ans !== null && ans !== '' && ans !== '-') return ans;

    const directAns = participant?.survey_answers ?? participant?.answers ?? participant?.user_answer ?? participant?.submitted_answer ?? participant?.response ?? participant?.submission ??
      participant?.contest_participant?.[0]?.user_answer ?? participant?.contest_participant?.[0]?.submitted_answer ?? participant?.contest_participant?.[0]?.answer ?? participant?.contest_participant?.[0]?.survey_answers ??
      participant?._contest_participant?.[0]?.user_answer ?? participant?._contest_participant?.[0]?.submitted_answer ?? participant?._contest_participant?.[0]?.answer ??
      participant?.contestParticipant?.user_answer ?? participant?.contestParticipant?.submitted_answer ?? participant?.contestParticipant?.answer;
    if (directAns !== undefined && directAns !== null && directAns !== '' && directAns !== '-') return directAns;

    if (sLevel && Array.isArray(sLevel.users_progress) && sLevel.users_progress.length > 0) {
      const candidateUserIds = Array.from(new Set([
        participant?._end_user?.id,
        participant?.end_user?.id,
        participant?.participant_id,
        participant?.participantId,
        participant?.user_id,
        participant?.id,
        participant?.contest_participant?.[0]?.id,
        participant?.contest_participant?.[0]?.participant_id,
        participant?.contest_participant?.[0]?.user_id,
      ].filter((v: any) => v !== undefined && v !== null && v !== '')));

      const up = sLevel.users_progress.find((item: any) => {
        const upUid = item?.user_id ?? item?.userId ?? item?.participant_id ?? item?.end_user_id ?? item?.id;
        return candidateUserIds.some((cid: any) => String(cid) === String(upUid));
      });

      if (up) {
        const upAns = up?.user_answer ?? up?.submitted_answer ?? up?.answer ?? up?.text_answer ?? up?.response ?? up?.survey_answers ?? up?.answers ?? up?.submission ?? up?.result ?? up?.data;
        if (upAns !== undefined && upAns !== null && upAns !== '' && upAns !== '-') return upAns;
      }
    }

    return null;
  }

  getParticipantSurveyAnswer(participant: any, sq: any, sIdx: number): string {
    if (!participant) return '-';

    const rawAns = this.getParticipantSurveyRawAnswer(participant);
    if (!rawAns || rawAns === '-') {
      return '-';
    }

    const totalQs = this.surveyLevelQuestions.length || 1;
    const qVal = this.extractQuestionAnswer(this.parseJsonSafely(rawAns), sq, sIdx, totalQs, this.surveyLevelQuestions);
    const resolved = this.resolveSurveyQuestionAnswer(qVal, sq);

    return this.isArabic
      ? (resolved.arabicDisplayAnswer || resolved.displayAnswer || '-')
      : (resolved.displayAnswer || '-');
  }

  async ensureParticipantsAnswersLoaded(): Promise<void> {
    const participants = this.filteredParticipants || [];
    const needFetch = participants.filter((p: any) => {
      const a = this.getParticipantSurveyRawAnswer(p);
      return !a || a === '-';
    });
    if (needFetch.length === 0) return;

    for (let i = 0; i < needFetch.length; i += 5) {
      const batch = needFetch.slice(i, i + 5);
      await Promise.all(
        batch.map(async (p: any) => {
          const levels = await this.fetchParticipantContestLevels(p);
          if (levels.length > 0) {
            p.levelsDetail = levels;
            p.levels_detail = levels;
          }
        })
      );
      this.cdr.detectChanges();
    }
  }
}

